import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {Button, Card, Col, Row , Dropdown, Spinner, DropdownToggle, DropdownMenu, DropdownItem, Accordion, AccordionItem, Collapse,} from "reactstrap";
import {useTranslation} from "react-i18next";
import $ from 'jquery';
import useApiCallService from '../../Helpers/ApiCall';
import {SaveLocalStorage} from "../../Helpers/SaveLocalStorage";
import {LoadLocalStorage} from "../../Helpers/LoadLocalStorage";
import classNames from 'classnames';
import DatePicker from "react-datepicker";
import { CustomDateLocale } from '../../Helpers/CustomDateLocale';
import { ChangeDateFormat } from '../../Helpers/ChangeDateFormat';
import { ToCustomDecimal } from '../../../AppSystem/Helpers/ToCustomDecimal';
import { SlidePanel } from '../../Components/Modal/SlidePanel';
import { CheckMobile } from '../../../AppSystem/Helpers/CheckMobile';
import { CustomButton } from '../../Components/Button/CustomButton';
import { ReimbursementTypeBadges, getReimbursementTypesFromReimbursement } from '../Badges/ReimbursementTypeBadges';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { components } from "react-select";
import i18n from "i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ReportProceedingInfo from '../../../Reimbursement/Views/ReportProceedingInfo';

// sessionStorage prefix of the filters saved by lists with listOptions.persistFilters
const PERSIST_FILTERS_PREFIX = 'sortableListFilters_';

// ─── Shared ActionMenu helpers ─────────────────────────────────────────────────

const inferActionLabelFromIconClass = (iconClass = '') => {
    const cls = (iconClass || '').toLowerCase();
    if (cls.includes('eye') || cls.includes('show') || cls.includes('search')) return 'Visa';
    if (cls.includes('edit') || cls.includes('pen') || cls.includes('pencil')) return 'Redigera';
    if (cls.includes('trash') || cls.includes('delete') || cls.includes('times')) return 'Ta bort';
    if (cls.includes('download') || cls.includes('file')) return 'Ladda ner';
    return '';
};

const normalizeText = (value) => (typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '');

const readTextContent = (node) => {
    if (typeof node === 'string') return normalizeText(node);
    if (!React.isValidElement(node)) return '';
    const children = React.Children.toArray(node.props?.children);
    const textParts = children
        .map((child) => readTextContent(child))
        .filter((text) => !!text);
    return normalizeText(textParts.join(' '));
};

const findTooltipText = (node) => {
    if (!React.isValidElement(node)) return '';
    const className = (node.props?.className || '').toString().toLowerCase();
    if (className.includes('tooltip')) {
        const tooltipText = readTextContent(node);
        if (tooltipText) return tooltipText;
    }
    const children = React.Children.toArray(node.props?.children);
    for (const child of children) {
        const nestedTooltipText = findTooltipText(child);
        if (nestedTooltipText) return nestedTooltipText;
    }
    return '';
};

const extractActionLabel = (node) => {
    if (!React.isValidElement(node)) return '';
    const tooltipText = findTooltipText(node);
    if (tooltipText) return tooltipText;
    const direct = node.props?.['aria-label'] || node.props?.title;
    if (typeof direct === 'string' && direct.trim()) return direct.trim();
    const children = React.Children.toArray(node.props?.children);
    for (const child of children) {
        if (typeof child === 'string' && child.trim()) return child.trim();
        if (React.isValidElement(child)) {
            const nestedLabel = extractActionLabel(child);
            if (nestedLabel) return nestedLabel;
            const childClass = child.props?.className || '';
            const labelFromClass = inferActionLabelFromIconClass(childClass);
            if (labelFromClass) return labelFromClass;
        }
    }
    return '';
};

const isTooltipNode = (node) => {
    if (!React.isValidElement(node)) return false;
    const className = (node.props?.className || '').toString().toLowerCase();
    return className.includes('tooltip');
};

const stripTooltipNodes = (nodes) => {
    return React.Children.map(nodes, (node) => {
        if (!React.isValidElement(node)) return node;
        if (isTooltipNode(node)) return null;
        const cleanedChildren = node.props?.children
            ? stripTooltipNodes(node.props.children)
            : node.props?.children;
        return React.cloneElement(node, { ...node.props, children: cleanedChildren });
    });
};

const enhanceActionMenuNodes = (nodes) => {
    return React.Children.map(nodes, (node) => {
        if (!React.isValidElement(node)) return node;
        const enhancedChildren = node.props?.children
            ? enhanceActionMenuNodes(node.props.children)
            : node.props?.children;
        const elementType = typeof node.type === 'string' ? node.type.toLowerCase() : '';
        const tooltipText = findTooltipText(node);
        const isClickableEntry =
            elementType === 'button' ||
            elementType === 'a' ||
            (!!node.props?.onClick && !!tooltipText) ||
            (!!node.props?.href && !!tooltipText);
        if (!isClickableEntry) {
            return React.cloneElement(node, { ...node.props, children: enhancedChildren });
        }
        const existingClassName = node.props?.className || '';
        const label = normalizeText(tooltipText || extractActionLabel(node));
        const iconOnlyChildren = stripTooltipNodes(enhancedChildren);
        return React.cloneElement(node, {
            ...node.props,
            className: classNames(existingClassName, 'reimbursement-action-menu-entry'),
            children: (
                <>
                    <span className="reimbursement-action-menu-entry-icon">{iconOnlyChildren}</span>
                    {!!label && <span className="reimbursement-action-menu-entry-label">{label}</span>}
                </>
            ),
        });
    });
};

export const ActionMenu = ({ item, actionColumn, totalItems, iconType = 'bx-dots-vertical-rounded' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);
    const rawActionContent = actionColumn(item, totalItems);
    const actionContent = enhanceActionMenuNodes(rawActionContent);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div ref={menuRef} className="reimbursement-action-menu-wrap" onClick={(e) => e.stopPropagation()}>
            <div
                onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
                className="reimbursement-action-menu-trigger"
            >
                <i className={`bx ${isOpen ? 'bx-x' : iconType} reimbursement-action-menu-icon`} />
            </div>
            {isOpen && (
                <div className="reimbursement-action-menu-dropdown">
                    <div className="action-menu-item">{actionContent}</div>
                </div>
            )}
        </div>
    );
};

/*
EXAMPLE USAGE:

const[apiCallDone, setApiCallDone] = useState(''); //if you need to know the response of the api outside the component use this state

const listOptions = {
    "model": TestModel,                 // Model
    "urlGetSearch" : "/test...",       // url apiCall
};

return (
    <>
        <SortableList
            listOptions={listOptions}
            setApiCallDone={setApiCallDone}           //if you need to know the response of the api outside the component use this state
            // type="model"                          // If you want to use filters by model
        />
    </>
);
EXAMPLE USAGE: WITH EXPORT
  const listOptions = {
        "exportIsAvailable": true,                                 ---> // if you want to show the dropdown for exporting documents you must set this option to true 
        "urlGetListItemsToExport": "/user/admin/search"            ---> // Here you must add the url of the search to get the list of items to export. 
    };                                                                  // NOTE: if you want to export documents both parameters are mandatory ( exportIsAvailable, urlGetListItemsToExport)
*/

const SortableList = ({ 
                          fetchDataFunction,
                          listOptions = {},
                          //handleFilterChange,
                          setApiCallDone = null,
                          type,
                          onResponse = null,
                          isReadOnly = false,
                      }) => {
    const isMobile = CheckMobile(998);
    const [searchPressed, setSearchPressed] = useState(false);
    const updateSearchPressed = (value) => {
        //if (window.location.pathname === '/admin/economic-report' || window.location.pathname === '/organization-economic-report') {
            setSearchPressed(value);
        //}
    };

    const STATUS_CFG = {
        Förslag:  { label: "Utkast",               bg: "#F3F4F6", color: "#6B7280", dot: "#9CA3AF" },
        Skickat:  { label: "Inväntar godkännande", bg: "#EEF3FB", color: "#2C5DBF", dot: "#3A65B8" },
        Godkänd:  { label: "Godkänd",              bg: "#E8F5EE", color: "#157553", dot: "#1A7A4A" },
        Utbetald: { label: "Utbetald",             bg: "#F0FDF4", color: "#15803D", dot: "#22C55E" },
        Nekad:    { label: "Nekad",                bg: "#FDECEA", color: "#C0392B", dot: "#EF4444" },
    };

    let value;
    const viewName = listOptions?.viewName ?? '';
    const viewSettingStorage = localStorage.getItem('viewSetting');

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();

    const [urlFiltersLoaded, setUrlFiltersLoaded] = useState(false);

    if (viewSettingStorage) {
        const viewSetting = JSON.parse(viewSettingStorage);
        const specificSetting = viewSetting.find(setting => setting.viewId === viewName);
        value = specificSetting?.settings?.itemsPerPage;
    } 
     
    const { t } = useTranslation();
    const capitalizeFirstLetter = (text = '') => {
        if (!text) return '';
        return text.replace(/^([a-zA-ZÀ-ÿ])/, (match) => match.toUpperCase());
    };
    let search = LoadLocalStorage("search");
    const pageStorageKey = `currentPage_${window.location.pathname.replace(/\//g, '_')}`;

    useEffect(() => {
        const currentId = searchParams.get('id');
        const currentPath = window.location.pathname;
        
        // Detectar tipo de vista
        const viewType = 
            currentPath === '/admin/digital-account-list/digital-account' ? 'digital-account' :
            currentPath === '/admin/report-tax-info' ? 'tax-info' :
            currentPath === '/admin/organization-list/organization' ? 'organization' :
            currentPath === '/admin/user-list/user' ? 'user' :
            null;
        
        if (viewType && currentId) {
            // Clave específica para cada tipo de vista
            const storageKey = `currentAccountId_${viewType}`;
            const previousId = sessionStorage.getItem(storageKey);
            
            // Solo limpiar si cambiamos a un ID diferente dentro del mismo tipo de vista
            if (previousId && previousId !== currentId) {
                setCurrentPage(1);
                localStorage.removeItem('currentPage');
            }
            
            // Guardar el ID actual para este tipo de vista
            sessionStorage.setItem(storageKey, currentId);
        }
    }, [searchParams.get('id'), window.location.pathname]);

    // Limpiar sessionStorage al salir completamente de las vistas
    useEffect(() => {
        return () => {
            const currentPath = window.location.pathname;
            if (!currentPath.includes('/digital-account') && 
                !currentPath.includes('/report-tax-info') &&
                !currentPath.includes('/organization') &&
                !currentPath.includes('/user')) {
                sessionStorage.removeItem('currentAccountId_digital-account');
                sessionStorage.removeItem('currentAccountId_tax-info');
                sessionStorage.removeItem('currentAccountId_organization');
                sessionStorage.removeItem('currentAccountId_user');
            }
        };
    }, [location.pathname]);

    const isOuterListWithInnerList = 
        window.location.pathname === '/admin/organization-list' ||
        window.location.pathname === '/admin/digital-account-list' ||
        window.location.pathname === '/admin/report-tax' ||
        window.location.pathname === '/admin/user-list';

    useEffect(() => {
        const currentPath = window.location.pathname;
        
        const innerViewsPerOuter = [
            { outer: '/admin/organization-list', inner: '/admin/organization-list/organization' },
            { outer: '/admin/digital-account-list', inner: '/admin/digital-account-list/digital-account' },
            { outer: '/admin/report-tax', inner: '/admin/report-tax-info' },
            { outer: '/admin/user-list', inner: '/admin/user-list/user' },
        ];

        // Borrar claves de vista interior cuando estás en la exterior
        innerViewsPerOuter.forEach(({ outer, inner }) => {
            if (currentPath === outer) {
                const innerKey = `currentPage_${inner.replace(/\//g, '_')}`;
                localStorage.removeItem(innerKey);
            }
        });

        // Same for the filters saved by the lists inside those views (persistFilters)
        if (innerViewsPerOuter.some(({ outer }) => currentPath === outer)) {
            Object.keys(sessionStorage)
                .filter(key => key.startsWith(PERSIST_FILTERS_PREFIX))
                .forEach(key => sessionStorage.removeItem(key));
        }

        // Borrar claves de rutas que no tengan nada que ver con la ruta actual
        const allKnownPaths = innerViewsPerOuter.flatMap(({ outer, inner }) => [outer, inner]);
        const isRelatedToCurrentPath = (path) => 
            currentPath.startsWith(path) || path.startsWith(currentPath);

        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('currentPage_')) {
                const keyPath = key.replace('currentPage_', '').replace(/_/g, '/');
                const isKnown = allKnownPaths.some(p => p === keyPath);
                if (isKnown && !isRelatedToCurrentPath(keyPath)) {
                    localStorage.removeItem(key);
                }
            }
        });
    }, [location.pathname]);

    const currentPageLocal = LoadLocalStorage(pageStorageKey) 
        ? parseInt(LoadLocalStorage(pageStorageKey)) 
        : LoadLocalStorage('currentPage') 
            ? parseInt(LoadLocalStorage('currentPage')) 
            : 1;
    //const itemsPerPageLocal = LoadLocalStorage('itemsPerPage') ? parseInt(LoadLocalStorage('itemsPerPage')) : 10 ;
    const itemsPerPageLocal = value || parseInt(localStorage.getItem('itemsPerPage')) || 10;
    // Configure sorting
    const defaultSortField = listOptions?.sort?.defaultSortField;
    const defaultSortOrder = listOptions?.sort?.defaultSortOrder || 'asc';
    const initialParams = {};
    if (defaultSortField) {
        initialParams.sortField = defaultSortField;
        initialParams.sort = defaultSortOrder;
    }
    const [params, setParams] = useState(initialParams);
    //Configure pagination
    const urlPage = searchParams.get('page') ? parseInt(searchParams.get('page')) : null;
    const initialPage = urlPage || (listOptions?.pagination?.initialPage ? listOptions?.pagination?.initialPage : currentPageLocal);
    const initialItemsPerPage = listOptions?.pagination?.initialItemsPerPage ? listOptions?.pagination?.initialItemsPerPage : itemsPerPageLocal;

    // Initialize pagination state
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);
    const [totalPages, setTotalPages] = useState(0);
    const paginationControlsPosition = listOptions?.layoutOptions?.pagination?.controlsPosition || 'bottom';
    const paginationView = listOptions?.pagination?.controlsView !== undefined ? listOptions?.pagination?.controlsView : true;
    const byMonth = listOptions?.groupByMonth !== undefined ? listOptions?.groupByMonth : false;

    // Initialize items state
    const [items, setItems] = useState([]);

    const [costBearerNames, setCostBearerNames] = useState([]);
    const [costPalces, setCostPlaces] = useState([]);
    const [models, setModels] = useState([]);

    const [costBearerOptions, setCostBearerOptions] = useState([]);

    //const [isSplitIntoSubtypesClicked, setIsSplitIntoSubtypesClicked] = useState(false);

    const [totalAmount, setTotalAmount] = useState([]);

    // Items to export
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);
    const [optionExport, setOptionExport] = useState(null);

    // Initialize loading state
    const [loading, setLoading] = useState(true);
    const [buttonSearchIsDisable, setButtonSearchIsDisable] = useState(true);
    const [isSpinnerLoading, setIsSpinnerLoading] = useState(true);
    const [economicFilterLoading, setEconomicFilterLoading] = useState(true);


    const [spinnerLoading, setSpinnerLoading] = useState(false);
    // Initialize view mode state
    const defaultLayout = listOptions?.layoutOptions?.grid?.renderGrid ? (listOptions?.availableLayouts && listOptions?.availableLayouts.includes(listOptions?.layout) ? listOptions?.layout : 'table') : 'table';
    const [viewMode, setViewMode] = useState(defaultLayout);
    const canChangeLayout = listOptions?.availableLayouts
        ? listOptions.availableLayouts.length >= 2
        : true; // Default to true if `availableLayouts` is not provided
    // Set table classes
    const tableClass = listOptions?.layoutOptions?.table?.tableClass || 'table ';
    const theadClass = listOptions?.layoutOptions?.table?.theadClass || 'border-bottom-black';
    const tbodyClass = listOptions?.layoutOptions?.table?.tbodyClass || '';
    const trClass = listOptions?.layoutOptions?.table?.trClass || '';
    const thClass = listOptions?.layoutOptions?.table?.thClass || '';
    const tdClass = listOptions?.layoutOptions?.table?.tdClass || '';
    const insideClass = listOptions?.layoutOptions?.table?.insideClass || '';

    //filterOptions
    const filterPosition = listOptions?.layoutOptions?.filter?.filterPosition || 'top';
    // generic filter options
    const allowToggleFilterVisibility = listOptions?.layoutOptions?.filter?.allowToggleVisibility !== undefined ? listOptions.layoutOptions.filter.allowToggleVisibility : false;
    const defaultFilterVisibility = allowToggleFilterVisibility ? (listOptions?.layoutOptions?.filter?.defaultVisibility || false) : true;
    const [showGenericFilterInputs, setShowGenericFilterInputs] = useState(defaultFilterVisibility);
    const viewDropdownPerPage = listOptions?.pagination?.viewDropdownPerPage !== undefined ? listOptions?.pagination?.viewDropdownPerPage : true;
    // Opt-in: reuse the ReportProceedingList design (filters bar + card table) in other views
    const useModernListDesign = listOptions?.modernDesign === true;
    const isReimbursementMobileFiltersMode = isMobile && (viewName === 'ReportProceedingList' || viewName === 'SalarySheetList' || viewName === 'AdminSalarySheetList' || viewName === 'AdminApiKeyList' || useModernListDesign);
    const isReimbursementFiltersDesignMode = viewName === 'ReportProceedingList' || viewName === 'SalarySheetList' || viewName === 'AdminSalarySheetList' || viewName === 'AdminApiKeyList' || useModernListDesign;
    const isReimbursementDesktopFiltersMode = !isMobile && isReimbursementFiltersDesignMode;

    // Export documents is avaible
    const exportIsAvailable = listOptions?.exportIsAvailable ?? false;
    const filterTestIsAvailable = listOptions?.filterTestIsAvailable ?? false;
    // Opt-in for lists inside an info page (ignoreUrlParams): keep the applied filters in the form and
    // restore them when the user opens an item and comes back (saved per list and per page id).
    // Their filters stay out of the URL, so the other lists of the page never read them as their own
    const persistFilters = listOptions?.persistFilters === true && listOptions?.ignoreUrlParams === true;
    const persistFiltersKey = `${PERSIST_FILTERS_PREFIX}${viewName}_${new URLSearchParams(window.location.search).get('id') ?? ''}`;
    // persistFilters: query of the last search (without paging), used by the export instead of the URL
    const persistedQueryRef = useRef('');
    const economicReportFilter = listOptions?.economicReportFilter ?? false;
    const urlGetListItemsToExport = listOptions?.urlGetListItemsToExport ?? '';
    const nameFile = listOptions?.nameFile ?? '';
    const ignoreFieldsToExport = listOptions?.ignoreFields ?? [];
    const modelExport = listOptions?.modelExport ?? '';
    const modelType = listOptions?.modelType ?? '';
    const hideFilter = listOptions?.hideFilter ?? false;
    const wrapTopInCard = listOptions?.layoutOptions?.wrapTopInCard ?? false;
    const renderTopHeader = listOptions?.layoutOptions?.topSection?.renderHeader;

    //const viewDropdownExport = listOptions?.pagination?.viewDropdownExport !== undefined ? listOptions?.pagination?.viewDropdownExport : true;
    const debounceTime = listOptions?.filter?.debounceTime || 1000; //500 1200
    // Initialize columns state
    const [columns, setColumns] = useState([]);
    const [allColumns, setAllColumns] = useState([]);
    // CURRENT SORT VALUES
    const [currentSortValues, setCurrentSortValues] = useState({});
    const [totalItems, setTotalItem] = useState('');
    const fetchItemsRef = useRef(null)
    // TODO: FIX IN THE FUTURE
    const [counter, setCounter] = useState(0);
   // console.log('handleFilterChange', handleFilterChange)
    //const addButton = listOptions?.addButton !== undefined ? listOptions?.addButton : false;

    const isRestoringRef = useRef(false);
    const hasRestoredUrlRef = useRef(false);
    
    const {orgData} = useSelector(state => state.auth);

    const [filter, setFilter] = useState("");
    const [apiResponseList, setApiResponseList] = useState(null);
    const urlGetSearch = listOptions?.urlGetSearch ?? '';
    const [tempFilters, setTempFilters] = useState({});
    const [ignoreFieldsEconomicReportToExport, setIgnoreFieldsEconomicReportToExport] = useState('');
    const [selectedField, setSelectedField] = useState('');
    const [viewSettingUpdated, setViewSettingUpdated] = useState('');
    const [activeSplitIntoSubtypes, setActiveSplitIntoSubtypes] = useState(!!tempFilters.splitIntoSubtypes);
    const [exportNotifications, setExportNotifications] = useState([]);
    let currentExportId = null;

    // ECONOMIC REPORT FILTER
    const [isRange, setIsRange] = useState({});
    const [click, setClick] = useState(false);
    const [minValue, setMinValue] = useState('');
    const [maxValue, setMaxValue] = useState('');
    const [groupBy, setGroupBy] = useState('');
    const [normalValues, setNormalValues] = useState({});
    const [localDisplay, setLocalDisplay] = useState(listOptions.filtersOpen ?? false);
    const customLocale = CustomDateLocale();
    const [buttonClearIsDisable, setButtonClearIsDisable] = useState(true);
    const [amountValue, setAmountValue] = useState('');
    const [rangeValues, setRangeValues] = useState({
        min: { value: '', name: '' },
        max: { value: '', name: '' },
    });
    const [amountRanges, setAmountRanges] = useState({});
    const [amountValues, setAmountValues] = useState({});
    const today = new Date();
    const [selectedValues, setSelectedValues] = useState({});

    const [dateValues, setDateValues] = useState(() => {
        const initialValues = {};
        columns.forEach((column) => {
            initialValues[column.field] = {
                startDate: new Date(today.setHours(0, 0, 0, 0)),
                endDate: new Date(today.setHours(23, 59, 59, 0)),
            };
        });
        return initialValues;
    });

    const [showDatePickerMap, setShowDatePickerMap] = useState({});
    const [collapsedMonthGroups, setCollapsedMonthGroups] = useState({});
    const [expandedSalaryRows, setExpandedSalaryRows] = useState({});
    const [salaryRowLoading, setSalaryRowLoading] = useState({});
    const [salaryRowData, setSalaryRowData] = useState({});
    const [payoutCardExpanded, setPayoutCardExpanded] = useState({});
    const [infoPanelOpen, setInfoPanelOpen] = useState(false);
    const [infoPanelItemId, setInfoPanelItemId] = useState(null);
    const [infoPanelLoading, setInfoPanelLoading] = useState(false);
    const [infoPanelUserId, setInfoPanelUserId] = useState(null);
    const [infoPanelVisualId, setInfoPanelVisualId] = useState(null);
    const [detailPurposeMap, setDetailPurposeMap] = useState({});

    const { ApiCall } = useApiCallService();

    const fetchDetailsPurpose = async (entry) => {
        if (!Array.isArray(entry?.paymentDetails)) return;
        // Only fetch purpose for items with model === "Förrättning"
        const forrattningDetails = entry.paymentDetails.filter(d =>
            (d?.model || d?.Model) === 'Förrättning'
        );
        if (forrattningDetails.length === 0) return;
        const detailIds = forrattningDetails.map(d => d?.modelId || d?.ModelId || null).filter(Boolean);
        if (detailIds.length === 0) return;

        setDetailPurposeMap(prev => {
            const next = { ...prev };
            detailIds.forEach(id => {
                next[id] = { ...next[id], loading: true };
            });
            return next;
        });

        const results = await Promise.allSettled(
            detailIds.map(id =>
                new Promise((resolve, reject) => {
                    ApiCall(
                        { method: 'get', url: `/reimbursement?id=${id}` },
                        (data) => resolve(data),
                        (error) => reject(error)
                    );
                })
            )
        );

        setDetailPurposeMap(prev => {
            const next = { ...prev };
            detailIds.forEach((id, idx) => {
                const result = results[idx];
                next[id] = {
                    loading: false,
                    data: result.status === 'fulfilled' ? result.value : null,
                };
            });
            return next;
        });
    };

    const routeGroupsRaw = [
        ['adminEconomicReportResults', '/admin/economic-report', '/admin/economic-report-info'],
        ['organizationEconomicReportResults', '/organization-economic-report', '/organization-economic-report-info'],
        ['honorariumListResults', '/admin/honorarium-list', '/admin/honorarium-list/honorarium'],
        ['reportProceedingListResults', '/report-proceeding-list', '/report-proceeding-list/report-proceeding'],
        ['digitalAccountResults', '/digital-account', '/digital-account/account-row'],
        ['payResults', '/pay', '/pay/pay'],
        ['payQuickPaymentResults', '/pay-quickpayment', '/pay-quickpayment/pay'],
        ['qrCodeListActiveResults', '/qr-code-list', '/qr-code-list/qr-code-info'],
        ['qrCodeListInactiveResults', '/qr-code-list', '/qr-code-list/qr-code-info'],
        ['organizationUserListResults', '/organization-user-list', '/organization-user-list/user'],
        ['organizationAttestListResults', '/organization-attest-list', '/organization-attest-info'],
        ['adminAttestListPendingPrimaryResults', '/admin/attest-list-pending-primary', '/admin/attest-list/attest'],
        ['adminAttestListPendingResults', '/admin/attest-list-pending', '/admin/attest-list/attest'],
        ['adminAttestListHistoryResults', '/admin/attest-list-history', '/admin/attest-list/attest'],
        ['adminDigitalAccountListResults', '/admin/digital-account-list', '/admin/digital-account-list/digital-account', '/admin/digital-account-list/digital-account/account-row'],
        ['adminReportTaxResults', '/admin/report-tax', '/admin/report-tax-info', '/admin/user-list/user'],
        ['adminOrganizationListResults', '/admin/organization-list', '/admin/organization-list/organization', '/admin/user-list/user'],
        ['adminUserListResults', '/admin/user-list', '/admin/user-list/user'],
        ['adminBankTransferPendingResults', '/bank-transfer-list', '/bank-transfer-info'],
        ['adminBankTransferHistoryResults', '/bank-transfer-list/bank-transfer-history-list', '/bank-transfer-info'],
        ['adminSwedBankReportResults', '/admin/swedbank-report', '/pay-quickpayment/report'],
    ];

    const routeGroups = routeGroupsRaw.map(([storageKey, ...paths]) => ({
        storageKey,
        paths
    }));

    const getStorageKeyForPath = (pathname) => {
        if (viewName && viewName.trim() !== '' && pathname.includes('/qr-code-list')) {
            return `${viewName}Results`;
        }
        const group = routeGroups.find(g => g.paths.includes(pathname));
        return group ? group.storageKey : null;
    };

    const getGroupForPath = (pathname) => {
        return routeGroups.find(group => group.paths.includes(pathname));
    };

    const isInfoView = () => {
        if (window.location.pathname.includes('/qr-code-list')) {
            return false;
        }
        const ignoreUrlParams = listOptions?.ignoreUrlParams || false;
        return ignoreUrlParams;
    };

    // ─── Ajustar padding-right del td de totalAmountUserSalary para que
    //      el contenido terminen alineado con el final del botón del header ───
    const headerPaddingLeftRef = useRef('');

    // Calcular el ancho máximo de los datos en salaryRowData usando useMemo
    const dataMaxContentWidth = useMemo(() => {
        const calcPayoutAmount = (entry) => {
            const details = Array.isArray(entry?.paymentDetails) ? entry.paymentDetails : [];
            return details.reduce((sum, detail) => {
                const value = Number(detail?.instructedAmount ?? 0);
                return sum + (Number.isFinite(value) ? value : 0);
            }, 0);
        };
        let maxDataWidth = 0;
        if (!salaryRowData || Object.keys(salaryRowData).length === 0) return 0;
        Object.values(salaryRowData).forEach(entries => {
            if (!Array.isArray(entries)) return;
            entries.forEach(entry => {
                const payoutAmount = calcPayoutAmount(entry);
                let content = ToCustomDecimal(payoutAmount) + ' kr';
                const measurer = document.createElement('span');
                measurer.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;font-size:13px;font-weight:500;font-family:\'Plus Jakarta Sans\',sans-serif;';
                measurer.textContent = content;
                document.body.appendChild(measurer);
                const w = measurer.getBoundingClientRect().width;
                document.body.removeChild(measurer);
                if (w > maxDataWidth) maxDataWidth = w;

                if (Array.isArray(entry?.paymentDetails)) {
                    entry.paymentDetails.forEach(detail => {
                        const instructed = detail?.instructedAmount ?? detail?.amount ?? 0;
                        content = ToCustomDecimal(instructed) + ' kr';
                        const m2 = document.createElement('span');
                        m2.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;font-size:13px;font-weight:500;font-family:\'Plus Jakarta Sans\',sans-serif;';
                        m2.textContent = content;
                        document.body.appendChild(m2);
                        const w2 = m2.getBoundingClientRect().width;
                        document.body.removeChild(m2);
                        if (w2 > maxDataWidth) maxDataWidth = w2;
                    });
                }
            });
        });
        return maxDataWidth;
    }, [salaryRowData]);

    const dataMaxContentWidthRef = useRef(0);
    // Sincronizar la ref con el valor de useMemo
    dataMaxContentWidthRef.current = dataMaxContentWidth;

    // Establecer min-width del <th> basado en los datos cargados (solo cuando cambian los datos)
    useEffect(() => {
        if (window.location.pathname !== '/salary-sheet') return;
        if (dataMaxContentWidth === 0) return;

        const headerTh = document.querySelector('th[data-amount-header="true"]');
        if (!headerTh) return;

        const minHeaderWidth = Math.max(dataMaxContentWidth * 0.55, 80);
        headerTh.style.minWidth = minHeaderWidth + 'px';
    }, [dataMaxContentWidth]);

    const alignAmountColumn = useCallback(() => {
        if (window.location.pathname !== '/salary-sheet') return;

        const tds = document.querySelectorAll('td[data-amount-td="true"]');
        if (tds.length === 0) return;

        tds.forEach(td => {
            td.style.textAlign = 'right';
            td.style.boxSizing = 'border-box';
        });
    }, []);

    // Ejecutar el ajuste una vez al montar y cuando cambien los items
    useEffect(() => {
        if (window.location.pathname !== '/salary-sheet') return;

        // Ejecutar después de que el DOM se haya pintado con los nuevos items
        requestAnimationFrame(() => {
            alignAmountColumn();
        });
    }, [items, alignAmountColumn]);

    // También ejecutar el ajuste cuando se expanden/colapsan filas de salary
    // (los payout rows tienen celdas con data-amount-td que necesitan alineación)
    // La función alignAmountColumn ya depende de salaryRowData para incluir
    // el contenido de datos cargados aunque no estén visibles
    useEffect(() => {
        if (window.location.pathname !== '/salary-sheet') return;

        requestAnimationFrame(() => {
            alignAmountColumn();
        });
    }, [expandedSalaryRows, payoutCardExpanded, alignAmountColumn]);

    // También re-ejecutar al cambiar el tamaño de la ventana (con debounce)
    useEffect(() => {
        if (window.location.pathname !== '/salary-sheet') return;

        let resizeTimer;
        const onResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                alignAmountColumn();
            }, 150);
        };

        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            clearTimeout(resizeTimer);
        };
    }, [alignAmountColumn]);

    // ─── Ajustar padding-left del td de visualId y createdAt en el summary
    //      para que el contenido empiece alineado con el texto del header ───
    useEffect(() => {
        if (window.location.pathname !== '/salary-sheet') return;

        const alignLeftColumns = () => {
            // Procesamos cada columna que necesita alineación izquierda
            ['visualId', 'createdAt', 'fileCreatedAt'].forEach(colName => {
                const headerTh = document.querySelector(`th[data-align-left-header="${colName}"]`);
                if (!headerTh) return;

                // Medimos la posición del borde izquierdo del contenido del header
                // (el button o span dentro del th)
                const contentEl = headerTh.querySelector('.sl-th-btn') || headerTh.querySelector('.sl-th-label-static');
                if (!contentEl) return;

                const contentLeft = contentEl.getBoundingClientRect().left;

                // Ajustamos cada celda para que su contenido empiece en contentLeft
                const tds = document.querySelectorAll(`td[data-align-left-td="${colName}"]`);
                tds.forEach(td => {
                    const tdLeft = td.getBoundingClientRect().left;
                    const paddingLeft = Math.max(0, contentLeft - tdLeft);
                    td.style.paddingLeft = paddingLeft + 'px';
                    td.style.boxSizing = 'border-box';
                });
            });
        };

        // Loop continuo con requestAnimationFrame
        let rafId;
        const loop = () => {
            alignLeftColumns();
            rafId = requestAnimationFrame(loop);
        };
        rafId = requestAnimationFrame(loop);

        window.addEventListener('resize', alignLeftColumns);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', alignLeftColumns);
        };
    }, []);



    useEffect(() => {
        if (isInfoView()) {
            setTempFilters({});
            setSelectedValues({});
            setDateValues({});
            setAmountRanges({});
            setAmountValues({});
            setNormalValues({});
            setGroupBy('');
            setFilter('');
            setUpdateFilter([]);
        }
    }, [location.pathname, listOptions?.ignoreUrlParams]);

    useEffect(() => {
        const currentPath = window.location.pathname;

        const listInsideAListView =
            currentPath.includes('/admin/organization-list/organization') ||
            currentPath.includes('/admin/digital-account-list/digital-account') ||
            currentPath.includes('/admin/report-tax-info');

        if (listInsideAListView) return;

        const allowedKeys = new Set();

        routeGroups.forEach(g => {
            const matches = g.paths.some(p =>
            currentPath === p ||
            currentPath.startsWith(p + "/") ||
            currentPath.includes(p)
            );
            if (matches) allowedKeys.add(g.storageKey);
        });

        const dynamicKey = getStorageKeyForPath(currentPath);
        if (dynamicKey) allowedKeys.add(dynamicKey);

        routeGroups.forEach(g => {
            const key = g.storageKey;
            try {
            if (localStorage.getItem(key) && !allowedKeys.has(key)) {
                localStorage.removeItem(key);
            }
            } catch (err) {
            console.error("Error checking/removing storageKey", key, err);
            }
        });

    }, [window.location.pathname]);

    //console.log('checkResponse',checkResponse )
    const fetchItems = async () => {
        const fetchApiCall = (response) => {
            setIsSpinnerLoading(true);
            if (response) {
                setActiveSplitIntoSubtypes(!!tempFilters.splitIntoSubtypes);
                if (onResponse !== null) onResponse();
                setSelectedField('');
                if (groupBy !== '') {
                    setClick(true);
                    setSelectedField(groupBy);
                    setIgnoreFieldsEconomicReportToExport(groupBy);
                }

                // const isEconomicReport = window.location.pathname === '/admin/economic-report' || window.location.pathname === '/organization-economic-report';

                const storageKey = getStorageKeyForPath(window.location.pathname, viewName);
                const listInsideAListView = 
                    window.location.pathname.includes('/admin/user-list/user') || 
                    window.location.pathname.includes('/admin/organization-list/organization') ||
                    window.location.pathname.includes('/admin/digital-account-list/digital-account') ||
                    window.location.pathname.includes('/admin/report-tax-info');

                if (storageKey && searchPressed && !listInsideAListView) {
                    localStorage.removeItem(storageKey);
                    if (response.items !== undefined) {
                            const dataToStore = {
                        ...response,
                        savedUrl: window.location.search
                    };
                    localStorage.setItem(storageKey, JSON.stringify(dataToStore));
                    }
                }

                setApiResponseList(response);

                let localStored = null;
                if (storageKey) {
                    try {
                        localStored = JSON.parse(localStorage.getItem(storageKey));
                    } catch (e) {
                        console.warn(`Error parsing localStorage ${storageKey}`, e);
                    }
                }
                
                const isInfoViewPath = window.location.pathname.includes('/admin/organization-list/organization')
                    || window.location.pathname.includes('/admin/user-list/user')
                    || window.location.pathname.includes('/admin/digital-account-list/digital-account')
                    || window.location.pathname.includes('/admin/report-tax-info')
                    || window.location.pathname.includes('/bank-transfer-list/bank-transfer-history-list')
                    || window.location.pathname.includes('/qr-code-list/qr-code-info')
                    || isInfoView();
                
                const dataToUse = (storageKey && localStored && !isInfoViewPath) ? localStored : response;
                if (storageKey && localStored && !isInfoViewPath && localStored.savedUrl && !searchPressed && !hasRestoredUrlRef.current) {
                    const currentUrlParams = window.location.search;
                    const savedUrlParams = localStored.savedUrl;
                    
                    if (currentUrlParams !== savedUrlParams) {
                        if (!currentUrlParams || isOnlyPaginationOrSort(currentUrlParams.replace(/^\?/, ''))) {
                            if (savedUrlParams && !isOnlyPaginationOrSort(savedUrlParams.replace(/^\?/, ''))) {
                                hasRestoredUrlRef.current = true;
                                isRestoringRef.current = true;
                                
                                navigate(`${location.pathname}${savedUrlParams}`, { 
                                    replace: true
                                });
                                
                                setTimeout(() => {
                                    isRestoringRef.current = false;
                                }, 500);
                            }
                        }
                    }
                }
                setItems(dataToUse.items);
                setTotalPages(dataToUse.pagination?.totalPages);
                setTotalItem(dataToUse.pagination?.total);
                setTotalAmount(dataToUse?.totalAmount);
                setCostBearerNames(dataToUse?.costBearerNames);
                setCostPlaces(dataToUse?.costPlaces);
                setModels(dataToUse?.models);
                setError(false);
                setLoading(false);
                setSpinnerLoading(false);
                setEconomicFilterLoading(false);
                if ((window.location.pathname === '/organization-economic-report' || window.location.pathname === '/admin/economic-report') && !searchPressed) {
                    setButtonSearchIsDisable(false);
                } else {
                    setButtonSearchIsDisable(true);
                }
                if (setApiCallDone !== null) setApiCallDone(response);
            } else {
                if (setApiCallDone !== null) setApiCallDone(response);
                setError(true);
                setTimeout(() => {
                    setSelectedField('');
                    setLoading(false);
                    setEconomicFilterLoading(false);
                }, 10000);
            }
        };
    
        setSpinnerLoading(true);
    
        // Si ignoreUrlParams es true, construimos la URL directamente sin modificar la query original
        const ignoreUrlParams = listOptions?.ignoreUrlParams || false;
        
        if (ignoreUrlParams) {
            // Para casos como digital-account donde no queremos tocar los params de la URL base
            const urlParts = urlGetSearch.split('?');
            const baseUrl = urlParts[0];
            const existingParams = urlParts[1] || '';
            
            const searchParams = new URLSearchParams(existingParams);
            searchParams.set('page', currentPage);
            searchParams.set('perPage', itemsPerPage);
            
            if (Object.keys(currentSortValues).length !== 0) {
                searchParams.set('sortField', currentSortValues.sortField);
                searchParams.set('sort', currentSortValues.sort);
            }
            
            Object.entries(tempFilters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                        searchParams.set(key, value);
                    } else if (value && typeof value === 'object' && typeof value.value === 'string') {
                        searchParams.set(key, value.value);
                    }
                }
            });
            
            // NUEVO: Añadir filtros multi-select
            Object.entries(selectedValues).forEach(([key, values]) => {
                if (Array.isArray(values)) {
                    values.forEach(val => {
                        if (typeof val === 'string') {
                            searchParams.append(key, val);
                        } else if (val && val.value) {
                            searchParams.append(key, val.value);
                        }
                    });
                }
            });
            
            // NUEVO: Añadir rangos de fechas
            Object.entries(dateValues).forEach(([field, { startDate, endDate }]) => {
                if (startDate && endDate) {
                    searchParams.set(`${field}fromDate`, ChangeDateFormat('dateTime', startDate));
                    searchParams.set(`${field}toDate`, ChangeDateFormat('dateTime', endDate));
                }
            });
            
            // NUEVO: Añadir rangos de montos
            const isProvided = (val) => val !== undefined && val !== null && val !== '';
            Object.entries(amountRanges).forEach(([field, { min, max }]) => {
                if (isProvided(min) || isProvided(max)) {
                    searchParams.set(`min${field}`, isProvided(min) ? min : -9000000);
                    searchParams.set(`max${field}`, isProvided(max) ? max : 9000000);
                }
            });

            try {
                const currentPath = (typeof window !== 'undefined' && window.location && window.location.pathname) ? window.location.pathname : '';
                if (currentPath.includes('admin/user-list/user')) {
                    const currentSearchParams = (typeof window !== 'undefined' && window.location && window.location.search) ? new URLSearchParams(window.location.search) : null;
                    const idFromUrl = currentSearchParams ? currentSearchParams.get('id') : null;
                    if (idFromUrl) {
                        searchParams.set('id', idFromUrl);
                    }
                }
            } catch (e) {
                console.warn('No se pudo extraer id desde la URL actual:', e);
            }

            const finalUrl = `${baseUrl}?${searchParams.toString()}`;

            if (persistFilters) {
                const searchQuery = new URLSearchParams(searchParams);
                searchQuery.delete('page');
                searchQuery.delete('perPage');
                persistedQueryRef.current = searchQuery.toString();

                const nonFilterKeys = new Set([...new URLSearchParams(existingParams).keys(), 'page', 'perPage', 'sortField', 'sort', 'id']);
                try {
                    sessionStorage.setItem(persistFiltersKey, JSON.stringify({
                        tempFilters, normalValues, selectedValues, dateValues, amountRanges, amountValues, currentSortValues,
                        hasFilters: [...searchParams.keys()].some(key => !nonFilterKeys.has(key)),
                    }));
                } catch (e) {
                    console.warn('Could not save the list filters', e);
                }
            }

            const apiCallOptions = {
                method: "get",
                url: finalUrl,
            };
            
            try {
                await ApiCall(apiCallOptions, fetchApiCall);
            } catch (error) {
                console.log(error);
            }
            return;
        }
        
        let base = urlGetSearch.split('?')[0];
        const combinedParams = { ...tempFilters };
        delete combinedParams.orgLabel;
    
        Object.entries(dateValues).forEach(([field, { startDate, endDate }]) => {
            if (startDate && endDate) {
                combinedParams[`${field}fromDate`] = ChangeDateFormat('dateTime', startDate);
                combinedParams[`${field}toDate`] = ChangeDateFormat('dateTime', endDate);
            }
        });
    
        const isProvided = (val) => val !== undefined && val !== null && val !== '';
        Object.entries(amountRanges).forEach(([field, { min, max }]) => {
            if (isProvided(min) || isProvided(max)) {
                combinedParams[`min${field}`] = isProvided(min) ? min : -9000000;
                combinedParams[`max${field}`] = isProvided(max) ? max : 9000000;
            }
        });
    
        if (groupBy !== '') {
            combinedParams['groupby'] = groupBy;
        }
    
        const searchParams = new URLSearchParams();
    
        Object.entries(combinedParams).forEach(([key, value]) => {
            if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                searchParams.set(key, value);
            }
            else if (value && typeof value === 'object' && typeof value.value === 'string') {
                searchParams.set(key, value.value);
            }
        });

        if (window.location.pathname === '/organization-economic-report') {
            const organizationId = orgData?.orgId;
            if (organizationId && !searchParams.has("organizationId")) {
                searchParams.set("organizationId", organizationId);
            }
        }
    
        Object.entries(selectedValues).forEach(([key, values]) => {
            if (Array.isArray(values)) {
                values
                    .filter(val =>
                        val &&
                        (
                            (typeof val === 'object' && typeof val.value === 'string') ||
                            typeof val === 'string'
                        )
                    )
                    .forEach(val => {
                        if (typeof val === 'string') {
                            searchParams.append(key, val);
                        } else {
                            searchParams.append(key, val.value);
                        }
                    });
            }
        });                
    
        searchParams.set('page', currentPage);
        searchParams.set('perPage', itemsPerPage);
        if (Object.keys(currentSortValues).length !== 0) {
            searchParams.set('sortField', currentSortValues.sortField);
            searchParams.set('sort', currentSortValues.sort);
        }
        if (window.location.pathname === '/admin/economic-report' || window.location.pathname === '/organization-economic-report') {
            if (searchPressed) {
                searchParams.set('fromSearchButton', true);
            } else {
                searchParams.set('fromSearchButton', false);
            }
        }
    
        //const isEconomicReport = window.location.pathname === '/admin/economic-report' || window.location.pathname === '/admin/economic-report-info' || window.location.pathname === '/organization-economic-report' || window.location.pathname === '/organization-economic-report-info';
        const storageKey = getStorageKeyForPath(window.location.pathname, viewName);
        let finalUrl;
        
        const cleanedParams = new URLSearchParams();

        for (const [key, value] of searchParams.entries()) {
            if (value == null) continue;
            const trimmed = value.toString().trim();
            if (trimmed === "") continue;

            if (trimmed === "-9000000" || trimmed === "9000000") continue;

            cleanedParams.append(key, value);
        }

        const newQuery = cleanedParams.toString();

        if (storageKey) {
            finalUrl = `${base}?${cleanedParams.toString()}`;
        
            const finalQueryString = finalUrl.split("?")[1];
            // if (finalUrl.includes("digital-account/admin/search")) {
            //     SaveLocalStorage("search", finalQueryString);
            // }
            if (storageKey && searchPressed) {
                SaveLocalStorage("search", newQuery);
            } else {
                localStorage.removeItem('fetchSearchResults');
            }
        } else {
            finalUrl = `${base}?${cleanedParams.toString()}`;
            setSpinnerLoading(true);
            if (finalUrl.includes("digital-account/admin/search")) {
                SaveLocalStorage("search", finalUrl.split("?")[1]);
            }
            localStorage.removeItem('fetchSearchResults');
        }
    
        navigate(newQuery ? `${location.pathname}?${newQuery}` : location.pathname, { replace: true });
        const apiCallOptions = {
            method: "get",
            url: finalUrl,
        };
        try {
            await ApiCall(apiCallOptions, fetchApiCall);
        } catch (error) {
            console.log(error);
        }
    };        

    const updateURLWithFilters = useCallback((filters) => {
        const params = new URLSearchParams();
        
        // Agregar filtros normales
        Object.entries(filters).forEach(([key, value]) => {
            if (value && value !== '' && value !== null && value !== undefined) {
                if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                    params.set(key, value);
                } else if (value && typeof value === 'object' && typeof value.value === 'string') {
                    params.set(key, value.value);
                }
            }
        });

        // Agregar valores multi-select
        Object.entries(selectedValues).forEach(([key, values]) => {
            if (Array.isArray(values)) {
                values.forEach(val => {
                    if (typeof val === 'string') {
                        params.append(key, val);
                    } else if (val && val.value) {
                        params.append(key, val.value);
                    }
                });
            }
        });

        // Agregar rangos de fechas
        Object.entries(dateValues).forEach(([field, { startDate, endDate }]) => {
            if (startDate && endDate) {
                params.set(`${field}fromDate`, ChangeDateFormat('dateTime', startDate));
                params.set(`${field}toDate`, ChangeDateFormat('dateTime', endDate));
            }
        });

        // Agregar rangos de montos
        Object.entries(amountRanges).forEach(([field, { min, max }]) => {
            if (min !== '' && min !== null && min !== undefined) {
                params.set(`min${field}`, min);
            }
            if (max !== '' && max !== null && max !== undefined) {
                params.set(`max${field}`, max);
            }
        });

        // Agregar paginación y ordenamiento
        params.set('page', currentPage);
        params.set('perPage', itemsPerPage);
        
        if (Object.keys(currentSortValues).length !== 0) {
            params.set('sortField', currentSortValues.sortField);
            params.set('sort', currentSortValues.sort);
        }

        // Actualizar la URL sin recargar la página
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }, [navigate, location.pathname, selectedValues, dateValues, amountRanges, currentPage, itemsPerPage, currentSortValues]);

    const loadFiltersFromURL = useCallback(() => {
        if (urlFiltersLoaded) return; // Solo cargar una vez

        const filters = {};
        const multiValues = {};
        const dateRanges = {};
        const amountFilters = {};
        let hasRealFilters = false; // Para determinar si hay filtros reales (no paginación/ordenamiento/navegación)

        // Parámetros que NO son filtros (paginación, ordenamiento, navegación interna)
        const nonFilterParams = ['page', 'perPage', 'sortField', 'sort', 'id', 'fromSearchButton'];

        for (let [key, value] of searchParams.entries()) {
            // Ignorar parámetros que no son filtros
            if (nonFilterParams.includes(key)) {
                if (key === 'page') {
                    setCurrentPage(parseInt(value));
                } else if (key === 'perPage') {
                    setItemsPerPage(parseInt(value));
                }
                continue;
            }

            if (['costPlace', 'costBearer', 'model'].includes(key)) {
                if (!multiValues[key]) multiValues[key] = [];
                multiValues[key].push({ value, label: value });
                hasRealFilters = true;
            }
            else if (key.includes('fromDate') || key.includes('toDate')) {
                const fieldName = key.replace(/(fromDate|toDate)/, '');
                if (!dateRanges[fieldName]) dateRanges[fieldName] = {};
                if (key.includes('fromDate')) {
                    dateRanges[fieldName].startDate = new Date(value);
                } else {
                    dateRanges[fieldName].endDate = new Date(value);
                }
                hasRealFilters = true;
            }
            else if (key.startsWith('min') || key.startsWith('max')) {
                const fieldName = key.replace(/^(min|max)/, '');
                if (!amountFilters[fieldName]) amountFilters[fieldName] = {};
                if (key.startsWith('min')) {
                    amountFilters[fieldName].min = value;
                } else {
                    amountFilters[fieldName].max = value;
                }
                hasRealFilters = true;
            }
            else if (key === 'groupby') {
                setGroupBy(value);
                hasRealFilters = true;
            } else {
                filters[key] = value;
                hasRealFilters = true;
            }
        }

        if (Object.keys(multiValues).length > 0) setSelectedValues(multiValues);
        if (Object.keys(dateRanges).length > 0) setDateValues(dateRanges);
        if (Object.keys(amountFilters).length > 0) setAmountRanges(amountFilters);
        if (Object.keys(filters).length > 0) setTempFilters(filters);

        if (searchParams.has('sortField') && searchParams.has('sort')) {
            setCurrentSortValues({
                sortField: searchParams.get('sortField'),
                sort: searchParams.get('sort')
            });
        }

        // Habilitar botón de limpiar si hay filtros reales aplicados
        if (hasRealFilters) {
            setButtonClearIsDisable(false);
        }

        setUrlFiltersLoaded(true);
    }, [searchParams, urlFiltersLoaded]);

    // Cargar filtros desde URL SOLO al montar
    useEffect(() => {
        if (searchParams.toString() && !urlFiltersLoaded) {
            loadFiltersFromURL();
        }
    }, []);

    // Cargar filtros desde URL al montar el componente Y ejecutar búsqueda
    useEffect(() => {
        if (searchParams.toString()) {
            const loadedFilters = loadFiltersFromURL();
            
            // Esperar a que los estados se actualicen antes de buscar
            setTimeout(() => {
                // Marcar que viene de URL para forzar la búsqueda
                setFilter(searchParams.toString());
                setButtonSearchIsDisable(false);
            }, 200);
        }
    }, [searchParams]);

    useEffect(() => {
        // Si hay parámetros en URL y aún no hemos cargado, esperar
        if (searchParams.toString() && !urlFiltersLoaded) {
            return;
        }

        // If we are restoring, skip this effect
        if (isRestoringRef.current) {
            return;
        }

        // If a call is already in progress, we cancel it.
        if (fetchItemsRef.current) {
            clearTimeout(fetchItemsRef.current);
        }

        const cameFromInfo = location.state?.cameFromInfo || false;

        // persistFilters lists keep the form as it is, so it shows the filter that is applied
        if (isInfoView() && !persistFilters) {
            setTempFilters({});
            setSelectedValues({});
            setDateValues({});
            setAmountRanges({});
        }
        
        if (window.location.pathname === '/organization-economic-report' && !cameFromInfo) {
            fetchItemsRef.current = fetchItems();
        } else {
            fetchItemsRef.current = setTimeout(() => {
                setIsSpinnerLoading(false);
                fetchItems();
            }, 300);
        }

        return () => {
            if (fetchItemsRef.current) {
                clearTimeout(fetchItemsRef.current);
            }
        };
    }, [currentPage, itemsPerPage, params, currentSortValues, filter, urlFiltersLoaded]);

    // First ApiCall
    // useEffect(() => {
    //     fetchItems();
    // }, []);

    //Dependencies ApiCall
    useEffect(() => {
        
       // If a call is already in progress, we cancel it.
       if (fetchItemsRef.current) {
        clearTimeout(fetchItemsRef.current);
       }

       // If we are restoring, skip this effect
        if (isRestoringRef.current) {
            return;
        }

        const cameFromInfo = location.state?.cameFromInfo || false;
        // Set a new call to be executed after a delay.
        if (window.location.pathname === '/organization-economic-report' && !cameFromInfo) {
            fetchItemsRef.current = fetchItems();
        } else {
            // Set a new call to be executed after a delay.
            fetchItemsRef.current = setTimeout(() => {
                setIsSpinnerLoading(false);
                fetchItems();
            }, 300);
        }

        // Cleanup when unmounting the component
        return () => {
            if (fetchItemsRef.current) {
                clearTimeout(fetchItemsRef.current);
            }
        };
    }, [currentPage,itemsPerPage, params, currentSortValues, filter ]);

    useEffect(() => {
        return () => {
            hasRestoredUrlRef.current = false;
            isRestoringRef.current = false;
        };
    }, [location.pathname]);

    const handleFilterChange = (newFilters) => {
        if (newFilters) {
            setFilter(`searchCriteria=${newFilters.searchCriteria}`);
        }
    };

    //Check the language to update the sortableList headers
    const [language, setLanguage] = useState(LoadLocalStorage('selectedLanguage'));
    useEffect(() => {
        const checkLanguageChange = () => {

            const currentLanguage = LoadLocalStorage("selectedLanguage");
            if (currentLanguage !== language) {
                setLanguage(currentLanguage);
            }
        };
        const intervalId = setInterval(checkLanguageChange, 600);

        return () => {
            clearInterval(intervalId);
        };
    }, [language]);


    // Generate columns from modelAttributes if provided and from columns if not and if neither is provided create the columns based on known items in the table
    useEffect(() => {
            //get model from listOptions
            const model = listOptions.model;
            //initiate the generatedColumns variable
            let generatedColumns = [];
            const getColumnsFromItems = () => {
                const uniqueKeys = extractKeysFromItems();
                generatedColumns = uniqueKeys.map(key => ({
                    field: key,
                    label: key,
                    value: (item) => item[key] || "not set",
                }));
            }
            //check if model exists and has a getAttributes function
            if (model && typeof model.getAttributes === 'function') {
                //get the attributes from the model
                const modelAttributes = model.getAttributes();
                //if the columns are provided in the listOptions use them
                if (listOptions.columns && Array.isArray(listOptions.columns)) {
                    generatedColumns = listOptions.columns.map(column => {
                        // If it's a string, use it as the field and set label to the value in modelAttributes
                        if (typeof column === 'string') {
                            const label = modelAttributes[column]?.label || column;
                            return {
                                field: column,
                                label,
                                ...modelAttributes[column],
                            };
                            //if it is an object use it as the column configuration
                        } else if (typeof column === 'object') {
                            return column;
                        }
                        return null;
                    }).filter(column => column !== null); // Remove null values if any
                } else {
                    // Generate columns from modelAttributes directly
                    generatedColumns = Object.keys(modelAttributes).map(key => ({
                        field: key,
                        ...modelAttributes[key]
                    }));
                }
            } else {
                //if the model wasn't passed, but we have columns in listOptions use them
                if (listOptions.columns && Array.isArray(listOptions.columns)) {
                    const anyStrings = listOptions.columns.some(column => typeof column === 'string');
                    if (anyStrings) {
                        console.warn("Warning: listOptions.columns contains strings but no model was provided.");
                        getColumnsFromItems();
                    } else {
                        generatedColumns = listOptions.columns;
                    }
                } else {
                    getColumnsFromItems();
                }
            }
            
            // Store all columns (for filters)
            setAllColumns(generatedColumns);
            
            // Filter out columns with showInTable: false (for table display only)
            const visibleColumns = generatedColumns.filter(column => column.showInTable !== false);
            
            //set the columns state
            setColumns(visibleColumns);

    }, [/*listOptions.model,*//*listOptions.model.getAttributes(),*/ language/*fetchItems*/]); //Only run this effect when the model or fetchDataFunction changes
    //If you put fetchitems in the dependencies, it updates the translations of the table headers but in some tables it creates an infinite loop, that's why it is committed

    // Function to extract keys from items if params are not provided
    const extractKeysFromItems = () => {
        const uniqueKeys = new Set();
        if (items && items.length > 0) {
            items.forEach(item => {
                Object.keys(item).forEach(key => uniqueKeys.add(key));
            });
        }
        return Array.from(uniqueKeys);
    };

    const [error, setError] = useState(false);

    // Fetch items from API
    /*useEffect(() => {
       // if (counter <= 1) {
        if (apiResponseList !== null) {
            console.log('entra en el counter')
            const getData = async () => {
                await fetchDataFunction(params, currentPage, itemsPerPage)
                    .then(response => {
                        if (response) {
                            //setFlag(true);
                            setItems(response.items);
                            setTotalPages(response.pagination?.totalPages);
                            setTotalItem(response.pagination?.total);
                            setError(false);
                            setLoading(false);
                        } else {
                            setError(true);
                            setLoading(false);
                        }
                    }).catch((error) => {
                        console.error('Error fetching data:', error);
                        setError(true);
                    })

                // setCounter(counter + 1);
            }
            getData();
        }

      // }
    }, [/!*currentSortValues, params, currentPage, itemsPerPage, fetchDataFunction, counter*!/ apiResponseList]); // Only run this effect when params, currentPage, itemsPerPage or fetchDataFunction changes*/

    const [showErrorMessage, setShowErrorMessage] = useState(false);
    useEffect(() => {
        if (error === true && loading === false && items?.length === 0) {
            setShowErrorMessage(true);
        } else {
            setShowErrorMessage(false);
        }
    }, [error, loading]);

    // Function to handle sorting when a column header is clicked
    const handleSortChange = (sortField, sort) => {
        // Check if the sortField is among the sortable Fields
        const isSortableField = columns.some(column =>
            (column.sortField || column.field) === sortField && column.sortable !== false
        );
        if (isSortableField) {
            if (type === "model") {
                setParams({...params, sortField, sort});
            }
            else {
                if (Object.keys(currentSortValues).length !== 0) {
                    if (currentSortValues.sortField === sortField && currentSortValues.sort === sort) {
                        sort = sort === 'asc' ? 'desc' : 'asc';
                    }
                }
                setParams(`sortField=${sortField}&sort=${sort}`);
                if (search !== null) {
                    const matchesSort = search.match(/sort=([^&]*)/);
                    const matchesField = search.match(/sortField=([^&]*)/);
                    if (matchesSort !== null && matchesSort[1] !== '' && matchesField !== null && matchesField[1] !== '') {
                        if (matchesField[1] !== sortField) {
                            //REPLACES SORTFIELD WHEN ITS VALUE IS DIFFERENT
                            SaveLocalStorage('search', search.replace(/(sortField=[^&]*)(?:&sort=[^&]*)?/, 'sortField=' + sortField + '&sort=' + sort));
                        } else {
                            //KEEP THE SORTFIELD VALUE AND ONLY CHANGE THE SORT VALUE
                            SaveLocalStorage('search', search.replace(/sort=[^&]*/, 'sort=' + (matchesSort[1] === 'asc' ? 'desc' : 'asc')));
                        }
                    } else {
                        //ADD SORTFIELD AND SORT, TO THE VALUE THAT ALREADY EXISTS IN LOCAL STORAGE
                        SaveLocalStorage('search', search + `&sortField=${sortField}&sort=${sort}`);
                    }
                } else {
                    //SAVE SORTFIELD AND SORT IF IT DOES NOT EXIST
                    SaveLocalStorage('search', `sortField=${sortField}&sort=${sort}`);
                }
                setCurrentSortValues({sortField, sort});
            }
        }
        setCounter(0);
    };

    // Function to toggle between table and grid view
    const toggleViewMode = () => {
        if (listOptions.layoutOptions && listOptions.layoutOptions.grid && canChangeLayout) {
            setViewMode(viewMode === 'table' ? 'grid' : 'table');
        }
    };

    // Function to render the filter UI
    const renderFilterUI = () => {
        if (!handleFilterChange) {
            // Log a warning if renderFilter or listOptions are provided without handleFilterChange
            if (listOptions.layoutOptions && listOptions.layoutOptions.filter && listOptions.layoutOptions.filter.renderFilter) {
                console.warn("Warning: renderFilter or listOptions provided without handleFilterChange.");
            }
            return null;
        }
        // If a custom filter was provided, use it
        if (listOptions.layoutOptions && listOptions.layoutOptions.filter && listOptions.layoutOptions.filter.renderFilter) {
            return listOptions.layoutOptions.filter.renderFilter(handleFilterChange);
        }
        // If listOptions were provided and no custom filter, use the generic filter UI
        else if (listOptions && columns) {
            return generateGenericFilterUI();
        }
        return null;
    };
    const [inputValue, setInputValue] = useState('');

    const debounce = (func) => {
        let inDebounce;
        return function() {
            const context = this;
            const args = arguments;
            clearTimeout(inDebounce);
            inDebounce = setTimeout(() => func.apply(context, args), debounceTime);
        };
    };
    const [updateFilter, setUpdateFilter] = useState([]);

    const handleInputChangeSearch = (value) => {
        const updatedFilters = { ...params, searchCriteria: value };
        if (value !== '') {
            if (search !== null) {
                const matchesCriteria = search.match(/searchCriteria=([^&]*)/);
                if (matchesCriteria !== null && matchesCriteria[1] !== '') {
                    //WHEN CRITERIA EXISTS
                    SaveLocalStorage('search', search.replace(/searchCriteria=[^&]*/, 'searchCriteria=' + value));
                } else {
                    //WHEN IT DOES NOT EXIST, IT IS ADDED TO THE VALUE THAT ALREADY IS
                    SaveLocalStorage('search', search + (search && search.charAt(search.length -1) !== "&" ? '&' : '') + `searchCriteria=${value}`);
                }
            } else {
                //REMOVE CRITERIA VALUE FROM LOCALSTORAGE
                SaveLocalStorage('search', /*(search && search.charAt(search.length -1) !== "&" ? '&' : '') + */`searchCriteria=${value}`);
            }
        } else {
            //ELIMINATE LOCALSTORAGE CRITERIA
           let finalSearch = '';
            if(search) {
                 finalSearch = search.replace(/searchCriteria=[^&]*/, '');
            }

            if (finalSearch.charAt(0) === "&") {
                finalSearch = finalSearch.substring(1);
            }
            if (finalSearch.charAt(finalSearch.length -1) === "&") {
                finalSearch = finalSearch.slice(0, -1);
            }
            SaveLocalStorage('search', finalSearch);// (/(&?)searchCriteria=[^&]*/, '$1')  (/searchCriteria=[^&]*/, '')
        }
        handleFilterChange(updatedFilters);
        setUpdateFilter(value)
        setCurrentPage(1);
        setCounter(0);
    };

    const debouncedHandleInputChangeSearch = useCallback(
        debounce(handleInputChangeSearch, 500),
        []
    );

    const handleChange = (e) => {
        setInputValue(e.target.value);
        debouncedHandleInputChangeSearch(e.target.value);
    };

    const deleteSearch = () => {
        setUpdateFilter([]);
        const searchCriteria = document.getElementById('searchCriteria');
        if (searchCriteria) {
           // fireEvent.change(searchCriteria, {target: {value: ''}});
            searchCriteria.value = '';
            handleInputChangeSearch('');
            if (Object.keys(params).length !== 0 || (typeof params === "string" && params !== "")) {
                const matchesCriteria = params.match(/searchCriteria=([^&]*)/);
                if (matchesCriteria) {
                    let finalParams = params.replace(/searchCriteria=([^&]*)/, '');
                    if (finalParams.charAt(0) === "&") {
                        finalParams = finalParams.substring(1);
                    }
                    setParams(finalParams);
                }
            }
        }
    }

    useEffect(() => {
        if (search === '') {
            localStorage.removeItem('search');
        }
    }, [search]);

    const searchCriteriaInput = document.getElementById('searchCriteria');

    useEffect(() => {
        search = LoadLocalStorage("search");
        if (search !== null /*&& search !== ''*/) {
            const matchesCriteria = search.match(/searchCriteria=([^&]*)/);
            if (matchesCriteria && searchCriteriaInput) {
                // EVENT IS ONLY FOR THE TEST MODE
                //fireEvent.change(searchCriteriaInput, {target: {value: matchesCriteria[1]}});
                searchCriteriaInput.value = matchesCriteria[1];
                handleInputChangeSearch(matchesCriteria[1]);
            }
            const params = new URLSearchParams(search);
            let newValues = {};
            const newDateValues = {};
            const restoredMultiValues = {};
            setTempFilters({});
            for (let [key, value] of params.entries()) {
                if (key.includes('amount')) {
                    key = 'amount';
                    const mini = params.get('min' + key) || '';
                    const maxi = params.get('max' + key) || '';
                    newValues[key] = { min:  mini, max: maxi};
                    setAmountRanges(newValues);
                    setTempFilters(newValues);
                    continue;
                }
                if (key.includes('Date')) {
                    let fieldName = key.replace(/(fromDate|toDate)/, '');
                    let fromKey = `${fieldName}fromDate`;
                    let toKey = `${fieldName}toDate`;
                    let fromValue = params.get(fromKey);
                    let toValue = params.get(toKey);
                    setDateValues((prevState) => ({
                        ...prevState,
                        [fieldName]: {
                            startDate: fromValue ? new Date(fromValue) : null,
                            endDate: toValue ? new Date(toValue) : null,
                        },
                    }));
            
                    continue;
                }
                if (['costPlace', 'costBearer', 'model', 'groupBy'].includes(key)) {
                    const rawValues = params.getAll(key);
                    
                    // If there are no values, continue
                    if (rawValues.length === 0) {
                        continue;
                    }

                    // If there is only one value, treat it as a simple string
                    if (rawValues.length === 1) {
                        const singleValue = rawValues[0];

                        // Check if it's a valid value
                        if (typeof singleValue === 'string' && !singleValue.includes('[object Object]')) {
                            let label = '';
                            
                            if (key === 'model') {
                                switch (singleValue) {
                                    case 'Förrättning':
                                        label = i18n.t('reimbursement.reportProcessing');
                                        break;
                                    case 'Arvode':
                                        label = i18n.t('honorarium.honorarium');
                                        break;
                                    case 'Betala':
                                        label = i18n.t('payReceive.pay');
                                        break;
                                    case 'Intern transaktion':
                                        label = i18n.t('internalTransaction.internalTransaction');
                                        break;
                                    case 'Faktura':
                                        label = i18n.t('invoice.invoiceEconomicReport');
                                        break;
                                    default:
                                        label = singleValue;
                                        break;
                                }
                            } else {
                                label = singleValue;
                            }
                            
                            newValues[key] = singleValue;
                            setNormalValues((prevState) => ({
                                ...prevState,
                                [key]: singleValue,
                            }));
                        }
                        continue;
                    }

                    // Multiple values - process as array
                    const validValues = rawValues.filter(
                        val => typeof val === 'string' && !val.includes('[object Object]')
                    );
                    
                    const uniqueValues = [...new Set(validValues)];
    
                        const valuesArray = uniqueValues.map(val => {
                            let label = '';
                            
                            if (key === 'model') {
                                switch (val) {
                                    case 'Förrättning':
                                        label = i18n.t('reimbursement.reportProcessing');
                                        break;
                                    case 'Arvode':
                                        label = i18n.t('honorarium.honorarium');
                                        break;
                                    case 'Betala':
                                        label = i18n.t('payReceive.pay');
                                        break;
                                    case 'Intern transaktion':
                                        label = i18n.t('internalTransaction.internalTransaction');
                                        break;
                                    case 'Faktura':
                                        label = i18n.t('invoice.invoiceEconomicReport');
                                        break;
                                    default:
                                        label = val;
                                        break;
                                }
                            } else {
                                label = val;
                            }

                        return { value: val, label };
                    });

                    restoredMultiValues[key] = valuesArray;

                    setSelectedValues((prevState) => ({
                        ...prevState,
                        [key]: valuesArray,
                    }));

                    newValues[key] = valuesArray;
                    setTempFilters(newValues);
                    continue;
                }
                newValues[key] = value;
            }
            setNormalValues(newValues);
            setTempFilters(newValues);
        }
    }, [searchCriteriaInput, setTempFilters]);

    // Function to generate a generic filter UI based on the columns
    const generateGenericFilterUI = () => {
        return (
            <>
                    <Row>
                        {allowToggleFilterVisibility && (
                            <span className="float-end w-auto">
                                <button className="border-0 bg-transparent" onClick={() => setShowGenericFilterInputs(!showGenericFilterInputs)}>
                                    <i className="fas fa-filter i-color-grow-brown"></i> <i className={`i-color-grow-brown ${showGenericFilterInputs ? "fas fa-sort-up":"fas fa-sort-down"}`}></i>
                                </button>
                            </span>
                        )}
                    </Row>
                    {showGenericFilterInputs && (
                        <Row>
                            {/* MODEL SEARCH */}
                            {type === "model" ? columns.map(column => {
                                let filterInput = createFilterInput(column);
                                if (filterInput) {
                                    return (
                                        <Col key={column.field} md={3} className="mt-3 sortable-filter-item">
                                            {filterInput}
                                        </Col>
                                    );
                                }
                                return null;
                            }) :
                            <Col md="5" className="mt-3 p-1 input-group-text pr-page-color input-focus-effect ms-1 ms-lg-3" tabIndex="0"> {/******************************/}
                                <i className="bx bx-search" onClick={()=> {$("#searchCriteria").focus()}}></i>
                                <input
                                    id="searchCriteria"
                                    key="searchCriteria"
                                    type="text"
                                    className="border-0 py-0 pr-page-color box-shadow-none w-100"
                                    placeholder={t('sortableList.search')}
                                    onChange={(e)=>{
                                        handleChange(e);
                                        handleFilterChange();
                                    }}
                                />
                                {searchCriteriaInput && searchCriteriaInput.value !== '' ?
                                    <i className='bx bx-x' onClick={deleteSearch}/>
                                    :''}
                            </Col>
                            }
                        </Row>
                    )}
            </>
        );
    };

    const handleDateChange = (dates, field) => {
        const [start, end] = dates;
    
        const startDateSelection = start ? new Date(start) : null;
        if (startDateSelection) {
            startDateSelection.setHours(0, 0, 0, 0);
        }

        let endDateSelection = end ? new Date(end) : null;
        if (endDateSelection) {
            endDateSelection.setHours(23, 59, 59, 0);
        }

        setDateValues((prevState) => ({
            ...prevState,
            [field]: {
                startDate: startDateSelection,
                endDate: endDateSelection,
            },
        }));
    
        if (end) {
            setShowDatePickerMap((prevState) => ({
                ...prevState,
                [field]: false,
            }));
            setButtonClearIsDisable(false);
            setButtonSearchIsDisable(false);
        }
    };

    const  handleSearch = async (click = null, fromOrg = false)  => {
                
        const filteredParams = Object.entries(tempFilters)
            .filter(([key, value]) => {
                return (
                    key.trim() !== "" &&
                    value !== "" &&
                    value !== null &&
                    value !== undefined &&
                    (typeof value === "string" || typeof value === "number" || typeof value === "boolean")
                );
            })
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`);

        let queryString = filteredParams.length > 0 ? `${filteredParams.join("&")}&` : "";
        let exportParams = "";
        let groupByParams = "";
        let selectedMultiParams = "";
        let amountQuery = '';

        Object.entries(dateValues).forEach(([field, { startDate, endDate }]) => {
            if (startDate && endDate) {
                exportParams += `${field}fromDate=${ChangeDateFormat('dateTime', startDate)}&${field}toDate=${ChangeDateFormat('dateTime', endDate)}&`;
            }
        });

        const isProvided = (val) => val !== undefined && val !== null && val !== '';
        Object.entries(amountRanges).forEach(([field, { min, max }]) => {
            if (isProvided(min) || isProvided(max)) {
                const minValue = isProvided(min) ? min : -9000000;
                const maxValue = isProvided(max) ? max : 9000000;
                // Solo añadir a la URL si son valores reales (no los defaults)
                if (isProvided(min)) {
                    amountQuery += `min${field}=${minValue}&`;
                }
                if (isProvided(max)) {
                    amountQuery += `max${field}=${maxValue}&`;
                }
            }
        });

        if(groupBy !== '') {
            groupByParams= `groupby=${groupBy}&`;
        }

        if (Object.keys(selectedValues).length > 0) {     
            const selectedValuesQuery = Object.entries(selectedValues)
                .flatMap(([key, values]) => values.map(value => `${encodeURIComponent(key)}=${encodeURIComponent(value.value)}`))
                .join('&');
        
            selectedMultiParams = selectedValuesQuery + "&";
        }
        

        const finalQueryString = queryString + amountQuery + exportParams + groupByParams + selectedMultiParams;
        const cleanedQueryString = finalQueryString.replace(/^&|&$/g, '').replace(/[^&=]+=(?=&|$)/g, '');

        if(click !== null){  
            //if (window.location.pathname === '/admin/economic-report' || window.location.pathname === '/organization-economic-report') {
                localStorage.removeItem('fetchSearchResults');
                setSearchPressed(true);
            //} else {
            //    setSearchPressed(false);
            //}
            setCurrentPage(1);
            SaveLocalStorage('currentPage', 1);
            setFilter(cleanedQueryString);
            setUpdateFilter(cleanedQueryString);
            setEconomicFilterLoading(true); 
            setIgnoreFieldsEconomicReportToExport('');

            const currentSearchParams = new URLSearchParams(window.location.search);
            const currentId = currentSearchParams.get('id');
            
            let finalUrl = cleanedQueryString ? `${window.location.pathname}?${cleanedQueryString}` : window.location.pathname;
            
            const preserveIdView = window.location.pathname.includes('/admin/user-list/user') 
                || window.location.pathname.includes('/admin/organization-list/organization')
                || window.location.pathname.includes('/admin/digital-account-list/digital-account')
                || window.location.pathname.includes('/admin/report-tax-info')
                || window.location.pathname.includes('/bank-transfer-list/bank-transfer-history-list')
                || window.location.pathname.includes('/qr-code-list');
            
            if (preserveIdView && currentId) {
                const separator = cleanedQueryString ? '&' : '?';
                finalUrl = cleanedQueryString 
                    ? `${window.location.pathname}?${cleanedQueryString}&id=${encodeURIComponent(currentId)}`
                    : `${window.location.pathname}?id=${encodeURIComponent(currentId)}`;
                // Keep the open tab of the page (admin user page), so going back lands on it
                const currentTab = currentSearchParams.get('tab');
                if (currentTab) {
                    finalUrl += `&tab=${encodeURIComponent(currentTab)}`;
                }
            }
            
            const currentQuery = window.location.search.replace(/^\?/, '');
            const newQuery = new URLSearchParams(finalUrl.split('?')[1] || '').toString();

            // persistFilters lists keep their filters out of the URL and the browser history
            if (currentQuery !== newQuery && !persistFilters) {
                const stateObj = {
                    filters: cleanedQueryString || null,
                    timestamp: Date.now(),
                    // Save all current filter states for restoration
                    filterStates: {
                        tempFilters: tempFilters,
                        selectedValues: selectedValues,
                        dateValues: dateValues,
                        amountRanges: amountRanges,
                        normalValues: normalValues,
                        groupBy: groupBy
                    }
                };

                if (stateObj.filters !== null || currentId) {
                    window.history.pushState(stateObj, '', finalUrl);
                }
            }
        } else {
            const storageKey = getStorageKeyForPath(window.location.pathname, viewName);
            if (!storageKey) {
                setSearchPressed(false);
            }
        }

        if (fromOrg) {
            setSearchPressed(false);
            const localStorageKey = getStorageKeyForPath(window.location.pathname, viewName);
            if (localStorageKey) {
                localStorage.removeItem(localStorageKey);
            }
        }

        return;      
    };

    const isOnlyPaginationOrSort = (qs) => {
        if (!qs) return true; 
        const params = new URLSearchParams(qs);
        const ignored = new Set(['page','perPage','sortField','sort','sortBy','fromSearchButton']);
        if (isInfoView()) {
            ignored.add('id');
        }
        for (const key of params.keys()) {
            if (!ignored.has(key)) return false; 
        }
        return true;
    };

    const updateFiltersViewWhenBack = () => {
        const qs = window.location.search.replace(/^\?/, '');

            if (!isOnlyPaginationOrSort(qs) && qs) {
                const params = new URLSearchParams(qs);
                const filters = {};
                const multiValues = {};
                const dateRanges = {};
                const amountFilters = {};
                let newGroupBy = '';

                for (let [key, value] of params.entries()) {
                    if (['page', 'perPage', 'sortField', 'sort', 'id', 'fromSearchButton', 'sortBy'].includes(key)) {
                        continue;
                    }

                    if (['costPlace', 'costBearer', 'model'].includes(key)) {
                        if (!multiValues[key]) multiValues[key] = [];
                        let label = value;
                        if (key === 'model') {
                            switch (value) {
                                case 'Förrättning':
                                    label = i18n.t('reimbursement.reportProcessing');
                                    break;
                                case 'Arvode':
                                    label = i18n.t('honorarium.honorarium');
                                    break;
                                case 'Betala':
                                    label = i18n.t('payReceive.pay');
                                    break;
                                case 'Intern transaktion':
                                    label = i18n.t('internalTransaction.internalTransaction');
                                    break;
                                case 'Faktura':
                                    label = i18n.t('invoice.invoiceEconomicReport');
                                    break;
                                default:
                                    label = value;
                            }
                        }
                        multiValues[key].push({ value, label });
                    }
                    else if (key.includes('fromDate') || key.includes('toDate')) {
                        const fieldName = key.replace(/(fromDate|toDate)/, '');
                        if (!dateRanges[fieldName]) dateRanges[fieldName] = {};
                        if (key.includes('fromDate')) dateRanges[fieldName].startDate = new Date(value);
                        else dateRanges[fieldName].endDate = new Date(value);
                    }
                    else if (key.startsWith('min') || key.startsWith('max')) {
                        const fieldName = key.replace(/^(min|max)/, '');
                        if (!amountFilters[fieldName]) amountFilters[fieldName] = {};
                        if (key.startsWith('min')) amountFilters[fieldName].min = value;
                        else amountFilters[fieldName].max = value;
                    }
                    else if (key === 'groupby') {
                        newGroupBy = value;
                    } else {
                        filters[key] = value;
                    }
                }

                // Reset all filters to empty before applying URL filters
                const clearedFilters = columns.reduce((acc, column) => {
                    acc[column.field] = '';
                    return acc;
                }, {});

                // Apply the extracted filters from the URL to the states
                setTempFilters({ ...clearedFilters, ...filters });
                setSelectedValues(multiValues);
                setDateValues(dateRanges);
                setAmountRanges(amountFilters);
                setNormalValues({ ...clearedFilters, ...filters });
                setGroupBy(newGroupBy);

                setFilter(qs);
                setUpdateFilter(qs);
                setSearchPressed(true);
                setButtonClearIsDisable(false);
                setButtonSearchIsDisable(false);
            } else {
                // Only pagination or sort params -> clear all filters
                clearFilters();
                setFilter('');
                setUpdateFilter('');
                setSearchPressed(false);
                setButtonClearIsDisable(true);
                setButtonSearchIsDisable(true);
            }
    };

    useEffect(() => {
        const onPopState = (event) => {
            const state = event.state;

            // persistFilters lists never put their filters in the history, so an entry never holds theirs
            // (it can hold the filters of another list of the same page)
            if (persistFilters) {
                return;
            }

            // If we have filter states saved, restore them
            if (state && state.filterStates) {
                const { filterStates } = state;

                // Restore all states
                if (filterStates.tempFilters) setTempFilters(filterStates.tempFilters);
                if (filterStates.selectedValues) setSelectedValues(filterStates.selectedValues);
                if (filterStates.dateValues) setDateValues(filterStates.dateValues);
                if (filterStates.amountRanges) setAmountRanges(filterStates.amountRanges);
                if (filterStates.normalValues) setNormalValues(filterStates.normalValues);
                if (filterStates.groupBy !== undefined) setGroupBy(filterStates.groupBy);

                // Extract query string from state
                const qs = state.filters || '';
                if (isOnlyPaginationOrSort(qs)) {
                    // If there are no real filters -> clear everything
                    clearFilters();
                    setFilter('');
                    setUpdateFilter('');
                    setSearchPressed(false);
                    setButtonClearIsDisable(true);
                    setButtonSearchIsDisable(true);
                } else {
                    setFilter(qs);
                    setUpdateFilter(qs);
                    setSearchPressed(!!qs);
                    if (qs) {
                        setButtonClearIsDisable(false);
                        setButtonSearchIsDisable(false);
                    }
                }
            } else {
                updateFiltersViewWhenBack();
            }
        };

        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    useEffect(() => {
        const storageKey = getStorageKeyForPath(window.location.pathname, viewName);
        const tempSearch = LoadLocalStorage(storageKey);
        if (tempSearch) {
            return;
        }
        const qs = window.location.search.replace(/^\?/, '');
        if (qs && !isOnlyPaginationOrSort(qs)) {
            updateFiltersViewWhenBack();
        } else {
            // Solo limpiar filtros si no hay parámetros de paginación en la URL
            if (!qs) {
                clearFilters();
            }
            setFilter('');
            setUpdateFilter('');
            setSearchPressed(false);
        }
    }, []);

    // persistFilters: put back the filters this list had before opening an item. It runs after the effects
    // that read the URL, so it replaces whatever they took from it (the URL can hold another tab's filters)
    useEffect(() => {
        if (!persistFilters) return;
        let saved = null;
        try {
            saved = JSON.parse(sessionStorage.getItem(persistFiltersKey));
        } catch (e) {
            saved = null;
        }
        const restoredDates = {};
        Object.entries(saved?.dateValues ?? {}).forEach(([field, range]) => {
            restoredDates[field] = {
                startDate: range?.startDate ? new Date(range.startDate) : null,
                endDate: range?.endDate ? new Date(range.endDate) : null,
            };
        });
        setTempFilters(saved?.tempFilters ?? {});
        setNormalValues(saved?.normalValues ?? {});
        setSelectedValues(saved?.selectedValues ?? {});
        setDateValues(restoredDates);
        setAmountRanges(saved?.amountRanges ?? {});
        setAmountValues(saved?.amountValues ?? {});
        if (saved?.currentSortValues) setCurrentSortValues(saved.currentSortValues);
        setButtonClearIsDisable(!saved?.hasFilters);
        // Open the filter form, so the active filters can be seen
        if (saved?.hasFilters) setLocalDisplay(true);
        // Fetch again with the restored filters (the first fetch was set up with the empty ones)
        setFilter('persistedFilters');
    }, []);

    const clearFilters = () => {
        localStorage.removeItem('search');
        setIgnoreFieldsEconomicReportToExport(''); 
        setMinValue('');
        setMaxValue('');
        setAmountValue('');
        const clearedValues = columns.reduce((acc, column) => {
            acc[column.field] = '';
            return acc;
        }, {});
        const initialState = { min: { value: '', name: '' }, max: { value: '', name: '' } };
        setAmountValues(clearedValues);
        setAmountRanges(clearedValues);
        setRangeValues(initialState);
        setNormalValues(clearedValues); 

        const preserveIdView = window.location.pathname.includes('/admin/user-list/user') 
            || window.location.pathname.includes('/admin/organization-list/organization')
            || window.location.pathname.includes('/admin/digital-account-list/digital-account')
            || window.location.pathname.includes('/admin/report-tax-info')
            || window.location.pathname.includes('/bank-transfer-list/bank-transfer-history-list')
            || window.location.pathname.includes('/qr-code-list');
        
        const currentSearchParams = new URLSearchParams(window.location.search);
        const currentId = currentSearchParams.get('id');

        setTempFilters(''); 
        if (!urlGetSearch.includes("export-data/admin/search-economic-report") && !urlGetSearch.includes("export-data/admin/search-organization-economic-report")) {
            if (preserveIdView && currentId) {
                setTempFilters({ orgName: '', ...clearedValues, id: currentId });
            } else {
                setTempFilters({ orgName: '', ...clearedValues });
            }
        } else {
            setTempFilters('');
        }

        setButtonClearIsDisable(true);
        setButtonSearchIsDisable(true); 
        if (window.location.pathname === '/organization-economic-report' || window.location.pathname === '/admin/economic-report') {
            setButtonSearchIsDisable(false);
        }
        handleSearch("click", true);
        handleInputChangeSearch('');
        setUpdateFilter([]);

        setDateValues({}); 
        setGroupBy('');
        setClick(false);
        setSelectedField('');
        setSelectedValues([]);
        
        localStorage.removeItem('search');

        const path = window.location.pathname;

        // persistFilters lists have nothing of theirs in the URL to clean
        if (preserveIdView && currentId && !persistFilters) {
            const currentTab = currentSearchParams.get('tab');
            window.history.replaceState({}, document.title, `${path}?id=${encodeURIComponent(currentId)}${currentTab ? `&tab=${encodeURIComponent(currentTab)}` : ''}`);
        } else if (!window.location.search) {
            window.history.replaceState({}, document.title, path);
        }
    };

    const handleClearAmountValue = (field) => {
        setAmountValue('');
        setAmountValues((prev) => ({
            ...prev,
            [field]: '',
          }));
        setButtonSearchIsDisable(false);
      };
    
      const handleClearMin = (field) => {
        setAmountRanges((prev) => ({
            ...prev,
            [field]: {
              ...prev[field],
              min: '',
            },
          }));
        setButtonSearchIsDisable(false);
      };
    
      const handleClearMax = (field) => {
        setAmountRanges((prev) => ({
            ...prev,
            [field]: {
              ...prev[field],
              max: '',
            },
          }));
        setButtonSearchIsDisable(false);
      };

    const handleGroupByDropdownChange = (selectedOption) => {
        if(!selectedOption?.value) {         
            setGroupBy(selectedOption ? selectedOption.value : '');
            setButtonSearchIsDisable(false);
            return;
        }
        setGroupBy(selectedOption ? selectedOption.value : '');
        setButtonSearchIsDisable(false);
        setButtonClearIsDisable(false);
    };

    let defaultSortOptions = [
        { value: "CreatedAt", label: "Gruppera per dag" },
        { value: "CostPlace", label: "KST" },
        { value: "CostBearerName", label: "Aktivitetsområde" },
        { value: "Model", label: "Transaktionstyp" },
        { value: "CreatedByName", label: "Person" },
        { value: "OrgName", label: "Organisation" }
    ];

    if (window.location.pathname === "/organization-economic-report") {
        defaultSortOptions = defaultSortOptions.filter(opt => 
            opt.value !== "CostPlace" && opt.value !== "OrgName"
        );
    }
    const generateFormFilterUI = () => {
        const reimbursementHeaderBtnClass = classNames(
            'reimbursement-mobile-filter-toggle py-2 pe-3 ps-3 accordion-button d-flex align-items-center fw-bold bg-white text-green rounded',
            {
                collapsed: !localDisplay,
                'border-0': !localDisplay,
                'border-2 border-green border-bottom-0 rounded-bottom-0': localDisplay,
            }
        );

        const reimbursementActionControlsClass = isReimbursementMobileFiltersMode
            ? `d-flex align-items-center gap-2 reimbursement-mobile-filter-actions me-1 ${exportIsAvailable ? 'reimbursement-mobile-filter-actions--with-export' : 'reimbursement-mobile-filter-actions--no-export'}`
            : 'd-flex align-items-center gap-2 reimbursement-mobile-filter-actions reimbursement-desktop-filter-actions me-1';

        const reimbursementAccordionClassSuffix = isReimbursementMobileFiltersMode
            ? (exportIsAvailable ? 'reimbursement-mobile-filter-accordion--with-export' : 'reimbursement-mobile-filter-accordion--no-export')
            : (isReimbursementDesktopFiltersMode
                ? (exportIsAvailable ? 'reimbursement-desktop-filter-accordion--with-export reimbursement-desktop-filter-accordion' : 'reimbursement-desktop-filter-accordion--no-export reimbursement-desktop-filter-accordion')
                : '');

        return (
            <>
            {isReimbursementFiltersDesignMode && (
                <>
                    <div className="d-flex align-items-center justify-content-between gap-2 w-100">
                        <button
                            className={`${reimbursementHeaderBtnClass} reimbursement-mobile-filter-label-btn ${isReimbursementDesktopFiltersMode ? 'reimbursement-desktop-filter-label-btn' : ''}`.trim()}
                            type="button"
                        >
                            <span>Filter</span>
                        </button>

                        <div className={reimbursementActionControlsClass}>
                            <Button
                                type="button"
                                className="bg-green-soft soft-icon-bordered d-flex align-items-center justify-content-center reimbursement-filter-toggle-action-btn"
                                onClick={() => setLocalDisplay(!localDisplay)}
                                aria-label={t('sortableList.filter') || 'Filter'}
                            >
                                <i className="bx bx-filter fs-4" />
                            </Button>
                            {exportIsAvailable && (
                                <div>{isReimbursementMobileFiltersMode ? renderExportFileDropdown(true) : renderExportFileDropdown()}</div>
                            )}
                        </div>
                    </div>
                    {!localDisplay && isMobile && <hr className="mt-3 mb-1" />}
                </>
            )}

            <Accordion
                id="accordionBordered"
                className={isReimbursementFiltersDesignMode ? "" : "mt-3"}
                open={localDisplay ? 'filters' : ''}
                toggle={() => setLocalDisplay(localDisplay)}
            >
                <AccordionItem
                    className={isReimbursementFiltersDesignMode
                        ? (localDisplay
                            ? `reimbursement-mobile-filter-accordion accordion-grey bg-white border-2 border-green rounded rounded-top-0 is-open ${reimbursementAccordionClassSuffix}`.trim()
                            : 'reimbursement-mobile-filter-accordion accordion-grey bg-white border-0')
                        : `accordion-grey bg-green rounded ${!localDisplay ? 'border-0' : 'border-2 border-green'}`
                    }
                >
                    {!isReimbursementFiltersDesignMode && (
                        <h2 className="accordion-header" id="accordionborderedExample2">
                        <button
                            className={classNames(
                            `py-2 pe-1 w-100 accordion-button d-flex justify-content-between sortable-filter-toggle-btn ${
                                !localDisplay ? 'accordion-grey text-green' : 'bg-green text-white'
                            } fw-bold border-bottom-0`,
                            { collapsed: !localDisplay }
                            )}
                            type="button"
                            onKeyDown={() => setLocalDisplay(true)}
                            onClick={() => setLocalDisplay(!localDisplay)}
                        >
                            <div>Filter</div>
                            {!localDisplay ? (
                            <i className="bx bx-chevron-down fs-3" />
                            ) : (
                            <i className="bx bx-chevron-up text-white fs-3" />
                            )}
                        </button>
                        </h2>
                    )}
                    <Collapse
                        isOpen={localDisplay}
                        className="accordion-collapse reimbursement-mobile-filter-collapse"
                        id="accor_borderedExamplecollapse2"
                    >
                    <div className="accordion-body">
                        <Row>
                        <div className="filters-grid sortable-filters-grid reimbursement-filters-grid-main">
                            {allColumns
                            .filter(
                                (column) =>
                                column.showInFilter !== false && !['orgType'].includes(column.field)
                            )
                            .map((column) => (
                                <React.Fragment key={`${column.field}-filter-wrap`}>
                                    <div
                                    key={`${column.field}-filter`}
                                    className="filter-item sortable-filter-item reimbursement-filter-item"
                                    >
                                    {createFilterFormInput(column)}

                                    {economicReportFilter && column.field === 'model' && (
                                        <div className="d-flex align-items-center mt-2">
                                        <input
                                            type="checkbox"
                                            id="splitIntoSubtypes"
                                            checked={!!tempFilters.splitIntoSubtypes}
                                             onChange={(e) => {
                                                const checked = e.target.checked;

                                                setTempFilters((prev) => ({
                                                    ...prev,
                                                    splitIntoSubtypes: checked,
                                                }));

                                                //setIsSplitIntoSubtypesClicked(checked);

                                                setButtonClearIsDisable(false);
                                                setButtonSearchIsDisable(false);
                                            }}
                                            className="reimbursement-split-checkbox"
                                        />
                                        <label htmlFor="splitIntoSubtypes" className="mb-0">
                                            {i18n.t('economicReport.checkboxSubtypes')}
                                        </label>
                                        </div>
                                    )}
                                    </div>

                                </React.Fragment>
                            ))}

                            {isReimbursementDesktopFiltersMode && viewDropdownPerPage && !hideFilter && (
                                <div className="filter-item sortable-filter-item reimbursement-filter-item">
                                    <label className="ms-1 d-block">{capitalizeFirstLetter(t('sortableList.itemsPage'))}</label>
                                    {renderItemsPerPageDropdown()}
                                </div>
                            )}
                        </div>
                        </Row>

                        {economicReportFilter && (
                        <div className="filters-grid sortable-filters-grid mt-3 reimbursement-filters-grid-secondary">
                            <div>
                                <label className="ms-1">Summera per</label>
                                <Select
                                    classNamePrefix="sortable-filter-select"
                                    value={
                                    defaultSortOptions.find((opt) => opt.value === groupBy) || null
                                    }
                                    onChange={handleGroupByDropdownChange}
                                    options={defaultSortOptions}
                                    placeholder="Ej valt"
                                    isClearable={true}
                                    theme={(theme) => ({
                                    ...theme,
                                    borderRadius: 5,
                                    colors: {
                                        ...theme.colors,
                                        primary25: 'rgba(21, 117, 83, 0.25)',
                                        primary: '#67a58f',
                                        neutral0: 'var(--bs-body-bg)',
                                        neutral80: 'var(--bs-body-color)',
                                    },
                                    })}
                                />
                            </div>
                        </div>
                        )}

                        {isReimbursementFiltersDesignMode && !isReimbursementDesktopFiltersMode && viewDropdownPerPage && !hideFilter && (
                            <div className="mt-3">
                                <label className="ms-1 mb-1 d-block">{capitalizeFirstLetter(t('sortableList.itemsPage'))}</label>
                                {renderItemsPerPageDropdown()}
                            </div>
                        )}
                    </div>

                    <div className="d-flex justify-content-end mt-3 mb-4 reimbursement-filter-actions-footer">
                        <Button
                        disabled={buttonClearIsDisable}
                        className="btn-submit-kassan ms-3"
                        onClick={clearFilters}
                        >
                        Rensa filter <i className="bx bx-reset pb-custom align-middle size-icon-custom-button" />
                        </Button>

                        {isSpinnerLoading ? (
                        <Button
                            disabled={buttonSearchIsDisable}
                            className="btn-submit-kassan ms-3"
                            onClick={() => {
                            handleSearch('click');
                            }}
                        >
                            {t('sortableList.search')}{' '}
                            <i className="bx bx-search pb-custom align-middle size-icon-custom-button" />
                        </Button>
                        ) : (
                        <Button
                            className="btn-submit-kassan ms-3"
                            onClick={() => {
                            handleSearch('click');
                            }}
                        >
                            <Spinner animation="border" variant="success" size="sm" />
                        </Button>
                        )}
                    </div>
                    </Collapse>
                </AccordionItem>
            </Accordion>
            {isReimbursementFiltersDesignMode && localDisplay && isMobile && <hr className="mt-1 mb-0" />}
            </>
        );
    };
    
    const handleInputClick = (field) => {
        setShowDatePickerMap((prevState) => ({
            ...prevState,
            [field]: true
        }));
    };
    const datePickerWrapperRef = useRef(null);
    

    const handleClickOutside = (event, field) => {
        if (datePickerWrapperRef.current && !datePickerWrapperRef.current.contains(event.target)) {
            setShowDatePickerMap((prevState) => ({
                ...prevState,
                [field]: false
            }));
        }
    };

    useEffect(() => {
        
        const handleClickOutsideWrapper = (event) => {
            Object.keys(showDatePickerMap).forEach((field) => {
                handleClickOutside(event, field);
            });
        };

        document.addEventListener('mousedown', handleClickOutsideWrapper);

        return () => {
            document.removeEventListener('mousedown', handleClickOutsideWrapper);
        };
    }, [showDatePickerMap]);

    const handleDateButtonClick = (field) => {
        setDateValues((prevState) => ({
            ...prevState,
            [field]: {
                startDate: null,
                endDate: null,
            },
        }));
        setButtonSearchIsDisable(false);
    };

    useEffect(() => {
        handleSearch();
    }, [tempFilters, minValue, maxValue, groupBy]);

    useEffect(() => {
        const isUserList = window.location.pathname.includes('/admin/user-list') || window.location.pathname.includes('/admin/economic-report');
        
        if (isUserList && tempFilters.orgName && !tempFilters.orgLabel) {
            const getOrgLabelById = async (orgId) => {
                if (!orgId || !/^[0-9a-fA-F]{24}$/.test(orgId)) {
                    console.warn('Invalid organization ID:', orgId);
                    return;
                }

                const apiCallOptions = {
                    method: "get",
                    url: `/org/organization/${orgId}`,
                };
        
                try {
                    const response = await new Promise((resolve, reject) => {
                        ApiCall(apiCallOptions, resolve, reject);
                    });

                    const type = response.type?.trim();
                    const label = type ? `${response.name} - ${type}` : response.name;
                    
                    setTempFilters(prev => ({
                        ...prev,
                        orgLabel: label
                    }));
                    setNormalValues(prev => ({
                        ...prev,
                        orgLabel: label
                    }));
                } catch (error) {
                    console.error('Error fetching organization by ID:', error);
                }
            };
            
            getOrgLabelById(tempFilters.orgName);
        }
    }, [tempFilters.orgName, tempFilters.orgLabel]);

    
    // Cargar opciones de cost bearer al montar el componente
    useEffect(() => {
        const fetchCostBearers = async () => {
            try {
                const apiCallOptions = {
                    method: "get",
                    url: "/costbearer/list-all-kbar",
                };
                
                await ApiCall(apiCallOptions, (response) => {
                    if (response && Array.isArray(response)) {
                        // Mapear y ordenar las opciones
                        const formattedOptions = response
                            .sort((a, b) => {
                                const valueA = a.value || '';
                                const valueB = b.value || '';
                                return valueA.localeCompare(valueB);
                            })
                            .map(item => ({
                                value: item.id,
                                label: `${item.showLabel || item.name} (${item.value})`
                            }));
                        
                        setCostBearerOptions(formattedOptions);
                    }
                });
            } catch (error) {
                console.error('Error fetching cost bearers:', error);
            }
        };

        const currentPath = window.location.pathname;
        if (
            currentPath.includes("admin/economic-report") ||
            currentPath.includes("organization-economic-report")
        ) {
            fetchCostBearers();
        }
    }, []);

    const createFilterFormInput = (column) => {   
        const isDatePickerVisible = showDatePickerMap[column.field] || false; 
        const { startDate, endDate } = dateValues[column.field] || {
            startDate: null,
            endDate: null,
        };
        if (column.filter === false) {
            return null;
        }
        const handleInputChange = (e) => {        
            const { name, value } = e.target;
            
            let updatedFilters = { ...tempFilters, [name]: value };
            updatedFilters[column.field] = value;
            setNormalValues((prevState) => ({
                ...prevState,
                [name]: value,
            }));
            setTempFilters(updatedFilters);
            setButtonClearIsDisable(false);
            setButtonSearchIsDisable(false);
        };

        const handleSimpleInputChange = (selectedOption, actionMeta) => {        
            // const { name, value } = e ? e : { name: '', value: '' };
            // let updatedFilters = { ...tempFilters, [name]: value };
            // updatedFilters[column.field] = value;
            // if(name){
            //     setNormalValues((prevState) => ({
            //         ...prevState,
            //         [name]: value || '',
            //     }));
            // }
            // else{
            //     const clearedValues = columns.reduce((acc, column) => {
            //         acc[column.field] = '';
            //         return acc;
            //     }, {});
            //     setNormalValues(clearedValues);
            //     setTempFilters(updatedFilters);
            // }
           
            // setTempFilters(updatedFilters);
            // setButtonClearIsDisable(false);
            // setButtonSearchIsDisable(false);
            const name  = actionMeta.name;
            const value = selectedOption ? selectedOption.value : '';

            setNormalValues(prev => ({
                ...prev,
                [name]: value,
            }));

            setTempFilters(prev => ({
                ...prev,
                [name]: value,
            }));

            setButtonClearIsDisable(false);
            setButtonSearchIsDisable(false);
        };



        const handleSelectMultiChange = (selectedOptions, actionMeta) => { 
            const { name } = actionMeta;
            setSelectedValues(prevState => ({
                ...prevState,
                [name]: selectedOptions || []
            }));
            if (!selectedOptions || selectedOptions.length === 0) {
                setButtonSearchIsDisable(false);
                return;
            }
            
            setButtonSearchIsDisable(false);
            setButtonClearIsDisable(false);
        };
        
        const handleAmountInputChange = (e, field) => {
            const { name, value } = e.target;
            let updatedFilters = { ...tempFilters, [name]: value };
        
            updatedFilters[column.field] = value;
            
       
            setAmountValues((prev) => ({
              ...prev,
              [field]: value,
            }));
            setTempFilters(updatedFilters);  
            setButtonClearIsDisable(false);  
            setButtonSearchIsDisable(false);    
        };

        const handleMinAmountInputChange = (e, field) => {
            
            const { value } = e.target;
            setAmountRanges((prev) => ({
              ...prev,
              [field]: {
                ...prev[field],
                min: value,
              },
            }));
            setButtonClearIsDisable(false);  
            setButtonSearchIsDisable(false); 
        };

        const handleMaxAmountInputChange = (e, field) => {
            const { value } = e.target;
            setAmountRanges((prev) => ({
              ...prev,
              [field]: {
                ...prev[field],
                max: value,
              },
            }));
            setButtonClearIsDisable(false);  
            setButtonSearchIsDisable(false);       
        };
     
        const debouncedHandleInputChange = debounce(handleInputChange);

        if (column.type === "number") {
            return (
                <div>
                    <label className='ms-1'>{column.label || column.field}</label>
                    <div className="d-flex align-items-center gap-2 sortable-filter-number-row">
                        {!isRange[column.field] ? (
                            <>
                            {/* Input de mínimo */}
                            <div className="sortable-relative-flex">
                                <input
                                type="number"
                                name={`min${column.field}`}
                                className="form-control sortable-filter-control sortable-input-full"
                                placeholder="Min"
                                value={(amountRanges[column.field] && amountRanges[column.field].min) || ''}
                                onChange={(e) => handleMinAmountInputChange(e, column.field)}
                                />
                                {amountRanges[column.field]?.min && (
                                <button
                                    className="close-button sortable-clear-btn"
                                    onClick={() => handleClearMin(column.field)}
                                >
                                    <i className="bx bx-x"></i>
                                </button>
                                )}
                            </div>

                            <span>till</span>

                            {/* Input de máximo */}
                            <div className="sortable-relative-flex">
                                <input
                                type="number"
                                name={`max${column.field}`}
                                className="form-control sortable-filter-control sortable-input-full"
                                placeholder="Max"
                                value={(amountRanges[column.field] && amountRanges[column.field].max) || ''}
                                onChange={(e) => handleMaxAmountInputChange(e, column.field)}
                                />
                                {amountRanges[column.field]?.max && (
                                <button
                                    className="close-button sortable-clear-btn"
                                    onClick={() => handleClearMax(column.field)}
                                >
                                    <i className="bx bx-x"></i>
                                </button>
                                )}
                            </div>
                            </>
                        ) : (
                            // Input único
                            <div className="sortable-relative-flex">
                            <input
                                type="number"
                                className="form-control sortable-filter-control sortable-input-full sortable-input-with-clear-padding"
                                placeholder="Belopp"
                                value={amountValues[column.field] || ''} // Cada input usa su valor propio
                                onChange={(e) => handleAmountInputChange(e, column.field)}
                            />
                            {amountValue && (
                                <button
                                className="close-button sortable-clear-btn"
                                onClick={() => handleClearAmountValue(column.field)}
                                >
                                <i className="bx bx-x"></i>
                                </button>
                            )}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        if (window.location.pathname.includes("/admin/accounting-list")) {
            if (column.field === 'requiredDocumentation') {
                const predefinedOptions = [
                    { value: 'Deltagarlista', label: 'Deltagarlista' },
                    { value: 'Antal deltagare', label: 'Antal deltagare' },
                    { value: 'Program eller kallelse', label: 'Program eller kallelse' },
                ];

                const handleRequiredDocumentationChange = (selectedOption) => {
                    const updatedFilters = {
                        ...tempFilters,
                        requiredDocumentation: selectedOption ? selectedOption.value : '',
                        requiredDocumentationLabel: selectedOption ? selectedOption.label : ''
                    };

                    setTempFilters(updatedFilters);
                    setNormalValues((prev) => ({
                        ...prev,
                        requiredDocumentation: selectedOption ? selectedOption.value : ''
                    }));

                    setButtonClearIsDisable(false);
                    setButtonSearchIsDisable(false);
                };

                return (
                    <div>
                        <label className="ms-1">{column.label || column.field}</label>
                        <Select
                            classNamePrefix="sortable-filter-select"
                            key={column.field}
                            name={column.field}
                            placeholder="Sök/Välj dokumentation"
                            options={predefinedOptions}
                            value={
                                tempFilters.requiredDocumentation
                                    ? { value: tempFilters.requiredDocumentation, label: tempFilters.requiredDocumentationLabel ?? tempFilters.requiredDocumentation }
                                    : null
                            }
                            onChange={handleRequiredDocumentationChange}
                            isClearable={true}
                            theme={(theme) => ({
                                ...theme,
                                borderRadius: 5,
                                colors: {
                                    ...theme.colors,
                                    primary25: 'rgba(21, 117, 83, 0.25)',
                                    primary: '#67a58f',
                                    neutral0: 'var(--bs-body-bg)',
                                    neutral80: 'var(--bs-body-color)',
                                },
                            })}
                        />
                    </div>
                );
            }
        }

        if (window.location.pathname.includes("admin/cost-bearer")) {
            if (column.field === 'relatedOrgs') {
                const loadOrgTypeOptions = async (inputValue) => {
                    const apiCallOptions = {
                        method: "get",
                        url: `/role/admin/type/org/list-complete`,
                    };

                    try {
                        const response = await new Promise((resolve, reject) => {
                            ApiCall(apiCallOptions, resolve, reject);
                        });

                        const items = Array.isArray(response) ? response : (response?.items ?? []);

                        const options = items.map(o => ({
                            value: o.type ?? o.name ?? o.id ?? '',
                            label: (o.type && o.type.trim())
                                ? o.type
                                : ((o.name && o.name.trim()) ? o.name : (o.id ?? '')),
                        }));

                        if (!inputValue) return options;

                        const q = inputValue.trim().toLowerCase();
                        return options.filter(opt => (opt.label || '').toLowerCase().includes(q));
                    } catch (error) {
                        console.error("Error fetching org types for filter:", error);
                        return [];
                    }
                };

                const handleRelatedOrgsChange = (selectedOption) => {
                    const updatedFilters = {
                        ...tempFilters,
                        relatedOrgs: selectedOption ? selectedOption.value : '',
                        relatedOrgsLabel: selectedOption ? selectedOption.label : ''
                    };

                    setTempFilters(updatedFilters);
                    setNormalValues((prev) => ({
                        ...prev,
                        relatedOrgs: selectedOption ? selectedOption.value : ''
                    }));

                    setButtonClearIsDisable(false);
                    setButtonSearchIsDisable(false);
                };

                return (
                    <div>
                        <label className="ms-1">{column.label || column.field}</label>
                        <AsyncSelect
                            classNamePrefix="sortable-filter-select"
                            key={column.field}
                            name={column.field}
                            loadOptions={loadOrgTypeOptions}
                            onChange={handleRelatedOrgsChange}
                            value={
                                tempFilters.relatedOrgs
                                    ? { value: tempFilters.relatedOrgs, label: tempFilters.relatedOrgsLabel ?? tempFilters.relatedOrgs }
                                    : null
                            }
                            placeholder="Sök/Välj organisation"
                            isClearable={true}
                            cacheOptions
                            defaultOptions
                            theme={(theme) => ({
                                ...theme,
                                borderRadius: 5,
                                colors: {
                                    ...theme.colors,
                                    primary25: 'rgba(21, 117, 83, 0.25)',
                                    primary: '#67a58f',
                                    neutral0: 'var(--bs-body-bg)',
                                    neutral80: 'var(--bs-body-color)',
                                },
                            })}
                        />
                    </div>
                );
            }
        }

        if (window.location.pathname.includes("/admin/user-list")) {
            if (column.field === 'orgName') {
                const loadOptions = async (inputValue) => {
                    if (!inputValue) return [];
                    const apiCallOptions = {
                        method: "get",
                        url: `/org/organization/options?query=${inputValue}`,
                    };
            
                    try {
                        const response = await new Promise((resolve, reject) => {
                            ApiCall(apiCallOptions, resolve, reject);
                        });

                        return response.items.map(org => {
                            const type = org.type?.trim();
                            const label = type ? `${org.name} - ${type}` : org.name;
                            return {
                                value: org.id,
                                label,
                                name: org.name
                            };
                        });
                    } catch (error) {
                        console.error('Error fetching organizations:', error);
                        return [];
                    }
                };
            
                const handleOrgChange = (selectedOption) => {
                    const updatedFilters = {
                        ...tempFilters,
                        orgName: selectedOption ? selectedOption.value : '',
                        orgLabel: selectedOption ? selectedOption.label : '',
                    };

                    setTempFilters(updatedFilters);
                    setButtonClearIsDisable(false);
                    setButtonSearchIsDisable(false);
                };
            
                return (
                    <div>
                        <label className="ms-1">{column.label || column.field}</label>
                        <AsyncSelect
                            classNamePrefix="sortable-filter-select"
                            key={column.field}
                            name={column.field}
                            loadOptions={loadOptions}
                            onChange={handleOrgChange}
                            value={
                                tempFilters.orgName
                                    ? { value: tempFilters.orgName, label: tempFilters.orgLabel }
                                    : null
                            }
                            placeholder="Sök organisation"
                            isClearable={true}
                            cacheOptions
                            defaultOptions
                            theme={(theme) => ({
                                ...theme,
                                borderRadius: 5,
                                colors: {
                                    ...theme.colors,
                                    primary25: 'rgba(21, 117, 83, 0.25)',
                                    primary: '#67a58f',
                                    neutral0: 'var(--bs-body-bg)',
                                    neutral80: 'var(--bs-body-color)',
                                },
                            })}
                        />
                    </div>
                );
            }
        }

        if (window.location.pathname.includes("admin/economic-report") || window.location.pathname.includes("organization-economic-report")) {
            if (column.field === 'model') {
                const predefinedOptions = [
                    { value: 'Faktura', label: i18n.t('invoice.invoiceEconomicReport') },
                    { value: 'Arvode', label: i18n.t('honorarium.honorarium') },
                    { value: 'Snabb betalning', label: 'Snabb betalning' },
                    { value: 'Skatteverket', label: 'Skatteverket' },
                    { value: 'Förrättning', label: i18n.t('reimbursement.reportProcessing') },
                    { value: 'Betala', label: i18n.t('payReceive.pay') },
                    { value: 'Intern transaktion', label: i18n.t('internalTransaction.internalTransaction') },
                    { value: 'Manuell överföring', label: 'Manuell överföring' },
                    // Systemmässiga överföringar (momstransaktioner) ingår inte i rapporten
                    { value: 'Org QR-betalning', label: 'Org QR-betalning' }
                ];

                const handleModelChange = (selectedOptions) => {
                    setSelectedValues((prevState) => ({
                        ...prevState,
                        [column.field]: selectedOptions || []
                    }));
                    setButtonSearchIsDisable(false);
                    setButtonClearIsDisable(false);
                };

                return (
                    <div>
                        <label className="ms-1">{column.label || column.field}</label>
                        <Select
                            classNamePrefix="sortable-filter-select"
                            key={column.field}
                            name={column.field}
                            isMulti
                            placeholder="Ej valt"
                            options={predefinedOptions}
                            value={selectedValues[column.field] || []}
                            onChange={handleModelChange}
                            theme={(theme) => ({
                                ...theme,
                                borderRadius: 5,
                                colors: {
                                    ...theme.colors,
                                    primary25: 'rgba(21, 117, 83, 0.25)',
                                    primary: '#67a58f',
                                    neutral0: 'var(--bs-body-bg)',
                                    neutral80: 'var(--bs-body-color)',
                                },
                            })}
                        />
                    </div>
                );
            }
            // AQUI ORG NAME
            if (column.field === 'orgName') {
                const loadOptions = async (inputValue) => {
                    if (!inputValue) return [];
                    const apiCallOptions = {
                        method: "get",
                        url: `/org/organization/options?query=${inputValue}`,
                    };
            
                    try {
                        const response = await new Promise((resolve, reject) => {
                            ApiCall(apiCallOptions, resolve, reject);
                        });

                        return response.items.map(org => {
                            const type = org.type?.trim();
                            const label = type ? `${org.name} - ${type}` : org.name;
                            return {
                                value: org.id,
                                label,
                                name: org.name
                            };
                        });
                    } catch (error) {
                        console.error('Error fetching organizations:', error);
                        return [];
                    }
                };
            
                const handleOrgChange = (selectedOption) => {
                    const updatedFilters = {
                        ...tempFilters,
                        orgName: selectedOption ? selectedOption.value : '',
                        orgLabel: selectedOption ? selectedOption.label : '',
                    };

                    setTempFilters(updatedFilters);
                    setButtonClearIsDisable(false);
                    setButtonSearchIsDisable(false);
                };
            
                return (
                    <div>
                        <label className="ms-1">{column.label || column.field}</label>
                        <AsyncSelect
                            classNamePrefix="sortable-filter-select"
                            key={column.field}
                            name={column.field}
                            loadOptions={loadOptions}
                            onChange={handleOrgChange}
                            value={
                                tempFilters.orgName
                                    ? { value: tempFilters.orgName, label: tempFilters.orgLabel }
                                    : null
                            }
                            placeholder="Sök organisation"
                            isClearable={true}
                            cacheOptions
                            defaultOptions
                            theme={(theme) => ({
                                ...theme,
                                borderRadius: 5,
                                colors: {
                                    ...theme.colors,
                                    primary25: 'rgba(21, 117, 83, 0.25)',
                                    primary: '#67a58f',
                                    neutral0: 'var(--bs-body-bg)',
                                    neutral80: 'var(--bs-body-color)',
                                },
                            })}
                        />
                    </div>
                );
            }

            if (column.field === 'costBearerName') {
                const handleCostBearerNameChange = (selectedOptions) => {
                    setSelectedValues((prevState) => ({
                        ...prevState,
                        [column.field]: selectedOptions || []
                    }));
                    setButtonSearchIsDisable(false);
                    setButtonClearIsDisable(false);
                };

                return (
                    <div>
                        <label className="ms-1">{column.label || column.field}</label>
                        <Select
                            classNamePrefix="sortable-filter-select"
                            key={column.field}
                            name={column.field}
                            isMulti
                            placeholder="Ej valt"
                            options={costBearerOptions} // Usar las opciones cargadas dinámicamente
                            value={selectedValues[column.field] || []}
                            onChange={handleCostBearerNameChange}
                            isLoading={costBearerOptions.length === 0} // Mostrar loading mientras carga
                            theme={(theme) => ({
                                ...theme,
                                borderRadius: 5,
                                colors: {
                                    ...theme.colors,
                                    primary25: 'rgba(21, 117, 83, 0.25)',
                                    primary: '#67a58f',
                                    neutral0: 'var(--bs-body-bg)',
                                    neutral80: 'var(--bs-body-color)',
                                },
                            })}
                        />
                    </div>
                );
            }

            const OptionWithTooltip = (props) => {
                const fullLabel = `${props.data.value}-${props.data.orgType} ${props.data.orgName}`;
                return (
                    <components.Option {...props}>
                    <div title={fullLabel}>
                        {fullLabel}
                    </div>
                    </components.Option>
                );
            };

            const MultiValueLabelWithTooltip = (props) => {
                const fullLabel = `${props.data.value}-${props.data.orgType} ${props.data.orgName}`;
                return (
                    <components.MultiValueLabel {...props}>
                    <span title={fullLabel}>
                        {props.data.value}
                    </span>
                    </components.MultiValueLabel>
                );
            };

            if (column.field === 'costPlace') {
                const loadCostPlaceOptions = async (inputValue) => {
                    const apiCallOptions = {
                        method: "get",
                        url: `/export-data/cost-places/options?query=${inputValue || ''}`,
                    };
            
                    try {
                        const response = await new Promise((resolve, reject) => {
                            ApiCall(apiCallOptions, resolve, reject);
                        });
            
                        return response.items.map(costPlace => ({
                            value: costPlace.name,
                            label: costPlace.name,
                            orgName: costPlace.orgName,
                            orgType: costPlace.orgType
                        }));
                    } catch (error) {
                        console.error('Error fetching cost places:', error);
                        return [];
                    }
                };
            
                const handleCostPlaceChange = (selectedOptions) => {
                    setSelectedValues((prevState) => ({
                        ...prevState,
                        [column.field]: selectedOptions || []
                    }));
                    setButtonSearchIsDisable(false);
                    setButtonClearIsDisable(false);
                };
            
                return (
                    <div>
                        <label className="ms-1">{column.label || column.field}</label>
                        <AsyncSelect
                            classNamePrefix="sortable-filter-select"
                            key={column.field}
                            name={column.field}
                            loadOptions={loadCostPlaceOptions}
                            onChange={handleCostPlaceChange}
                            value={selectedValues[column.field] || []}
                            placeholder="Sök KST"
                            isMulti
                            cacheOptions
                            defaultOptions
                            components={{
                                Option: OptionWithTooltip,
                                MultiValueLabel: MultiValueLabelWithTooltip,
                            }}
                            theme={(theme) => ({
                                ...theme,
                                borderRadius: 5,
                                colors: {
                                    ...theme.colors,
                                    primary25: 'rgba(21, 117, 83, 0.25)',
                                    primary: '#67a58f',
                                    neutral0: 'var(--bs-body-bg)',
                                    neutral80: 'var(--bs-body-color)',
                                },
                            })}
                        />
                    </div>
                );
            }
        }

        if (column.type === 'dateTime') {
            return (
                <div className="sortable-mt-0">
                    <label className='ms-1'>{column.label || column.field}</label>
                    {!isDatePickerVisible ? (
                        <div className="sortable-relative-flex">
                            <input
                                type="text"
                                className="form-control sortable-filter-control sortable-input-full sortable-input-with-clear-padding"
                                value={startDate || endDate ? `${startDate ? startDate.toLocaleDateString() : ''} - ${endDate ? endDate.toLocaleDateString() : ''}` : "Ej valt"}
                                onClick={() => handleInputClick(column.field)}
                                readOnly
                            />
                            {startDate || endDate ? 
                                <button
                                    className="close-button sortable-clear-btn"
                                    onClick={() => handleDateButtonClick(column.field)}
                                >
                                    <i className="bx bx-x"></i>
                                </button>
                            : ''}
                        </div>
                    ) : (
                        <div className="sortable-datepicker-absolute" ref={datePickerWrapperRef}>
                            <DatePicker                             
                                selected={startDate}
                                onChange={(dates) => handleDateChange(dates, column.field)}
                                startDate={startDate}
                                endDate={endDate}
                                inline
                                selectsRange
                                className="w-100 border-grey-date rounded-2 padding-input-date"
                                maxDate={today}
                                locale={customLocale}
                                
                            />
                        </div>
                    )}
                </div>
            );
        }
        
        if (column.type === 'select') {
            const filterType = column.type || 'text';
            const filterPlaceholder =  column.label;
            let defaultSortOptions = [];

            if(column.field === 'status'){
                defaultSortOptions = [
                    ...(modelType === 'ActivityCalendar'
                        ? [
                            { name: column.field, value: "Genomförd", label: "Genomförd" },
                            { name: column.field, value: "Skall genomföras", label: "Skall genomföras" },
                            { name: column.field, value: "Preliminär", label: "Preliminär" },
                        ]
                        : []),
                    ...(modelType === 'Attest' || modelType === 'OrgAttest'
                        ? [{ name:column.field, value: "Godkänd", label: "Godkänd" },]
                        : []),
                    ...(modelType === 'Reimbursement'
                        ? [
                            { name:column.field, value: "Skickat", label: "För godkännande" },
                            { name:column.field, value: "Godkänd", label: "Godkänd" },
                            { name:column.field, value: "Förslag", label: "Utkast" },
                            { name:column.field, value: "Utbetald", label: "Utbetald" },
                          ]
                        : [
                            ...(modelType === 'Pay'
                                ? [
                                    { name:column.field, value: "Godkänd", label: "Godkänd" },
                                    { name:column.field, value: "Skickat", label: "För godkännande" },
                                ]
                                : [
                                    ...(modelType === 'Invoice' || modelType === 'AdminInvoice'
                                        ? [
                                            { name:column.field, value: "Attestera kö", label: "För godkännande" },
                                            { name:column.field, value: "Betald", label: "Betald" },
                                            { name:column.field, value: "Förslag", label: "Utkast" },
                                            { name:column.field, value: "Krediterad", label: "Krediterad" },
                                            { name:column.field, value: "Hanterad", label: "Hanterad" },
                                            { name:column.field, value: "Godkänd", label: "Godkänd" },
                                        ]
                                        : [
                                            ...(modelType === 'BankTransferPending'
                                                ? [
                                                    { name:column.field, value: "Obetald", label: "Obetald" },
                                                    { name:column.field, value: "Väntande", label: "Väntande" },
                                                ]
                                                : [
                                                    ...(modelType === 'Honorarium'
                                                        ? [
                                                            { name:column.field, value: "Pågår", label: "Pågår" },
                                                            { name:column.field, value: "Misslyckad", label: "Misslyckad" },
                                                            { name:column.field, value: "Skickat", label: "För godkännande" },
                                                            { name:column.field, value: "Godkänd", label: "Godkänd" },
                                                        ]
                                                        : modelType === 'ActivityCalendar' ? [] : [
                                                            { name: column.field, value: "Betald", label: "Utbetald" },
                                                        ]),
                                                ]),  
                                        ]),     
                                ]),                        
                          ]), 
                    ...(modelType === 'BankTransferPending'|| modelType === 'Honorarium'|| modelType === 'ActivityCalendar')   ? 
                    [] : [{ name:column.field, value: "Nekad", label: "Nekad" },],                                           
                    
                ];

                if (modelType === 'ApiKey') {
                    defaultSortOptions = [
                        { name: column.field, value: "Active", label: i18n.t('apiKey.statusActive') },
                        { name: column.field, value: "Expired", label: i18n.t('apiKey.statusExpired') },
                        { name: column.field, value: "Revoked", label: i18n.t('apiKey.statusRevoked') },
                    ];
                }
            }
            else if (column.field === 'model') {
                defaultSortOptions = [
                  { name: column.field, value: "Förrättning", label: "Ersättningsbegäran" },
                  { name: column.field, value: "Arvode", label: "Årsarvode" },
                  // o estas en la vista de /organization-attest-list
                  ...(modelType === 'AccountRow' || modelType === 'OrgAttest'
                    ? [{ name: column.field, value: "Faktura", label: "Skapa faktura" }]
                    : []),
                  ...(modelType === 'BankTransferHistory' || modelType === 'AccountRow'
                    ? [{ name: column.field, value: "Skatteverket", label: "Skatteinbetalning" }]
                    : []),
                  { name: column.field, value: "Betala", label: "Registrera utbetalning" },
                  ...(modelType === 'AccountRow'
                    ? [
                        { name: column.field, value: "Org QR-betalning", label: "Org QR-betalning" },
                        { name: column.field, value: "Snabb betalning", label: "Snabb betalning" },
                        { name: column.field, value: "Intern transaktion", label: "Överföra mellan kassor" },
                        { name: column.field, value: "Manuell överföring", label: "Manuell överföring" },
                        { name: column.field, value: "Systemmässiga överföringar", label: "Systemmässiga överföringar" }
                     ]
                    : []),
                ];
            }
            else if (column.field === 'displayStatus') {
                defaultSortOptions = [
                  { name: column.field, value: "Initialiserad", label: "Skickad" },
                  { name: column.field, value: "Betald", label: "Betald" },
                ];
            }
            else if(column.field === 'type'){  
                defaultSortOptions = [
                    ...(modelType === 'Pay'
                        ? [
                            { name:column.field, value: "Bankkonto", label: "Bankkonto" },
                            { name:column.field, value: "Bankgiro", label: "Bankgiro" },
                            { name:column.field, value: "Plusgiro", label: "Plusgiro" },
                          ]
                        : []),
                    ...(modelType === 'Invoice' || modelType === 'AdminInvoice'
                        ? [
                            { name:column.field, value: "Kredit Faktura", label: "Kredit Faktura" },
                            { name:column.field, value: "Faktura", label: "Faktura" },
                            ]
                        : []),
                ];  
            }
            else if(column.field === 'paymentInterval') {
                defaultSortOptions = [
                    { name:column.field, value: "1", label: "Årligen" },
                    { name:column.field, value: "12", label: "Månadsvis" },
                    { name:column.field, value: "4", label: "Dagligen (Test)" },
                ];
            }          
            return (
                <div>
                    <label className='ms-1'>{filterPlaceholder}</label>
                    {filterType === 'select' ? (
                        <Select
                            classNamePrefix="sortable-filter-select"
                            key={column.field}
                            name={column.field}
                            value={defaultSortOptions.find(option => option.value === normalValues[column.field])|| ''}
                            //onChange={handleSimpleInputChange}
                            onChange={(selected, actionMeta) => handleSimpleInputChange(selected, actionMeta)}
                            placeholder="Ej valt"
                            options={defaultSortOptions}
                            isClearable={true}
                            theme={(theme) => ({
                                ...theme,
                                borderRadius: 5,
                                colors: {
                                    ...theme.colors,
                                    primary25: 'rgba(21, 117, 83, 0.25)',
                                    primary: '#67a58f', 
                                    neutral0: 'var(--bs-body-bg)', 
                                    neutral80: 'var(--bs-body-color)',
                                },
                            })}
                        >
                        </Select>
                    ) : (
                        <input
                            key={column.field}
                            className="form-control sortable-filter-control sortable-input-full"
                            type="text"
                            name={column.field}
                            placeholder={filterPlaceholder}
                            onChange={debouncedHandleInputChange}
                        />
                    )}
                </div>
            );
        }
        else if(column.type === 'selectMulti'){
            const customStyles = {
                menu: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                }),
                menuList: (provided) => ({
                  ...provided,
                  maxHeight: "600px",
                  overflowY: "auto",
                }),
              };
            const filterType = column.type || 'text';        
            const filterPlaceholder =  column.label;
            let uniqueValues = [];
            if(column.field === 'costPlace'){
                uniqueValues = [...new Set(costPalces || [])].sort((a, b) => Number(a) - Number(b));
            }
            else if(column.field === 'costBearerName'){
                uniqueValues = [...new Set(costBearerNames || [])];
            }
            else if(column.field === 'model'){
                uniqueValues = [...new Set(models || [])];
            }
            else{
                uniqueValues = [...new Set((items || []).map(option => option[column.field]).filter(value => value && value.trim() !== ''))];
            }           
            return (
                <div>
                    <label className='ms-1'>{filterPlaceholder}</label>
                        <Select
                            classNamePrefix="sortable-filter-select"
                            key={column.field}
                            name={column.field}
                            isMulti
                            styles={customStyles}
                            placeholder="Ej valt"
                            menuPortalTarget={document.body}
                            theme={(theme) => ({
                                ...theme,
                                borderRadius: 5,
                                colors: {
                                  ...theme.colors,
                                  primary25: 'rgba(21, 117, 83, 0.25)',
                                  primary: '#67a58f', 
                                  neutral0: 'var(--bs-body-bg)', 
                                  neutral80: 'var(--bs-body-color)',
                                },
                              })}
                              
                            defaultValue={selectedValues[column.field] || ''}
                            onChange={(selected, actionMeta) => handleSelectMultiChange(selected, actionMeta)}
                            options={uniqueValues.map(option => ({
                                value: option,
                                label: column.field === 'model'
                                    ? (option === 'Förrättning' ? t('reimbursement.reportProcessing') :
                                       option === 'Arvode' ? t('honorarium.honorarium') :
                                       option === 'Betala' ? t('payReceive.pay') :
                                       option === 'Intern transaktion' ? t('internalTransaction.internalTransaction') :
                                       option === 'Faktura betald' ? t('invoice.invoiceEconomicReport') :
                                       option === 'Faktura' ? t('invoice.invoiceEconomicReport') :
                                       option)
                                    : option
                            }))}
                            value={selectedValues[column.field] || []}
                        >
                        </Select>
                </div>
            );
        } 
        else {
            return (
                <div>
                    <label className='ms-1'>{column.label || column.field}</label>
                    <input
                        key={column.field}
                        className="form-control sortable-input-fixed-260"
                        name={column.field}
                        type="text"
                        placeholder={column.label || column.field}
                        value={normalValues[column.field] || ''}
                        onChange={handleInputChange}
                    />
                </div>
            );
        }
    };






    // Function to render the generic pagination controls
    const renderGenericPaginationControls = () => {
        const pages = [];
       // console.log('pages', pages)
        let i = Math.max(1, currentPage - 2);
       // console.log('i del pagination', i)
        const maxPages = Math.min(totalPages, currentPage + 2);
        //console.log('maxPages del pagination', maxPages)
       // console.log('i <= maxPages', i <= maxPages)

        while (i <= maxPages) {
            pages.push(i);
            i += 1;
        }
        return (
            <>
                {paginationView === true ?
                    <>
                        <Col xs="auto" className="text-center p-0 sortable-pagination-buttons-col">
                            <button className="btn sortable-pagination-btn" aria-label={t('accessibility.goFirst')} onClick={() => {
                                updateSearchPressed(true);
                                setCurrentPage(1);
                                if (isOuterListWithInnerList) {
                                    SaveLocalStorage(pageStorageKey, 1);
                                }
                                else {
                                    SaveLocalStorage('currentPage', 1);
                                }
                                setCounter(0);
                            }} disabled={currentPage === 1}>
                                <i className="bx bx-chevrons-left sortable-pagination-icon"></i>
                            </button>
                            <button className="btn sortable-pagination-btn" aria-label={t('accessibility.prevPage')} onClick={() => {
                                updateSearchPressed(true);
                                setCurrentPage(currentPage - 1);
                                if (isOuterListWithInnerList) {
                                    SaveLocalStorage(pageStorageKey, currentPage - 1);
                                }
                                else {
                                    SaveLocalStorage('currentPage', currentPage - 1);
                                }
                                setCounter(0);
                            }} disabled={currentPage <= 1}>
                                <i className="bx bx-left-arrow-alt sortable-pagination-icon"></i>
                            </button>
                            {pages.map(page => (
                                <button key={page} className={`btn sortable-pagination-btn fw-bold ${currentPage === page ? 'active' : ''}`} onClick={() => {
                                    updateSearchPressed(true);
                                    setCurrentPage(page);
                                    if (isOuterListWithInnerList) {
                                        SaveLocalStorage(pageStorageKey, page);
                                    }
                                    else {
                                        SaveLocalStorage('currentPage', page);
                                    }
                                    setCounter(0);
                                }}>
                                    {page}
                                </button>
                            ))}
                            {/*<span> Page {currentPage} of {totalPages} </span>*/}
                            <button className="btn sortable-pagination-btn" aria-label={t('accessibility.backPage')} onClick={() => {
                                updateSearchPressed(true);
                                setCurrentPage(currentPage + 1);
                                if (isOuterListWithInnerList) {
                                    SaveLocalStorage(pageStorageKey, currentPage + 1);
                                }
                                else {
                                    SaveLocalStorage('currentPage', currentPage + 1);
                                }
                                setCounter(0);
                            }} disabled={currentPage >= totalPages}>
                                <i className="bx bx-right-arrow-alt sortable-pagination-icon"></i>
                            </button>
                            <button className="btn sortable-pagination-btn" aria-label={t('accessibility.goLast')} onClick={() => {
                                updateSearchPressed(true);
                                setCurrentPage(totalPages);
                                if (isOuterListWithInnerList) {
                                    SaveLocalStorage(pageStorageKey, totalPages);
                                }
                                else {
                                    SaveLocalStorage('currentPage', totalPages);
                                }
                                setCounter(0);
                            }} disabled={currentPage === totalPages}>
                                <i className="bx bx-chevrons-right sortable-pagination-icon"></i>
                            </button>
                        </Col>
                    </>
                    : ''}

                <Col xs="auto" className={`text-end p-0 sortable-pagination-total-col ${isReimbursementFiltersDesignMode ? (isMobile ? 'me-5' : 'me-3') : ''}`.trim()}>
                    { totalItems !== undefined ?  <span> Total: {totalItems} </span> : ''}
                </Col>


            </>
        );
    };

// Function to render pagination controls
    const renderPaginationControls = () => {
        const renderPaginationFunction = listOptions.layoutOptions && listOptions.layoutOptions.pagination && listOptions.layoutOptions.pagination.renderPaginationControls;
        if (renderPaginationFunction) {
            return renderPaginationFunction(currentPage, setCurrentPage, totalPages);
        }
        return renderGenericPaginationControls(); // Call the generic renderer
    };

    // Function to render the generic sort dropdown
    const renderGenericSortDropdown = () => {
        // Generate sort options based on the columns, defaulting to sortable unless explicitly set to false
        const sortOptions = columns
            .filter(column => column.sortable !== false) // Default to sortable unless explicitly set to false
            .flatMap(column => {
                // Use sortField if provided, otherwise default to field
                const sortField = column.sortField || column.field;
                return [
                    { value: `${sortField}_asc`, label: `${column.label || column.field} (Asc)` },
                    { value: `${sortField}_desc`, label: `${column.label || column.field} (Desc)` }
                ];
            });
        const currentSort = `${currentSortValues.sortField}_${currentSortValues.sort}`;
        const handleSortDropdownChange = (e) => {
            const [sortField, sort] = e.target.value.split('_');
            handleSortChange(sortField, sort);
            setCounter(0);
        };
        return (
            <select className="form-control arrow-select" value={currentSort} onChange={handleSortDropdownChange}>
                {sortOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
        );
    };

    //Pagination
    //det default options
    const defaultItemsPerPageOptions = [10, 20, 50, 100000]; // Default options

    //det default options to export
    const defaultExportFormatOptions = [ "csv", "xlsx", "pdf"]; // Default options

    const UpdateUserViewSetting  = async (value) => {
            try {



                const apiCallOptions = {
                    method: "put",
                    url: "/usersetting/view-setting",
                    data: {
                        viewId: viewName,
                        key: "itemsPerPage",
                        value: value
                    }
                };
                await ApiCall(apiCallOptions, setViewSettingUpdated);
            } catch (error) {
                console.log('error', error);
            }
    };

    function updateItemsPerPageInStorage(viewId, newItemsPerPage) {
        try {
        const storedSettingsJSON = localStorage.getItem('viewSetting');
        const settingsArray = storedSettingsJSON ? JSON.parse(storedSettingsJSON) : [];
        let viewFound = false;

        const updatedSettingsArray = settingsArray.map(setting => {
            if (setting.viewId === viewId) {
                viewFound = true;
                return {
                    ...setting,
                    settings: {
                    ...setting.settings,
                    itemsPerPage: newItemsPerPage
                    }
                };
            }
            return setting;
        });

        if (!viewFound) {
            updatedSettingsArray.push({
                viewId: viewId,
                settings: { itemsPerPage: newItemsPerPage }
            });
        }

        const newSettingsJSON = JSON.stringify(updatedSettingsArray);
        localStorage.setItem('viewSetting', newSettingsJSON);

        } catch (error) {
        console.error("Error updating configuration in Local Storage:", error);
        }
    }

// Function to render the generic items per page dropdown
    const renderGenericItemsPerPageDropdown = () => {
        const options = listOptions?.pagination?.itemsPerPageOptions || defaultItemsPerPageOptions;
        const itemsPageLabel = capitalizeFirstLetter(t('sortableList.itemsPage'));
        const selectOptions = options.map(option => ({
            value: option,
            label: option === 100000 ? t('sortableList.all') : `${option} ${itemsPageLabel}`,
        }));

        return (
            <Select
                className="pr-page-color"
                classNamePrefix="sortable-filter-select"
                value={selectOptions.find((opt) => opt.value === itemsPerPage) || selectOptions.find((opt) => opt.value === initialItemsPerPage)}
                options={selectOptions}
                isSearchable={false}
                onChange={(selectedOption) => {
                    const value = selectedOption?.value;
                    if (value === undefined || value === null) {
                        return;
                    }
                    if(economicReportFilter){
                        setEconomicFilterLoading(true);
                    }
                    setItemsPerPage(parseInt(value,10));
                    setCurrentPage(1);
                    SaveLocalStorage('itemsPerPage', value);
                    setCounter(0);
                    updateItemsPerPageInStorage(viewName, parseInt(value, 10));
                    UpdateUserViewSetting(value);
                }}
                theme={(theme) => ({
                    ...theme,
                    borderRadius: 5,
                    colors: {
                        ...theme.colors,
                        primary25: 'rgba(21, 117, 83, 0.25)',
                        primary: '#67a58f',
                        neutral0: 'var(--bs-body-bg)',
                        neutral80: 'var(--bs-body-color)',
                    },
                })}
            />
        );
    };


    const toggle = () => setDropdownOpen(prevState => !prevState);

    useEffect(()=>{
    if(apiResponse !== null && optionExport !== null){
            fetchExport(optionExport, apiResponse.items);
    }
    },[apiResponse])
//Function to export files and get list items
    const fetchItemsToExport = async (params, formatOption) => {
        const notificationId = Date.now();
        currentExportId = notificationId;
        
        setExportNotifications(prev => [...prev, {
            id: notificationId,
            format: formatOption ? formatOption.toUpperCase() : '',
            status: 'generating',
            fileName: ''
        }]);

        // Security timeout to handle long requests
        const timeoutId = setTimeout(() => {
            setExportNotifications(prev => 
                prev.map(notif => 
                    notif.id === notificationId && notif.status === 'generating'
                        ? { ...notif, status: 'error' } 
                        : notif
                )
            );
            setTimeout(() => {
                setExportNotifications(prev => prev.filter(notif => notif.id !== notificationId));
            }, 5000);
        }, 40000); // 40 seconds

        const isEconomicReport = window.location.pathname === '/admin/economic-report' || window.location.pathname === '/organization-economic-report';
        let url = urlGetListItemsToExport;
        if (url.includes('?')) {
            url += `&page=1&perPage=100000`;
        } else {
            url += `?page=1&perPage=100000`;
        }

        const currentSearchParams = new URLSearchParams(window.location.search);
        const preserveIdView = window.location.pathname.includes('/admin/user-list/user') 
            || window.location.pathname.includes('/admin/organization-list/organization')
            || window.location.pathname.includes('/admin/digital-account-list/digital-account')
            || window.location.pathname.includes('/admin/report-tax-info')
            || window.location.pathname.includes('/bank-transfer-list/bank-transfer-history-list');

        if (preserveIdView) {
            // persistFilters lists keep their filters out of the URL: export with the query of their last search
            const exportSearchParams = persistFilters ? new URLSearchParams(persistedQueryRef.current) : currentSearchParams;
            for (let [key, value] of exportSearchParams.entries()) {
                if (key !== 'page' && key !== 'perPage') {
                    if (!url.includes(`${key}=`)) {
                        url += `&${key}=${encodeURIComponent(value)}`;
                    }
                }
            }
        } else {
        if (filterTestIsAvailable) {
            const storageKey = getStorageKeyForPath(window.location.pathname, viewName);
            if (storageKey) {
                let modifiedSearch = localStorage.getItem('search') || "";

                if (isEconomicReport) {
                    modifiedSearch = modifiedSearch
                        .replace('&fromSearchButton=false', '&fromSearchButton=true')
                        .replace(/(^|&)page=\d+/g, '')
                        .replace(/(^|&)perPage=\d+/g, '');
                }

                if (modifiedSearch && !modifiedSearch.startsWith('&')) {
                    modifiedSearch = '&' + modifiedSearch;
                }

                url += modifiedSearch;
            } else if (typeof updateFilter === "string" && updateFilter !== "") {
                url += `&${updateFilter}`;
            }
        } else {
            if (typeof updateFilter === "string" && params !== "searchCriteria=" && updateFilter !== "") {
                url += `&searchCriteria=${updateFilter}`;
            }
        }

            for (let [key, value] of currentSearchParams.entries()) {
                if (key !== 'page' && key !== 'perPage' && !url.includes(`${key}=`)) {
                    url += `&${key}=${encodeURIComponent(value)}`;
                }
            }
        }

        if (isEconomicReport && !url.includes('fromSearchButton=')) {
            url += `&fromSearchButton=true`;
        }

        const apiCallOptions = {
            method: "get",
            url: url,
        };
        try {
            await ApiCall(apiCallOptions, 
                (response) => {
                    clearTimeout(timeoutId);
                    setApiResponse(response);
                },
                (errorTitle) => {
                    clearTimeout(timeoutId);
                    setExportNotifications(prev =>
                        prev.map(notif =>
                            notif.id === notificationId ? { ...notif, status: 'error' } : notif
                        )
                    );
                    setTimeout(() => {
                        setExportNotifications(prev => prev.filter(notif => notif.id !== notificationId));
                    }, 5000);
                }
            );        
        } catch (error) {
            clearTimeout(timeoutId);
            console.log(error);
            setExportNotifications(prev => 
                prev.map(notif => 
                    notif.id === notificationId 
                        ? { ...notif, status: 'error' } 
                        : notif
                )
            );
            setTimeout(() => {
                setExportNotifications(prev => prev.filter(notif => notif.id !== notificationId));
            }, 5000);
        }
    };

    const handleApiResponse  = (apiResponse, exportId) => {

        if (apiResponse !== null && apiResponse !== undefined) {
            
            let file;
            let fileName = '';
            if (apiResponse instanceof Blob) {                
                file = apiResponse;
            } 
            else {
                file = new Blob([apiResponse]);
            } 

            let type = file.type.split("/")
            let extension = type[1]

            const now = new Date();
            const year = now.getFullYear();
            const month = (now.getMonth() + 1).toString().padStart(2, '0');
            const day = now.getDate().toString().padStart(2, '0');
            const hours = now.getHours().toString().padStart(2, '0');
            const minutes = now.getMinutes().toString().padStart(2, '0');
            const seconds = now.getSeconds().toString().padStart(2, '0');

            if (extension === 'pdf' || extension === 'csv') {
                fileName = `${nameFile}_${year}${month}${day}_${hours}${minutes}${seconds}.${extension}`;
            } 
            else {
                fileName = `${nameFile}_${year}${month}${day}_${hours}${minutes}${seconds}.xlsx`;
            }
            
            const urlToFile = URL.createObjectURL(file);                    
            const link = document.createElement('a');
            link.href = urlToFile;
            link.setAttribute('download', fileName);                    
            document.body.appendChild(link);
            link.click();                   
            document.body.removeChild(link);
            
            // Update notification to completed
            setExportNotifications(prev =>
                prev.map(notif =>
                    notif.id === exportId
                        ? { ...notif, status: 'completed', fileName }
                        : notif
                )
            );

            setTimeout(() => {
                setExportNotifications(prev =>
                    prev.filter(notif => notif.id !== exportId)
                );
            }, 4000);
        }                     
    };

    const addStatusToList = (list, modelExport) => {
        if (modelExport !== null && modelExport !== undefined && modelExport === 'AdminUsers') {
            return list.map(item => {
                if (!item.hasOwnProperty('status')) {
                    return {
                        visualId: item.visualId,
                        firstName: item.firstName,
                        lastName: item.lastName,
                        pnr: item.pnr,
                        status: 'Aktiv',
                        email: item.email,
                        phone: item.phone
                    };
                }
                return item;
            });
        }

        if (modelExport !== null && modelExport !== undefined && modelExport === 'Pay') {
            return list.map(item => {
                if (!item.hasOwnProperty('date')) {
                    return {
                        visualId: item.visualId,
                        type: item.type,
                        date: 'Snarast ',
                        description: item.description,
                        amount: item.amount,
                        status: item.status,
                        createdByName: item.createdByName
                    };
                }
                return item;
            });
        }
        return list;
    };

    const fetchExport = async (formatType, list) => {
        const lastNotificationId = exportNotifications[exportNotifications.length - 1]?.id;
        let exportId = lastNotificationId; // usamos este ID para esta exportación
        
        setExportNotifications(prev => 
            prev.map(notif => 
                notif.id === exportId 
                    ? { ...notif, format: formatType.toUpperCase(), status: 'generating' } 
                    : notif
            )
        );
        
        list = addStatusToList(list, modelExport);

        let url = '/export-data'
        let ignoreFields = [];
        let includeFieldsToEconomiReport = [];
        if(ignoreFieldsEconomicReportToExport && modelType === 'EconomicReport'){
            const formattedGroupBy = formatGroupBy(ignoreFieldsEconomicReportToExport);
            includeFieldsToEconomiReport = [formattedGroupBy, "amount"];
            if (selectedField && formattedGroupBy === "orgName") {
                includeFieldsToEconomiReport.unshift("orgType");
            }
        }
        let json = {
            format: formatType,
            includeFields:includeFieldsToEconomiReport.length > 0 ? includeFieldsToEconomiReport : ignoreFieldsToExport ? ignoreFieldsToExport : ignoreFields,
            list: list,
            modelExport : modelExport,
            model : modelType ? modelType : ''
        };

        const apiCallOptions = {
            method: "post",
            url: url,
            data: json,
            responseType : "blob",
        };

        try {
            await ApiCall(apiCallOptions, (res) => handleApiResponse(res, exportId));
        } catch (error) {
            console.log(error);
            // Update error notification
            setExportNotifications(prev => 
                prev.map(notif => 
                    notif.id === exportId 
                        ? { ...notif, status: 'error' } 
                        : notif
                )
            );
            setTimeout(() => {
                setExportNotifications(prev => prev.filter(notif => notif.id !== exportId));
            }, 4000);
        }         
    };

    const handleOptionSelect = async (option) => { 
        setOptionExport(option);
        fetchItemsToExport(params)
    };

    const renderGenericItemsExportDropdown =  (iconOnly = false) => { 
        const options = defaultExportFormatOptions;    
        return (
            <div className="p-0">
                <Dropdown isOpen={dropdownOpen} toggle={toggle}>
                    <DropdownToggle
                        className={`${iconOnly ? 'bg-green-soft soft-icon-bordered' : 'bg-green'} border-0 ${iconOnly ? 'd-flex align-items-center justify-content-center' : ''}`}
                        caret={!iconOnly}
                        title={iconOnly ? (t('sortableList.exportData') || 'Export') : undefined}
                    >
                        {iconOnly ? <i className="bx bx-download fs-4" /> : t('sortableList.exportData')}
                    </DropdownToggle>
                    <DropdownMenu>
                        {options.map(option => (
                            <DropdownItem key={option} onClick={() => handleOptionSelect(option)}>
                                {option}
                            </DropdownItem>
                        ))}
                    </DropdownMenu>
                </Dropdown>
            </div>                   
        );
        
    };

// Function to render the items per page dropdown
    const renderItemsPerPageDropdown = () => {
        const renderDropdownFunction = listOptions.layoutOptions && listOptions.layoutOptions.pagination && listOptions.layoutOptions.pagination.renderItemsPerPageDropdown;
        if (renderDropdownFunction) {
            return renderDropdownFunction(setItemsPerPage); // Pass setItemsPerPage as an argument
        }
        return renderGenericItemsPerPageDropdown(); // Call the generic renderer
    };

    // Function to render the Export file dropdown
    const renderExportFileDropdown = (iconOnly = false) => { 
        return renderGenericItemsExportDropdown(iconOnly); // Call the generic renderer
    };

    // Function to extract column headers from listOptions or all items
    const extractColumnHeaders = () => {
        if (columns) {
            // Use the 'field' property from each column in listOptions
            return columns.map(column => column.field);
        } else {
            // If columns are not provided, extract keys from all items
            const uniqueKeys = new Set();
            items.forEach(item => {
                Object.keys(item).forEach(key => uniqueKeys.add(key));
            });
            return Array.from(uniqueKeys);
        }
    };

    const [checkSort, setCheckSort] = useState(false);
    // Function to handle sorting when a column header is clicked
    const onColumnHeaderClick = (header) => {
        // Find the corresponding column configuration
        const columnConfig = columns.find(column => column.field === header);
        if (columnConfig && columnConfig.sortable !== false) {
            const sortField = columnConfig.sortField || columnConfig.field;
            const newSortOrder = params.sortField === sortField && params.sort === 'asc' ? 'desc' : 'asc';
            handleSortChange(sortField, newSortOrder);
            setTimeout(() => {
                setCheckSort(true);
            },80);
        }
    };

    useEffect(() => {
       if (checkSort === true && search !== null) {
           setParams(search);
           setCheckSort(false);
       }
    }, [checkSort]);

    useEffect(() => {
        if (search !== null) {
            setParams(search);
            setCheckSort(false)
        }
    }, []);

    const createFilterInput = (column) => {
        if (column.filter === false) {
            return null;
        }
        const handleInputChange = (e) => {
            const updatedFilters = { ...params, [column.field]: e.target.value };
            handleFilterChange(updatedFilters);
            setCurrentPage(1);
            setCounter(0);
        };
        const debouncedHandleInputChange = debounce(handleInputChange);
        // Only create filters for columns that have a filter configuration
        if (column.filter) {
            const filterType = column.filter.type || 'text';
            const filterPlaceholder = column.filter.placeholder || column.label;
            return filterType === 'select' ? (
                <select
                    key={column.field}
                    className="form-control sortable-filter-control"
                    defaultValue={column.filter.default || ''}
                    onChange={debouncedHandleInputChange}
                >
                    <option key={0} value="">{`Select ${filterPlaceholder}`}</option>
                    {column.filter.options.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
            ) : (
                <input
                    key={column.field}
                    className="form-control sortable-filter-control"
                    type="text"
                    placeholder={filterPlaceholder}
                    onChange={debouncedHandleInputChange}
                />
            );
        } else {
            return <input
                key={column.field}
                className="form-control sortable-filter-control"
                type="text"
                placeholder={column.label || column.field}
                onChange={debouncedHandleInputChange}
            />
        }
    };

    const addButton = listOptions.actions && listOptions.actions.addButton;
    const renderAddButton = (item) => {
        return (
            <Row>
                {typeof addButton === "function" ? addButton(item) :''}
            </Row>
        );

    }
    const formatGroupBy = (str) => str ? str.charAt(0).toLowerCase() + str.slice(1) : "";

    const getSalaryTransactionAmount = (item) => {
        if (!item?.salaryTransactions) return 0;
        const forrattning = item.salaryTransactions.find(st => st.model === 'Förrättning');
        if (!forrattning?.transactionTypes) return 0;
        const anvandarlon = forrattning.transactionTypes.find(tt => tt.type === 'Användarlön');
        return anvandarlon?.amount ?? 0;
    };

    const getSalaryArvodeAmount = (item) => {
        if (!item?.salaryTransactions) return 0;
        const arvode = item.salaryTransactions.find(st => st.model === 'Arvode');
        if (!arvode?.transactionTypes) return 0;
        const anvandarlon = arvode.transactionTypes.find(tt => tt.type === 'Användarlön');
        return anvandarlon?.amount ?? 0;
    };

    const loadSalaryRowData = async (itemId) => {

        setSalaryRowLoading(prev => ({ ...prev, [itemId]: true }));
        try {
            const response = await new Promise((resolve, reject) => {
                ApiCall(
                    { method: 'get', url: `/salary-sheet/payments?salaryId=${itemId}` },
                    (data) => resolve(data),
                    (error) => reject(error)
                );
            });
            const items = Array.isArray(response) ? response : [];
            const filtered = items.filter(entry => Array.isArray(entry?.paymentDetails) && entry.paymentDetails.length > 0);

            setSalaryRowData(prev => ({ ...prev, [itemId]: filtered }));
            setExpandedSalaryRows(prev => ({ ...prev, [itemId]: filtered.length > 0 }));
        } catch (error) {
            console.error('Error loading salary row data', error);
            setSalaryRowData(prev => ({ ...prev, [itemId]: [] }));
            setExpandedSalaryRows(prev => ({ ...prev, [itemId]: false }));
        } finally {
            setSalaryRowLoading(prev => ({ ...prev, [itemId]: false }));
        }
    };

    const getSalaryMonthParam = (item) => {
        if (!item?.date) return null;
        const parts = String(item.date).split('-');
        return parts[0] ?? null;
    };

    const getSalaryYearParam = (item) => {
        if (!item?.date) return null;
        const parts = String(item.date).split('-');
        return parts[1] ?? null;
    };

    const formatDateWithoutTime = (value) => {
        if (!value) return '-';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('sv-SE');
    };

    const getPayoutAmount = (entry) => {
        const details = Array.isArray(entry?.paymentDetails) ? entry.paymentDetails : [];
        return details.reduce((sum, detail) => {
            const value = Number(detail?.instructedAmount ?? 0);
            return sum + (Number.isFinite(value) ? value : 0);
        }, 0);
    };

    const openPaymentDetailInfo = async (detail, userId = null) => {
        const model = detail?.model || detail?.Model || '';
        const modelId = detail?.modelId || detail?.ModelId || '';
        if (!modelId) return;

        if (model === 'Förrättning') {
            setInfoPanelLoading(true);
            setInfoPanelOpen(true);
            setInfoPanelUserId(userId);
            setInfoPanelVisualId(detail?.visualId ?? detail?.id ?? null);
            try {
                await ApiCall(
                    { method: 'get', url: `/reimbursement?id=${modelId}` + (userId && userId !== "" && userId !== "undefined" ? `&userId=${userId}` : "") },
                    (response) => {
                        setInfoPanelItemId(response?.id ?? modelId);
                        setInfoPanelLoading(false);
                    },
                    () => {
                        setInfoPanelItemId(modelId);
                        setInfoPanelLoading(false);
                    }
                );
            } catch {
                setInfoPanelItemId(modelId);
                setInfoPanelLoading(false);
            }
        }
    };

    // ─── HELPERS ────────────────────────────────────────────────────────────────

    const STATUS_CFG_SL = {
        // Colores exactos del mockup
        Förslag: {
            label: "Godkänd för utbetalning",
            bg: "#C8EDD9",       // verde menta saturado
            color: "#0D6E45",    // verde oscuro
        },
        Skickat: {
            label: "Inväntar godkännande",
            bg: "#D4E1F7",       // azul lavanda claro
            color: "#1A3F8F",    // azul oscuro
        },
        Nekad: {
            label: "Nekade ärenden",
            bg: "#F5B8B8",       // rosa coral
            color: "#8B1A1A",    // rojo oscuro
        },
        Hanterad: {
            label: "Hanterad",
            bg: "#FFF4CC",       // amarillo claro
            color: "#8A6500",    // marrón oscuro
        },
        // Utbetald → sin badge, texto con fecha en verde
    };

    const StatusBadgeSL = ({ status, item }) => {
        if (status === "Utbetald") {
            const formatted = item?.paidDateTime
                ? ChangeDateFormat('date', item.paidDateTime)
                : "";
            return (
                <span className="sl-status-paid">
                    {formatted ? `Utbetald: ${formatted}` : "Utbetald"}
                </span>
            );
        }
        const cfg = STATUS_CFG_SL[status];
        if (!cfg) {
            return <span className="sl-status-default">{status}</span>;
        }
        const statusClass =
            status === "Förslag"
                ? "sl-status-badge-proposal"
                : status === "Skickat"
                    ? "sl-status-badge-sent"
                    : status === "Nekad"
                        ? "sl-status-badge-denied"
                        : status === "Hanterad"
                            ? "sl-status-badge-handled"
                            : "sl-status-badge-default";

        return (
            <span className={`sl-status-badge ${statusClass}`}>
                {cfg.label}
            </span>
        );
    };

    const groupItemsByMonth = (items = [], dateField = "createdAt") => {
        const groups = {};
        items.forEach(item => {
            const raw = item[dateField] || item.createdAt || item.date;
            const d = raw ? new Date(raw) : new Date();
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            const label = d.toLocaleDateString("sv-SE", { month: "long", year: "numeric" });
            const labelCap = label.charAt(0).toUpperCase() + label.slice(1);
            if (!groups[key]) groups[key] = { label: labelCap, items: [] };
            groups[key].items.push(item);
        });
        return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
    };

    const toggleMonthGroupVisibility = (groupKey) => {
        setCollapsedMonthGroups((prev) => ({
            ...prev,
            [groupKey]: !prev[groupKey],
        }));
    };

    const ReimbursementDesktopActionMenu = ({ item, actionColumn, totalItems }) => {
        return <ActionMenu item={item} actionColumn={actionColumn} totalItems={totalItems} iconType="bx-dots-horizontal-rounded" />;
    };

// ─── genericRenderTable ──────────────────────────────────────────────────────

    const genericRenderTable = (items) => {
        const isEconomicReport = window.location.pathname === '/admin/economic-report'
            || window.location.pathname === '/admin/economic-report-info'
            || window.location.pathname === '/organization-economic-report'
            || window.location.pathname === '/organization-economic-report-info';
        const localSearch = localStorage.getItem('search') || '';
        const hasSubtypesInSearch = localSearch.includes('splitIntoSubtypes=true');

        const visibleColumns = columns.filter(col => {
            if (
                isEconomicReport &&
                !activeSplitIntoSubtypes &&
                !hasSubtypesInSearch &&
                ['relatedReimbursementModel', 'relatedReimbursementExtraInfo'].includes(col.field)
            ) return false;
            return true;
        });

        const shouldHideActionColumn = isEconomicReport && (
            localSearch.includes('groupby=') || selectedField !== ''
        );
        const actionColumn         = !shouldHideActionColumn && listOptions.actions && listOptions.actions.actionColumn;
        const checkColumn          = listOptions.actions && listOptions.actions.checkColumn;
        const allCheckColumn       = listOptions.actions && listOptions.actions.allCheckColumn;
        const actionAllCheckColumn = listOptions.actions && listOptions.actions.actionAllCheckColumn;
        const filterInHeader       = listOptions.layoutOptions?.table?.filterInHeader;

        // byMonth: agrupación activada para todas las vistas salvo economic report
        const dateFields = ['createdAt', 'CreatedAt', 'visualId', 'VisualId'];
        let currentSortField = '';
        if (search !== null) {
            const mf = search.match(/sortField=([^&]*)/);
            if (mf) currentSortField = mf[1];
        }
        const isSortingByDate = dateFields.includes(currentSortField) || currentSortField === '';
        // Also the ReportProceedingList embedded in the admin user page, so its table matches the header and filter above it
        const isReimbursementDesktopReportProceeding = window.location.pathname === '/report-proceeding-list' || listOptions?.viewName === 'ReportProceedingList';
        const isSalarySheetDesktop = window.location.pathname === '/salary-sheet' /*|| window.location.pathname === '/admin/salary-sheet'*/;
        const isApiKeyList = listOptions?.viewName === 'AdminApiKeyList';
        const isDesktopCardStyle = isReimbursementDesktopReportProceeding || isSalarySheetDesktop || isApiKeyList || (useModernListDesign && !isMobile);

        const groupByMonthEnabled =
            listOptions?.groupByMonth == true
            && !isEconomicReport
            && (isDesktopCardStyle || isSortingByDate);

        const sortedGroups = groupByMonthEnabled
            ? groupItemsByMonth(items, "createdAt")
            : [["all", { label: null, items }]];
        const getReimbursementColumnWeight = (field) => {
            // Per-view override, e.g. listOptions.columnWeights = { value: 0.3 } for a narrow column
            if (listOptions?.columnWeights?.[field] !== undefined) return listOptions.columnWeights[field];
            switch (field) {
                case 'visualId':
                    return 0.75; // slightly larger for high-zoom readability
                case 'date':
                    return isSalarySheetDesktop ? 1.6 : 1.15; // wider for SalarySheet desktop
                case 'userName':
                    return 1.25; // about 25% bigger
                case 'createdAt':
                    return 1; // default width
                case 'purpose':
                    return 1.5; // about 50% bigger
                case 'grossAmount':
                    return 1.1; // slightly larger for readability at high zoom
                case 'totalAmountUserSalary':
                    return 1.1; // slightly larger for readability at high zoom
                case 'status':
                    return 1.25; // slightly larger for better readability at high zoom
                case 'orgName':
                    return 1.25; // about 25% bigger
                default:
                    return 1;
            }
        };

        const checkColumnWeight = checkColumn ? 0.5 : 0;
        const actionColumnWeight = actionColumn ? 0.4 : 0;
        const dataColumnsWeight = visibleColumns.reduce((sum, col) => sum + getReimbursementColumnWeight(col.field), 0);
        const totalReimbursementColumnsWeight = checkColumnWeight + actionColumnWeight + dataColumnsWeight;
        const getReimbursementColumnWidth = (weight) =>
            totalReimbursementColumnsWeight > 0
                ? `${((weight / totalReimbursementColumnsWeight) * 100).toFixed(4)}%`
                : 'auto';

        const SortIcon = ({ field }) => {
            let sortLS = '', fieldLS = '';
            if (search !== null) {
                const ms = search.match(/sort=([^&]*)/);
                const mf = search.match(/sortField=([^&]*)/);
                if (ms) sortLS = ms[1];
                if (mf) fieldLS = mf[1];
            }
            const isActive = fieldLS === field || currentSortValues.sortField === field;
            const dir = fieldLS === field ? sortLS : currentSortValues.sort;
            if (!isActive) return <span className="sl-sort-icon-inactive">↕</span>;
            return (
                <>
                    <span className="sl-sort-icon-active">
                        {dir === 'asc' ? '↑' : '↓'}
                    </span>
                    {spinnerLoading && (
                        <Spinner animation="border" variant="success" size="sm" className="sl-sort-spinner" />
                    )}
                </>
            );
        };

        // si estoy en la vista /report-proceeding-list o /salary-sheet hacer este return si no hacer otro
        if (isDesktopCardStyle) {
            return (
                <>
                    <style>{`
                        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
                        .sl-wrap { font-family: 'Plus Jakarta Sans', sans-serif; }
                        .sl-th-btn {
                            background: none; border: none; padding: 0; cursor: pointer;
                            display: inline-flex; align-items: center; gap: 2px;
                            font-family: inherit; font-size: 11px; font-weight: 700;
                            color: #157553; text-transform: uppercase; letter-spacing: .07em;
                            white-space: nowrap;
                        }
                        .sl-th-btn:hover { color: #157553; }
                        .sl-table-wrap { width: 100%; }
                        .sl-table { width: 100%; table-layout: fixed; border-collapse: collapse; }
                        .sl-table td, .sl-table th { word-break: break-word; overflow-wrap: anywhere; white-space: normal; min-width: 0; }
                        .sl-col-action { white-space: normal; width: auto; min-width: 0; }
                    `}</style>

                    <div className={`sl-wrap sl-wrap-spacing ${useModernListDesign ? 'sl-wrap--modern' : ''}`.trim()}>
                        {sortedGroups.map(([key, group]) => (
                            <div key={key} className="sl-month-block">
                                {groupByMonthEnabled && group.label && (
                                    <div className="sl-month-divider">
                                        <span className="sl-month-divider-label">
                                            {group.label}
                                        </span>
                                        <div className="sl-month-divider-line" />
                                        {collapsedMonthGroups[key] && (
                                            <button
                                                type="button"
                                                className="sl-month-toggle-btn"
                                                onClick={() => toggleMonthGroupVisibility(key)}
                                                aria-label={t('sortableList.expand') || 'Expand month rows'}
                                            >
                                                <i className="bx bx-caret-down sl-month-toggle-icon" />
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Card */}
                                {!collapsedMonthGroups[key] && (
                                    <div className="sl-card-shell">
                                        <div className="sl-table-wrap">
                                            <table className="sl-table sl-table-layout">
                                            <colgroup>
                                                {isSalarySheetDesktop && <col style={{ width: 32 }} />}
                                                {checkColumn && <col style={{ width: getReimbursementColumnWidth(checkColumnWeight) }} />}
                                                {visibleColumns.map((col) => (
                                                    <col key={`${col.field}-col`} style={{ width: getReimbursementColumnWidth(getReimbursementColumnWeight(col.field)) }} />
                                                ))}
                                                {actionColumn && <col style={{ width: getReimbursementColumnWidth(actionColumnWeight) }} />}
                                            </colgroup>
                                            <thead>
                                                <tr className="sl-table-head-row">
                                                    {isSalarySheetDesktop && (
                                                        <th className="sl-th-base" style={{ width: 32, textAlign: 'center' }}></th>
                                                    )}
                                                    {checkColumn && (
                                                        <th className="sl-th-base">
                                                            {allCheckColumn && actionAllCheckColumn && !isReadOnly
                                                                ? <input id="checkInput" type="checkbox" onChange={(e) => actionAllCheckColumn(e)} />
                                                                : ''}
                                                        </th>
                                                    )}
                                                    {visibleColumns.map(col => (
                                                        <th
                                                            key={col.field}
                                                            data-amount-header={col.field === 'totalAmountUserSalary' ? 'true' : undefined}
                                                            data-align-left-header={col.field === 'visualId' || col.field === 'createdAt' || col.field === 'fileCreatedAt' ? col.field : undefined}
                                                            className={col.sortable !== false ? `sl-th-base ${col.field === 'totalAmountUserSalary' ? 'sl-td-amount-right' : ''}` : `sl-th-base sl-th-no-sort ${col.field === 'totalAmountUserSalary' ? 'sl-td-amount-right' : ''}`}
                                                            onClick={() => col.sortable !== false && onColumnHeaderClick(col.field)}
                                                        >
                                                            {col.sortable !== false ? (
                                                                <button className="sl-th-btn">
                                                                    {col.label || col.field}
                                                                    <SortIcon field={col.field} />
                                                                </button>
                                                            ) : (
                                                                <span className="sl-th-label-static">
                                                                    {col.label || col.field}
                                                                </span>
                                                            )}
                                                        </th>
                                                    ))}
                                                    {actionColumn && (
                                                        <th
                                                            className={`sl-col-action sl-th-base sl-th-action ${groupByMonthEnabled && !isSalarySheetDesktop ? 'sl-th-action-toggle' : ''}`}
                                                            onClick={() => groupByMonthEnabled && !isSalarySheetDesktop && toggleMonthGroupVisibility(key)}
                                                        >
                                                            <span
                                                                className={`sl-th-action-chevron ${groupByMonthEnabled && !isSalarySheetDesktop ? 'sl-th-action-chevron--expanded' : ''}`}
                                                                role={groupByMonthEnabled && !isSalarySheetDesktop ? 'button' : undefined}
                                                                tabIndex={groupByMonthEnabled && !isSalarySheetDesktop ? 0 : undefined}
                                                                onKeyDown={(e) => {
                                                                    if (!groupByMonthEnabled || isSalarySheetDesktop) return;
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        toggleMonthGroupVisibility(key);
                                                                    }
                                                                }}
                                                                aria-label={groupByMonthEnabled && !isSalarySheetDesktop
                                                                    ? (t('sortableList.collapse') || 'Collapse month rows')
                                                                    : undefined}
                                                            >
                                                                {!isSalarySheetDesktop && <i className="bx bx-caret-up"/>}
                                                            </span>
                                                        </th>
                                                    )}
                                                </tr>

                                                {filterInHeader && (
                                                    <tr className="sl-table-head-filter-row">
                                                        {checkColumn && <th className="sl-th-base" />}
                                                        {visibleColumns.map(col => (
                                                            <th key={`${col.field}-filter`} className="sl-th-base">
                                                                {createFilterInput(col)}
                                                            </th>
                                                        ))}
                                                        {actionColumn && <th className="sl-th-base" />}
                                                    </tr>
                                                )}
                                            </thead>

                                            <tbody>
                                                {group.items.map((item, index) => {
                                                    const itemId = item?.id ?? index;
                                                    const isSalaryExpanded = !!expandedSalaryRows[itemId];
                                                    return (
                                                    <React.Fragment key={index}>
                                                    <tr
                                                        className={`sl-row ${isSalarySheetDesktop && isSalaryExpanded ? 'sl-row-no-divider' : index < group.items.length - 1 ? 'sl-row-divider' : ''} ${listOptions.actions?.rowClick ? 'sl-row-clickable' : ''}`}
                                                        onClick={listOptions.actions?.rowClick
                                                            ? () => listOptions.actions.rowClick(item)
                                                            : undefined}
                                                    >
                                                        {isSalarySheetDesktop && (
                                                            <td className="sl-td-base sl-td-expand-toggle">
                                                                <button
                                                                    type="button"
                                                                    className="sl-expand-toggle-btn"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (isSalaryExpanded) {
                                                                            setExpandedSalaryRows(prev => ({ ...prev, [itemId]: false }));
                                                                            return;
                                                                        }

                                                                        if (salaryRowLoading[itemId]) return;

                                                                        const hasLoadedData = Array.isArray(salaryRowData[itemId]) && salaryRowData[itemId].length > 0;
                                                                        if (hasLoadedData) {
                                                                            setExpandedSalaryRows(prev => ({ ...prev, [itemId]: true }));
                                                                            return;
                                                                        }

                                                                        const salaryMonth = getSalaryMonthParam(item);
                                                                        const salaryYear = getSalaryYearParam(item);
                                                                        loadSalaryRowData(itemId);
                                                                    }}
                                                                    aria-label={isSalaryExpanded ? 'Collapse' : 'Expand'}
                                                                    disabled={salaryRowLoading[itemId] || (Array.isArray(salaryRowData[itemId]) && salaryRowData[itemId].length === 0)}
                                                                >
                                                                    {salaryRowLoading[itemId] ? (
                                                                        <Spinner animation="border" size="sm" variant="success" />
                                                                    ) : (
                                                                        <i className={`bx ${isSalaryExpanded ? 'bx-caret-up' : 'bx-caret-down'}`} />
                                                                    )}
                                                                </button>
                                                            </td>
                                                        )}
                                                        {checkColumn && (
                                                            <td className="sl-td-base sl-td-check">
                                                                {item.status !== 'Väntande'
                                                                    ? checkColumn(item.id)
                                                                    : <>&nbsp;</>}
                                                            </td>
                                                        )}

                                                        {economicReportFilter === false
                                                            ? visibleColumns.map(col => {
                                                                const raw = col.value ? col.value(item) : item[col.field];
                                                                const isStatus = col.field === 'status';
                                                                const isDate = col.field === 'date';
                                                                const isVisualId = col.field === 'visualId';
                                                                const isPaidStatus = isStatus && item?.status === 'Utbetald';
                                                                const paidDateText = isPaidStatus && item?.paidDateTime
                                                                    ? ChangeDateFormat('date', item.paidDateTime)
                                                                    : '';
                                                                const displayContent = isPaidStatus
                                                                    ? <span className="sl-status-paid sl-status-paid-wrap">{paidDateText ? `Utbetald: ${paidDateText}` : raw}</span>
                                                                    : (raw == null || React.isValidElement(raw) ? raw : (typeof raw === 'object' ? JSON.stringify(raw) : raw));

                                                                const capitalizeFirst = (str) => {
                                                                    if (!str) return '';
                                                                    return str.charAt(0).toUpperCase() + str.slice(1);
                                                                };
                                                                const dateStr = isDate && typeof raw === 'string' ? capitalizeFirst(raw) : raw;

                                                                return (
                                                                    <td
                                                                        key={`${index}-${col.field}`}
                                                                        className={`sl-td-base ${isVisualId ? 'sl-td-id' : ''} ${isStatus ? 'sl-td-status' : ''} ${isDate ? 'sl-td-date' : ''} ${col.field === 'totalAmountUserSalary' ? 'sl-td-amount-right' : ''} ${col.tdClass ? col.tdClass(item) : ''}`}
                                                                        data-amount-td={col.field === 'totalAmountUserSalary' ? 'true' : undefined}
                                                                        style={col.field === 'totalAmountUserSalary' ? { textAlign: 'right' } : undefined}
                                                                    >
                                                                        <div
                                                                             className={`${isStatus ? 'sl-status-cell-content' : ''} ${col.insideClass ? col.insideClass(item) : ''}`.trim()}>
                                                                            {isSalarySheetDesktop && isDate ? (
                                                                                <>
                                                                                    <span className="sl-date-pill">{dateStr}</span>
                                                                                </>
                                                                            ) : isSalarySheetDesktop && isVisualId ? (
                                                                                <>&nbsp;</>
                                                                            ) : (
                                                                                displayContent
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                );
                                                            })
                                                            : visibleColumns.map(col => {
                                                                const columnKey = col.field;
                                                                const formattedGroupBy = formatGroupBy(selectedField);
                                                                let allowedFields = click && selectedField
                                                                    ? [formattedGroupBy, "amount"]
                                                                    : visibleColumns.map(c => c.field);
                                                                if (selectedField && formattedGroupBy === "orgName") {
                                                                    allowedFields.push("orgType");
                                                                }
                                                                const raw = col.value ? col.value(item) : item[col.field];
                                                                const displayVal = (formattedGroupBy === 'createdAt' && columnKey === 'createdAt')
                                                                    ? ChangeDateFormat('date', raw)
                                                                    : raw;
                                                                return (
                                                                    <td key={`${index}-${columnKey}`} className="sl-td-base">
                                                                        {allowedFields.includes(columnKey) ? (
                                                                            <div className={col.insideClass ? col.insideClass(item) : ''}>
                                                                                {displayVal == null || React.isValidElement(displayVal) ? displayVal : (typeof displayVal === 'object' ? JSON.stringify(displayVal) : displayVal)}
                                                                            </div>
                                                                        ) : (
                                                                            <div>&nbsp;</div>
                                                                        )}
                                                                    </td>
                                                                );
                                                            })
                                                        }
                                                        {actionColumn && (
                                                            <td className="sl-col-action sl-td-base sl-td-action">
                                                                <div className="sl-td-action-wrap">
                                                                    {isSalarySheetDesktop ? (
                                                                        <span className="sl-salary-action-btn">
                                                                            {actionColumn(item, totalItems)}
                                                                        </span>
                                                                    ) : (
                                                                        <ReimbursementDesktopActionMenu
                                                                            item={item}
                                                                            actionColumn={actionColumn}
                                                                            totalItems={totalItems}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                    {isSalarySheetDesktop && isSalaryExpanded && (
                                                        <tr className="sl-row-expanded">
                                                            <td className="sl-expanded-td" colSpan={(isSalarySheetDesktop ? 1 : 0) + (checkColumn ? 1 : 0) + visibleColumns.length + (actionColumn ? 1 : 0)}>
                                                                <div className="sl-expanded-cards-row">
                                                                    {(Array.isArray(salaryRowData[itemId]) ? salaryRowData[itemId] : []).map((entry, entryIndex) => {
                                                                        const payoutKey = `${itemId}-${entryIndex}`;
                                                                        const isPayoutExpanded = !!payoutCardExpanded[payoutKey];
                                                                        const payoutAmount = (typeof getPayoutAmount === 'function') ? (getPayoutAmount(entry) ?? 0) : 0;
                                                                        const amountClass = payoutAmount >= 0 ? 'sl-amount-positive' : 'sl-amount-negative';
                                                                        return (
                                                                            <div key={payoutKey} className="sl-payout-row sl-expanded-card sl-payout-table-card">
                                                                                <table className="sl-payout-inner-table">
                                                                                    <colgroup>
                                                                                        <col style={{ width: 36 }} />
                                                                                        {visibleColumns.map((col) => (
                                                                                            <col key={`inner-${col.field}-col`} style={{ width: getReimbursementColumnWidth(getReimbursementColumnWeight(col.field)) }} />
                                                                                        ))}
                                                                                        {actionColumn && <col style={{ width: getReimbursementColumnWidth(actionColumnWeight) }} />}
                                                                                    </colgroup>
                                                                                    <tbody>
                                                                                        <tr className="sl-payout-summary-row">
                                                                                            <td className="sl-td-base sl-td-expand-toggle">
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className="sl-expand-toggle-btn"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        const newVal = !isPayoutExpanded;
                                                                                                        setPayoutCardExpanded(prev => ({ ...prev, [payoutKey]: newVal }));
                                                                                                        if (newVal) {
                                                                                                            fetchDetailsPurpose(entry);
                                                                                                        }
                                                                                                    }}
                                                                                                    aria-label={isPayoutExpanded ? 'Collapse payout' : 'Expand payout'}
                                                                                                >
                                                                                                    <i className={`bx ${isPayoutExpanded ? 'bx-caret-up' : 'bx-caret-down'}`} />
                                                                                                </button>
                                                                                            </td>
                                                                                            {visibleColumns.map(col => {
                                                                                                const colField = col.field;
                                                                                                if (colField === 'date') {
                                                                                                    return <td key={`${payoutKey}-${colField}`} className="sl-td-base sl-payout-label">{t('salarySheet.payout')} (LRF{entry?.visualId || ''})</td>;
                                                                                                }
                                                                                                if (colField === 'visualId') {
                                                                                                    return <td key={`${payoutKey}-${colField}`} className="sl-td-base">&nbsp;</td>;
                                                                                                }
                                                                                                if (colField === 'userName') {
                                                                                                    return <td key={`${payoutKey}-${colField}`} className="sl-td-base">&nbsp;</td>;
                                                                                                }
                                                                                                if (colField === 'totalAmountUserSalary') {
                                                                                                    return <td key={`${payoutKey}-${colField}`} className={`sl-td-base ${amountClass} sl-td-amount-right`} data-amount-td="true" style={{ textAlign: "right" }}>{ToCustomDecimal(payoutAmount)} kr</td>;
                                                                                                }
                                                                                                if (colField === 'createdAt' || colField === 'fileCreatedAt') {
                                                                                                    return <td key={`${payoutKey}-${colField}`} className="sl-td-base sl-payout-date" data-align-left-td="fileCreatedAt">{formatDateWithoutTime(entry?.updatedAt)}</td>;
                                                                                                }
                                                                                                return <td key={`${payoutKey}-${colField}`} className="sl-td-base">&nbsp;</td>;
                                                                                            })}
                                                                                            {actionColumn && <td className="sl-td-base"/>}
                                                                                        </tr>
                                                                                        {isPayoutExpanded && Array.isArray(entry?.paymentDetails) && entry.paymentDetails.length > 0 ? (
                                                                                            (() => {
                                                                                                // Only check loading for "Förrättning" items; others are always "loaded"
                                                                                                const allLoaded = entry.paymentDetails.every(d => {
                                                                                                    const isForrattning = (d?.model || d?.Model) === 'Förrättning';
                                                                                                    if (!isForrattning) return true;
                                                                                                    const id = d?.modelId || d?.ModelId || null;
                                                                                                    return !id || (detailPurposeMap[id] && !detailPurposeMap[id].loading);
                                                                                                });
                                                                                                if (!allLoaded) {
                                                                                                    return (
                                                                                                        <tr className="sl-payment-detail-row">
                                                                                                            <td className="sl-td-base">&nbsp;</td>
                                                                                                            <td className="sl-td-base" colSpan={visibleColumns.length}>
                                                                                                                <Spinner animation="border" size="sm" variant="success" />
                                                                                                            </td>
                                                                                                            {actionColumn && <td className="sl-td-base"/>}
                                                                                                        </tr>
                                                                                                    );
                                                                                                }
                                                                                                return entry.paymentDetails.map((detail, detailIndex) => {
                                                                                                const detId = detail?.id ?? detailIndex;
                                                                                                const modelLabel = typeof detail === 'string' ? detail : (detail?.model || detail?.Model || '');
                                                                                                const translatedModel = modelLabel === 'Förrättning' ? t('reimbursement.reportProcessing') : modelLabel === 'Arvode' ? t('sidebar.honorarium') : modelLabel;
                                                                                                const visualId = detail?.visualId ?? detail?.id ?? '';
                                                                                                const instructed = detail?.instructedAmount ?? detail?.amount ?? 0;
                                                                                                const detailAmountClass = instructed >= 0 ? 'sl-amount-positive' : 'sl-amount-negative';
                                                                                                const purpose = detail?.modelId || detail?.ModelId ? detailPurposeMap[detail.modelId || detail.ModelId]?.data?.purpose : null;
                                                                                                return (
                                                                                                    <tr key={`${payoutKey}-${detId}`} className="sl-payment-detail-row">
                                                                                                        <td className="sl-td-base">&nbsp;</td>
                                                                                                        {visibleColumns.map(col => {
                                                                                                            const colField = col.field;
                                                                                                            if (colField === 'date') {
                                                                                                                return <td key={`${payoutKey}-${detId}-${colField}`} className="sl-td-base sl-payment-model">{translatedModel} {visualId || ''}: {formatDateWithoutTime(detail?.createdAt)} {purpose ? ` ${purpose}` : ''}</td>;
                                                                                                            }
                                                                                                            if (colField === 'visualId') {
                                                                                                                return <td key={`${payoutKey}-${detId}-${colField}`} className="sl-td-base">&nbsp;</td>;
                                                                                                            }
                                                                                                            if (colField === 'userName') {
                                                                                                                return <td key={`${payoutKey}-${detId}-${colField}`} className="sl-td-base">&nbsp;</td>;
                                                                                                            }
                                                                                                            if (colField === 'totalAmountUserSalary') {
                                                                                                                return <td key={`${payoutKey}-${detId}-${colField}`} className={`sl-td-base ${detailAmountClass} sl-td-amount-right`} data-amount-td="true" style={{ textAlign: "right" }}>{ToCustomDecimal(instructed)} kr</td>;
                                                                                                            }
                                                                                                            if (colField === 'createdAt') {
                                                                                                                return <td key={`${payoutKey}-${detId}-${colField}`} className="sl-td-base">&nbsp;</td>;
                                                                                                            }
                                                                                                            return <td key={`${payoutKey}-${detId}-${colField}`} className="sl-td-base"></td>;
                                                                                                        })}
                                                                                                        <td className="sl-td-base sl-td-action">
                                                                                                            {(modelLabel === 'Förrättning') && (
                                                                                                                <span className="sl-salary-action-btn">
                                                                                                                    <CustomButton
                                                                                                                        icon="bx bx-show"
                                                                                                                        iconColor="#157553"
                                                                                                                        hoverIconColor="white"
                                                                                                                        hoverButtonColor="#A3C2B8"
                                                                                                                        iconSize={4}
                                                                                                                        buttonColor="#cde0da"
                                                                                                                        tooltipText={t('button.info')}
                                                                                                                        customClasses="rounded sl-payment-detail-action-btn"
                                                                                                                        buttonBorder={false}
                                                                                                                        customClassesTooltip="tooltip-custom-button-table"
                                                                                                                        onClick={(e) => { e.stopPropagation(); openPaymentDetailInfo(detail, item?.userId); }}
                                                                                                                    />
                                                                                                                </span>
                                                                                                            )}
                                                                                                        </td>
                                                                                                    </tr>
                                                                                                );
                                                                                                });
                                                                                            })()
                                                                                        ) : null}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                    </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            );
        } else {
            return (
                <Card className=" border-bottom-0 border-0 px-0 pt-3 mb-2">
                    <div className="sortable-table-scroll">
                    <table className={`${tableClass} sortable-table-minwidth`}>
                        <thead className={theadClass}>
                            <tr className={trClass}>
                                {checkColumn && (
                                    <th key="checkColumn" className={`${thClass} sortable-nowrap`}>
                                        {allCheckColumn && allCheckColumn === true && actionAllCheckColumn && !isReadOnly ? (
                                            <input id="checkInput" type="checkbox" onChange={(e) => actionAllCheckColumn(e)} />
                                        ) : ''}
                                    </th>
                                )}
                                {visibleColumns.map(column => {
                                    let sortLocalStorage = '';
                                    let fieldLocalStorage = '';

                                    if (search !== null) {
                                        const matchesSort = search.match(/sort=([^&]*)/);
                                        const matchesField = search.match(/sortField=([^&]*)/);
                                        if (matchesSort) sortLocalStorage = matchesSort[1];
                                        if (matchesField) fieldLocalStorage = matchesField[1];
                                    }

                                    return (
                                        <th
                                            key={column.field}
                                            onClick={() => column.sortable !== false && onColumnHeaderClick(column.field)}
                                            className={`${thClass || ''} ${column.sortable !== false ? "cursor-pointer-custom text-dark text-start" : ""} ${column.thClass || ''}`}
                                        >
                                            {column.label || column.field}
                                            {fieldLocalStorage === column.field ? (
                                                sortLocalStorage === 'asc' ? (
                                                    <> ↑ {spinnerLoading && <Spinner animation="border" variant="success" size="sm" />} </>
                                                ) : (
                                                    <> ↓ {spinnerLoading && <Spinner animation="border" variant="success" size="sm" />} </>
                                                )
                                            ) : type === "model" ? (
                                                params.sortField === column.field ? (params.sort === 'asc' ? ' ↑' : ' ↓') : ''
                                            ) : (
                                                currentSortValues.sortField === column.field ? (currentSortValues.sort === 'asc' ? ' ↑' : ' ↓') : ''
                                            )}
                                        </th>
                                    );
                                })}
                                {actionColumn && (
                                    <th key="actionColumn" className={`${thClass} sortable-nowrap`}></th>
                                )}
                            </tr>
                            {filterInHeader && (
                                <tr className={trClass}>
                                    {visibleColumns.map(column => (
                                        <th key={`${column.field}-filter`} className={thClass}>
                                            {createFilterInput(column)}
                                        </th>
                                    ))}
                                    {actionColumn && (
                                        <th key="actionColumn-filter" className={`${thClass} sortable-nowrap`}></th>
                                    )}
                                </tr>
                            )}
                        </thead>
                        <tbody className={tbodyClass}>
                            {items.map((item, index) => (
                                <tr key={index} className={`${trClass} ${listOptions.actions?.rowClick ? 'sortable-row-clickable' : ''}`} onClick={listOptions.actions?.rowClick ? () => listOptions.actions.rowClick(item) : undefined}>
                                    {checkColumn && (
                                        <td key={`${index}-checkColumn`} className={`${tdClass} sortable-nowrap`}>
                                            <div className="float-start">
                                                {checkColumn && item.status !== 'Väntande' ? checkColumn(item.id) : <>&nbsp;</>}
                                            </div>
                                        </td>
                                    )}
                                    {economicReportFilter === false ?
                                        visibleColumns.map(column => (
                                            <td key={`${index}-${column.field}`} className={tdClass + ' ' + (column.tdClass ? column.tdClass.bind(this)(item) : 'align-middle')}>
                                                {column.value(item) == null || React.isValidElement(column.value(item)) ?
                                                    <div className={column.insideClass ? column.insideClass.bind(this)(item) : ''}>{column.value(item)}</div>
                                                    : typeof column.value(item) === 'object' ?
                                                    <div className={column.insideClass ? column.insideClass.bind(this)(item) : ''}>{JSON.stringify(column.value(item))}</div>
                                                    :
                                                    <div className={column.insideClass ? column.insideClass.bind(this)(item) : ''}>{column.value(item)}</div>
                                                }
                                            </td>
                                        ))
                                        :
                                        visibleColumns.map(column => {
                                            const columnKey = column.field;
                                            const formattedGroupBy = formatGroupBy(selectedField);
                                            let allowedFields = click && selectedField ? [formattedGroupBy, "amount"] : visibleColumns.map(c => c.field);
                                            if (selectedField && formattedGroupBy === "orgName") {
                                                allowedFields.push("orgType");
                                            }

                                            return (
                                                <td key={`${index}-${columnKey}`} className={tdClass + ' ' + (column.tdClass ? column.tdClass.bind(this)(item) : 'align-middle')}>
                                                    {allowedFields.includes(columnKey) ? (
                                                        <div className={column.insideClass ? column.insideClass.bind(this)(item) : ''}>
                                                            {formattedGroupBy === 'createdAt' && columnKey === 'createdAt'
                                                                ? ChangeDateFormat('date', column.value(item))
                                                                : column.value(item) == null || React.isValidElement(column.value(item))
                                                                    ? column.value(item)
                                                                    : typeof column.value(item) === 'object'
                                                                        ? JSON.stringify(column.value(item))
                                                                        : column.value(item)
                                                            }
                                                        </div>
                                                    ) : (
                                                        <div>&nbsp;</div>
                                                    )}
                                                </td>
                                            );
                                        })
                                    }
                                    {actionColumn && (
                                        <td key={`${index}-actionColumn`} className={tdClass + ' align-middle sortable-nowrap'}>
                                            <div className="float-end">
                                                {actionColumn(item, totalItems)}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
            );
        }
        
    };

    const genericRenderMobileReportProceedingCards = (items) => {
        const isReportProceeding = listOptions?.viewName === "ReportProceedingList";
        if (!isReportProceeding) return null;

        const groupByMonthEnabled = listOptions?.groupByMonth === true;
        const sortedGroups = groupByMonthEnabled
            ? groupItemsByMonth(items, "createdAt")
            : [["all", { label: null, items }]];

        const getStatusMeta = (status) => {
            switch (status) {
                case "Godkänd": return { label: "Godkänd för utbetalning", bg: "#D9F1E6", color: "#157553" };
                case "Skickat": return { label: "Inväntar godkännande", bg: "#DDE7FA", color: "#2B5BB8" };
                case "Nekad": return { label: "Nekade ärenden", bg: "#F8DCDC", color: "#C0392B" };
                case "Förslag": return { label: "Utkast", bg: "#FFF4CC", color: "#8A6500" };
                default: return { label: status || "", bg: "#E5E7EB", color: "#374151" };
            }
        };

        const isEconomicReport = window.location.pathname.includes('economic-report');
        const localSearch = localStorage.getItem('search') || '';
        const shouldHideActionColumn = isEconomicReport && (localSearch.includes('groupby=') || selectedField !== '');
        const actionColumn = !shouldHideActionColumn && listOptions.actions && listOptions.actions.actionColumn;

        const formatDateMobile = (item) => {
            if (!item) return "";
            const dateValue = /*item.updatedAt ||*/ item.createdAt;
            if (!dateValue) return "";
            const d = new Date(dateValue);
            if (Number.isNaN(d.getTime())) return "";
            return d.toLocaleDateString("sv-SE");
        };

        const getDeniedReasonText = (item) => {
            if (item?.status !== "Nekad") return "";
            const reason = item?.Reason ?? item?.reason ?? "";
            return typeof reason === "string" ? reason.trim() : "";
        };

        const cardClick = (item) => {
            if (listOptions?.actions?.rowClick) {
                listOptions.actions.rowClick(item);
            }
        };

        return (
            <div className='px-0 mx-0 reimbursement-cards-wrap'>
                {sortedGroups.map(([key, group]) => (
                    <div key={key} className="reimbursement-month-group">
                        {groupByMonthEnabled && group.label && (
                            <div className="reimbursement-month-header">
                                <span className="reimbursement-month-title">
                                    {group.label}
                                </span>
                            </div>
                        )}

                        <div className="reimbursement-cards-list">
                            {group.items.map((item, index) => {
                                const statusMeta = getStatusMeta(item?.status);
                                const dateText = formatDateMobile(item);
                                const paidDateText = item?.paidDateTime ? ChangeDateFormat('date', item.paidDateTime) : '';
                                const grossAmount = item?.grossAmount ?? 0;
                                const amountColorClass = grossAmount < 0 ? "reimbursement-card-amount-negative" : "reimbursement-card-amount-positive";
                                const deniedReason = getDeniedReasonText(item);
                                const isPaidStatus = item?.status === "Utbetald";
                                const statusClass = item?.status === "Utbetald"
                                    ? "reimbursement-card-status-paid"
                                    : item?.status === "Godkänd"
                                        ? "reimbursement-card-status-approved"
                                        : item?.status === "Skickat"
                                            ? "reimbursement-card-status-sent"
                                            : item?.status === "Nekad"
                                                ? "reimbursement-card-status-denied"
                                                : item?.status === "Förslag"
                                                    ? "reimbursement-card-status-draft"
                                                    : item?.status === "Hanterad"
                                                        ? "reimbursement-card-status-handled"
                                                    : "reimbursement-card-status-default";

                                return (
                                    <div
                                        key={`${item?.id ?? index}`}
                                        onClick={() => cardClick(item)}
                                        className="reimbursement-card-item"
                                    >
                                        <div className="reimbursement-card-content-row">
                                            <div className="reimbursement-card-left">
                                                <div className="reimbursement-card-purpose">
                                                    {item?.purpose || "-"}
                                                </div>
                                                {item?.orgName && (
                                                    <div className="reimbursement-card-org">
                                                        {item.orgName}
                                                    </div>
                                                )}
                                                {isPaidStatus ? (
                                                    <div className="sl-status-paid">
                                                        {paidDateText ? `Utbetald: ${paidDateText}` : 'Utbetald'}
                                                    </div>
                                                ) : (
                                                    <div className={`reimbursement-card-status ${statusClass}`}>
                                                        {statusMeta.label}
                                                    </div>
                                                )}
                                                {!!deniedReason && (
                                                    <div className="reimbursement-card-denied-reason">
                                                        Anledning: "{deniedReason}"
                                                    </div>
                                                )}
                                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    <div style={{
                                                        display: "inline-flex",
                                                        padding: "3px 8px",
                                                        borderRadius: 999,
                                                        fontSize: 11.5,
                                                        fontWeight: 800,
                                                        background: statusMeta.bg,
                                                        color: statusMeta.color
                                                    }}>
                                                        {statusMeta.label}
                                                    </div>
                                                    <ReimbursementTypeBadges types={getReimbursementTypesFromReimbursement(item)} />
                                                </div>
                                            </div>

                                            <div className="reimbursement-card-right">
                                                <div className="reimbursement-card-right-top">
                                                    <div className={`reimbursement-card-amount ${amountColorClass}`}>
                                                        {ToCustomDecimal(grossAmount)} kr
                                                    </div>
                                                    <div className="reimbursement-card-actions-slot">
                                                        {actionColumn ? (
                                                            <ActionMenu
                                                                item={item}
                                                                actionColumn={actionColumn}
                                                                totalItems={items.length}
                                                            />
                                                        ) : (
                                                            <i className="bx bx-dots-vertical-rounded reimbursement-card-actions-disabled" />
                                                        )}
                                                    </div>
                                                </div>
                                                {dateText && (
                                                    <div className="reimbursement-card-date">
                                                        {dateText}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Mobile cards for the API keys list, same structure/classes as the ReportProceedingList cards
    const genericRenderMobileApiKeyCards = (items) => {
        if (!Array.isArray(items) || items.length === 0) return '';

        const actionColumn = listOptions.actions && listOptions.actions.actionColumn;

        const getStatusMeta = (status) => {
            switch (status) {
                case "Active": return { label: i18n.t('apiKey.statusActive'), bg: "#D9F1E6", color: "#157553" };
                case "Revoked": return { label: i18n.t('apiKey.statusRevoked'), bg: "#F8DCDC", color: "#C0392B" };
                case "Expired": return { label: i18n.t('apiKey.statusExpired'), bg: "#E5E7EB", color: "#374151" };
                default: return { label: status || "", bg: "#E5E7EB", color: "#374151" };
            }
        };

        return (
            <div className='px-0 mx-0 reimbursement-cards-wrap'>
                <div className="reimbursement-month-group">
                    <div className="reimbursement-cards-list">
                        {items.map((item, index) => {
                            const statusMeta = getStatusMeta(item?.status);
                            const expiresText = item?.expiresAt
                                ? `${i18n.t('apiKey.expiresAt')}: ${ChangeDateFormat('date', item.expiresAt)}`
                                : `${i18n.t('apiKey.expiresAt')}: ${i18n.t('apiKey.never')}`;
                            const lastUsedText = item?.lastUsedAt
                                ? `${i18n.t('apiKey.lastUsedAt')}: ${ChangeDateFormat('dateTime', item.lastUsedAt)}`
                                : i18n.t('apiKey.neverUsed');

                            return (
                                <div key={`${item?.id ?? index}`} className="reimbursement-card-item">
                                    <div className="reimbursement-card-content-row">
                                        <div className="reimbursement-card-left">
                                            <div className="reimbursement-card-purpose">
                                                {item?.name || "-"}
                                            </div>
                                            {item?.prefix && (
                                                <div className="reimbursement-card-org">
                                                    <code>{`${item.prefix}…`}</code>
                                                </div>
                                            )}
                                            <div className="reimbursement-card-org">
                                                {lastUsedText}
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <div style={{
                                                    display: "inline-flex",
                                                    padding: "3px 8px",
                                                    borderRadius: 999,
                                                    fontSize: 11.5,
                                                    fontWeight: 800,
                                                    background: statusMeta.bg,
                                                    color: statusMeta.color
                                                }}>
                                                    {statusMeta.label}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="reimbursement-card-right">
                                            <div className="reimbursement-card-right-top">
                                                <div className="reimbursement-card-date">
                                                    {expiresText}
                                                </div>
                                                <div className="reimbursement-card-actions-slot">
                                                    {actionColumn ? (
                                                        <ActionMenu
                                                            item={item}
                                                            actionColumn={actionColumn}
                                                            totalItems={items.length}
                                                        />
                                                    ) : (
                                                        <i className="bx bx-dots-vertical-rounded reimbursement-card-actions-disabled" />
                                                    )}
                                                </div>
                                            </div>
                                            {item?.createdAt && (
                                                <div className="reimbursement-card-date">
                                                    {ChangeDateFormat('date', item.createdAt)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    // Mobile cards for "modernDesign" lists, same look as the ReportProceedingList mobile cards.
    // Configured per view with listOptions.mobileCard = { title, subtitle, value, valueCaption, badges } (functions of item)
    const genericRenderMobileModernCards = (items) => {
        const cfg = listOptions?.mobileCard || {};
        const actionColumn = listOptions?.actions?.actionColumn;
        const call = (fn, item) => (typeof fn === 'function' ? fn(item) : null);

        return (
            <div className='px-0 mx-0 reimbursement-cards-wrap'>
                <div className="reimbursement-cards-list">
                    {items.map((item, index) => {
                        const title = call(cfg.title, item);
                        const subtitle = call(cfg.subtitle, item);
                        const value = call(cfg.value, item);
                        const valueCaption = typeof cfg.valueCaption === 'function' ? cfg.valueCaption(item) : cfg.valueCaption;
                        const badges = (call(cfg.badges, item) || []).filter(Boolean);

                        return (
                            <div
                                key={`${item?.id ?? index}`}
                                onClick={() => listOptions?.actions?.rowClick && listOptions.actions.rowClick(item)}
                                className="reimbursement-card-item"
                            >
                                <div className="reimbursement-card-content-row">
                                    <div className="reimbursement-card-left">
                                        <div className="reimbursement-card-purpose">{title || "-"}</div>
                                        {subtitle && <div className="reimbursement-card-org">{subtitle}</div>}
                                        {badges.length > 0 && (
                                            <div className="sl-mobile-card-badges">
                                                {badges.map((badge) => (
                                                    <span key={badge} className="sl-mobile-card-badge">{badge}</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="reimbursement-card-right">
                                        <div className="reimbursement-card-right-top">
                                            {value !== null && value !== undefined && value !== '' && (
                                                <div className="reimbursement-card-amount reimbursement-card-amount-positive">{value}</div>
                                            )}
                                            <div className="reimbursement-card-actions-slot">
                                                {actionColumn ? (
                                                    <ActionMenu
                                                        item={item}
                                                        actionColumn={actionColumn}
                                                        totalItems={totalItems}
                                                    />
                                                ) : (
                                                    <i className="bx bx-dots-vertical-rounded reimbursement-card-actions-disabled" />
                                                )}
                                            </div>
                                        </div>
                                        {valueCaption && <div className="reimbursement-card-date">{valueCaption}</div>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

const genericRenderMobileSalarySheetCards = (items) => {
        const isSalaryView = listOptions?.viewName === "SalarySheetList" || listOptions?.viewName === "AdminSalarySheetList";
        if (!isSalaryView) return null;

        const groupByMonthEnabled = listOptions?.groupByMonth === true;
        const sortedGroups = groupByMonthEnabled
            ? groupItemsByMonth(items, "date")
            : [["all", { label: null, items }]];

        const actionColumn = listOptions.actions && listOptions.actions.actionColumn;

        const formatDateMobile = (item) => {
            if (!item) return "";
            const dateValue = item.date;
            if (!dateValue) return "";
            const d = new Date(dateValue);
            if (Number.isNaN(d.getTime())) return "";
            return d.toLocaleDateString("sv-SE");
        };

        const formatCreatedAtMobile = (item) => {
            if (!item?.createdAt) return "";
            const d = new Date(item.createdAt);
            if (Number.isNaN(d.getTime())) return "";
            return d.toLocaleDateString("sv-SE");
        };

        const capitalizeFirst = (str) => {
            if (!str) return '';
            return str.charAt(0).toUpperCase() + str.slice(1);
        };

        return (
            <div className='px-0 mx-0 reimbursement-cards-wrap'>
                {sortedGroups.map(([key, group]) => (
                    <div key={key} className="reimbursement-month-group">
                        {groupByMonthEnabled && group.label && (
                            <div className="reimbursement-month-header">
                                <span className="reimbursement-month-title">
                                    {group.label}
                                </span>
                            </div>
                        )}

                        <div className="reimbursement-cards-list">
                            {group.items.map((item, index) => {
                                const itemId = item?.id ?? index;
                                const isSalaryExpanded = !!expandedSalaryRows[itemId];
                                const dateText = item?.date || "";
                                const createdAtText = formatCreatedAtMobile(item);
                                const amount = item?.totalUserSalaryAmount ?? 0;
                                const amountColorClass = amount < 0 ? "reimbursement-card-amount-negative" : "reimbursement-card-amount-positive";

                                return (
                                    <div
                                        key={`${itemId}`}
                                        className="reimbursement-card-item"
                                        style={{ paddingTop: 8, paddingBottom: 8 }}
                                    >
                                        <div className="row g-1">
                                            <div className="col-6 d-flex align-items-center">
                                                {dateText && (
                                                    <span className="sl-date-pill">{capitalizeFirst(dateText)}</span>
                                                )}
                                            </div>
                                            <div className="col-6 d-flex align-items-center justify-content-between">
                                                <div>
                                                    <div className="reimbursement-card-field-label">{t('modelLabel.id')}</div>
                                                    <div className="reimbursement-card-field-value">{item?.visualId || ''}</div>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    {actionColumn && (
                                                        <span className="sl-salary-action-btn">
                                                            {actionColumn(item, items.length)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row g-1">
                                            <div className="col-6">
                                                <div className="reimbursement-card-field-label">{t('modelLabel.grossAmount')}</div>
                                                <div className={`reimbursement-card-amount ${amountColorClass}`} style={{ fontSize: 13, fontWeight: 600 }}>
                                                    {ToCustomDecimal(amount)} kr
                                                </div>
                                            </div>
                                            <div className="col-6 d-flex align-items-center justify-content-between">
                                                <div>
                                                    <div className="reimbursement-card-field-label">{t('modelLabel.createdAt')}</div>
                                                    <div className="reimbursement-card-field-value">{createdAtText ? capitalizeFirst(createdAtText) : '-'}</div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="sl-expand-toggle-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (isSalaryExpanded) {
                                                            setExpandedSalaryRows(prev => ({ ...prev, [itemId]: false }));
                                                            return;
                                                        }

                                                        if (salaryRowLoading[itemId]) return;

                                                        const hasLoadedData = Array.isArray(salaryRowData[itemId]) && salaryRowData[itemId].length > 0;
                                                        if (hasLoadedData) {
                                                            setExpandedSalaryRows(prev => ({ ...prev, [itemId]: true }));
                                                            return;
                                                        }

                                                        const salaryMonth = getSalaryMonthParam(item);
                                                        const salaryYear = getSalaryYearParam(item);
                                                        loadSalaryRowData(itemId);
                                                    }}
                                                    aria-label={isSalaryExpanded ? 'Collapse' : 'Expand'}
                                                    disabled={salaryRowLoading[itemId] || (Array.isArray(salaryRowData[itemId]) && salaryRowData[itemId].length === 0)}
                                                >
                                                    {salaryRowLoading[itemId] ? (
                                                        <Spinner animation="border" size="sm" variant="success" />
                                                    ) : (
                                                        <i className={`bx ${isSalaryExpanded ? 'bx-caret-up' : 'bx-caret-down'}`} />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                        {isSalaryExpanded && (
                                            <div className="mt-1 pt-1" style={{ borderTop: '1px solid #E5E7EB' }}>
                                                <div className="d-flex flex-column gap-1 mt-1">
                                                    {(Array.isArray(salaryRowData[itemId]) ? salaryRowData[itemId] : []).map((entry, entryIndex) => {
                                                        const payoutKey = `${itemId}-${entryIndex}`;
                                                        const isPayoutExpanded = !!payoutCardExpanded[payoutKey];
                                                        return (
                                                            <div key={payoutKey} className="reimbursement-card-item sl-payout-mobile-card">
                                                                <div className="row g-1">
                                                                    <div className="col-4">
                                                                        <div className="reimbursement-card-field-label">{t('modelLabel.id')}</div>
                                                                        <div className="reimbursement-card-field-value">{entry?.visualId || ''}</div>
                                                                    </div>
                                                                    <div className="col-4">
                                                                        <div className="reimbursement-card-field-label">{t('salarySheet.payout')}</div>
                                                                        <div className={`reimbursement-card-field-value ${getPayoutAmount(entry) >= 0 ? 'text-green' : 'text-red'}`}>{ToCustomDecimal(getPayoutAmount(entry))} kr</div>
                                                                    </div>
                                                                    <div className="col-4">
                                                                        <div className="reimbursement-card-field-label">{t('modelLabel.createdAt')}</div>
                                                                        <div className="reimbursement-card-field-value">{formatDateWithoutTime(entry?.createdAt) || ''}</div>
                                                                    </div>
                                                                </div>
                                                                <div className="position-absolute top-0 end-0 mt-1 me-2">
                                                                    <button
                                                                        type="button"
                                                                        className="sl-expand-toggle-btn"
                                                                        onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                const newVal = !isPayoutExpanded;
                                                                                setPayoutCardExpanded(prev => ({ ...prev, [payoutKey]: newVal }));
                                                                                if (newVal) {
                                                                                    fetchDetailsPurpose(entry);
                                                                                }
                                                                            }}
                                                                        aria-label={isPayoutExpanded ? 'Collapse payout' : 'Expand payout'}
                                                                    >
                                                                        <i className={`bx ${isPayoutExpanded ? 'bx-caret-up' : 'bx-caret-down'}`} />
                                                                    </button>
                                                                </div>
                                                                {isPayoutExpanded && (
                                                                    <div className="mt-1 pt-1 sl-payout-mobile-divider">
                                                                        {Array.isArray(entry?.paymentDetails) && entry.paymentDetails.length > 0 ? (
                                                                            (() => {
                                                                                const allLoaded = entry.paymentDetails.every(d => {
                                                                                    const isForrattning = (d?.model || d?.Model) === 'Förrättning';
                                                                                    if (!isForrattning) return true;
                                                                                    const id = d?.modelId || d?.ModelId || null;
                                                                                    return !id || (detailPurposeMap[id] && !detailPurposeMap[id].loading);
                                                                                });
                                                                                if (!allLoaded) {
                                                                                    return <Spinner animation="border" size="sm" variant="success" />;
                                                                                }
                                                                                return entry.paymentDetails.map((detail, detailIndex) => {
                                                                                const detId = detail?.id ?? detailIndex;
                                                                                const modelLabel = typeof detail === 'string' ? detail : (detail?.model || detail?.Model || '');
                                                                                const translatedModel = modelLabel === 'Förrättning' ? t('reimbursement.reportProcessing') : modelLabel === 'Arvode' ? t('sidebar.honorarium') : modelLabel;
                                                                                const visualId = detail?.visualId ?? detail?.id ?? '';
                                                                                const instructed = detail?.instructedAmount ?? detail?.amount ?? 0;
                                                                                const purpose = detail?.modelId || detail?.ModelId ? detailPurposeMap[detail.modelId || detail.ModelId]?.data?.purpose : null;
                                                                                return (
                                                                                    <div key={`${payoutKey}-${detId}`} className="reimbursement-card-item sl-payment-detail-mobile-card mt-1">
                                                                                        <div className="row g-1">
                                                                                            <div className="col-10">
                                                                                                <div className="reimbursement-card-field-label">{translatedModel}</div>
                                                                                                <div className="reimbursement-card-field-value">
                                                                                                    {visualId} {formatDateWithoutTime(detail?.createdAt)} {purpose ? purpose : ''}
                                                                                                </div>
                                                                                            </div>
                                                                                            <div className="col-2 text-end">
                                                                                                {(modelLabel === 'Förrättning') && (
                                                                                                    <span className="sl-salary-action-btn">
                                                                                    <ActionMenu
                                                                                        item={detail}
                                                                                        actionColumn={(det) => (
                                                                                            <Button
                                                                                                className="bg-green-light text-green border-0 position-relative reimbursement-report-action-btn"
                                                                                                onClick={(e) => { e.stopPropagation(); openPaymentDetailInfo(det, item?.userId); }}
                                                                                            >
                                                                                                <i className="bx bx-show fs-5 align-middle pb-1" />
                                                                                                <div className="tooltip-custom-button">{t('button.info')}</div>
                                                                                                                    </Button>
                                                                                                                )}
                                                                                                                totalItems={entry?.paymentDetails?.length ?? 0}
                                                                                                            />
                                                                                                        </span>
                                                                                                    )}
                                                                                                </div>
                                                                                        </div>
                                                                                        <div className="row g-1">
                                                                                            <div className="col-6">
                                                                                                <div className="reimbursement-card-field-label">{t('modelLabel.amount')}</div>
                                                                                                <div className={`reimbursement-card-field-value ${instructed >= 0 ? 'text-green' : 'text-red'}`}>{ToCustomDecimal(instructed)} kr</div>
                                                                                            </div>
                                                                                            <div className="col-6">
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                                                });
                                                                                            })()
                                                                                        ) : (
                                                                            '-'
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

// ─── Shared styles ───────────────────────────────────────────────────────────
    const thSt = {
        padding: "9px 10px",
        fontSize: 11,
        fontWeight: 700,
        color: "#9CA3AF",
        textTransform: "uppercase",
        letterSpacing: ".07em",
        textAlign: "left",
        whiteSpace: "nowrap",
        cursor: "pointer",
        userSelect: "none",
        background: "transparent",
    };

    const tdSt = {
        padding: "11px 10px",
        fontSize: 13,
        color: "#1A1F2E",
        fontWeight: 500,
        verticalAlign: "middle",
    };

    // Conditional Rendering
    const renderContentBasedOnViewMode = () => {
        if (!items) {
            return <div className="pb-3 text-center">{t('sortableList.noResults')}</div>;
        }

        if (isMobile && listOptions?.viewName === "ReportProceedingList") {
            return genericRenderMobileReportProceedingCards(items);
        }

        if (isMobile && useModernListDesign && listOptions?.mobileCard) {
            return genericRenderMobileModernCards(items);
        }

        if (isMobile && (listOptions?.viewName === "SalarySheetList" || listOptions?.viewName === "AdminSalarySheetList")) {
            return genericRenderMobileSalarySheetCards(items);
        }

        if (isMobile && listOptions?.viewName === "AdminApiKeyList") {
            return genericRenderMobileApiKeyCards(items);
        }

        return items.length > 0 ? (
            viewMode === 'table' ?
                (listOptions.layoutOptions && listOptions.layoutOptions.table && listOptions.layoutOptions.table.renderTable
                    ? listOptions.layoutOptions.table.renderTable(items, handleSortChange, params.sortField, params.sort)
                    : genericRenderTable(items))
                : (listOptions.layoutOptions && listOptions.layoutOptions.grid && listOptions?.layoutOptions?.grid?.renderGrid
                    ? listOptions?.layoutOptions?.grid?.renderGrid(items)
                    : null)
        ) : ''
    };

    if (showErrorMessage === true && error === true) {
        return <div className="pb-3 text-center">{t('sortableList.apiErrorResult')}</div>;
    }

    const isReimbursementMobileList = isMobile && (listOptions?.viewName === "ReportProceedingList" || listOptions?.viewName === "SalarySheetList" || listOptions?.viewName === "AdminSalarySheetList" || listOptions?.viewName === "AdminApiKeyList" || useModernListDesign);
    const topControlsRowClass = 'd-flex align-items-center justify-content-between flex-wrap flex-md-nowrap';
    const topFilterColClass = `mt-3 ${isReimbursementFiltersDesignMode ? 'flex-grow-1' : 'flex-grow-1 me-2'}`.trim();
    const topFormFilterColClass = `${isReimbursementFiltersDesignMode ? 'flex-grow-1' : 'flex-grow-1 me-2'}`.trim();
    const shouldShowPagination =
        (Array.isArray(items) && items.length > 0) ||
        (typeof totalItems === 'number' && totalItems > 0);

    if (loading === true) {
        if (isReimbursementMobileList) {
            return (
                <div className="sortable-center-loader-screen">
                    <div className='d-flex align-items-center'>
                        <Spinner color='success' />
                        <span className='ps-3 align-self-center'>{t('sortableList.gettingData')}</span>
                    </div>
                </div>
            );
        }

        return <Row className='w-100 text-center'>
            <Col lg={2} className='d-flex align-items-center mx-auto' >
                <Spinner color='success' />
                <span className='ps-3 align-self-center'>{t('sortableList.gettingData')}</span>
            </Col>
        </Row>
    }

    if (economicFilterLoading === true) {
        if (isReimbursementMobileList) {
            return (
                <div className="sortable-center-loader-screen">
                    <div className='d-flex align-items-center'>
                        <Spinner color='success' />
                        <span className='ps-3 align-self-center'>{t('sortableList.gettingData')}</span>
                    </div>
                </div>
            );
        }

        return (
        <>
        <Row className="d-flex align-items-center justify-content-between">
                {/* Form Filter UI */}  
                {!filterTestIsAvailable && (              
                    <Col md={exportIsAvailable ? 8 : 10}>
                        {renderFilterUI()}
                    </Col>
                )}
                {filterTestIsAvailable && (
                    <Col md={8} xxl={9}>
                        {generateFormFilterUI()} 
                    </Col>
                )}
            
                {/* Export Dropdown */}
                {exportIsAvailable && !isReimbursementFiltersDesignMode && (
                    <Col xs={4} md={2} xxl={1} className="d-flex align-items-center justify-content-md-end pt-3 pe-0 pe-md-3 px-2">
                        {renderExportFileDropdown()}
                    </Col>
                )}
            
                {/* Items per Page Dropdown */}
                {viewDropdownPerPage && !isReimbursementFiltersDesignMode && (
                    <Col xs={4} md={2} className="d-flex align-items-center justify-content-md-end p-0 pt-3 pe-0 pe-md-2">
                        {renderItemsPerPageDropdown()}
                    </Col>
                )}
        </Row>
        <Row className='w-100 text-center pt-5'>
            <Col lg={2} className='d-flex align-items-center mx-auto' >
                <Spinner color='success' />
                <span className='ps-3 align-self-center'>Sökning pågår...</span>
            </Col>
        </Row>
        </>
    )}

    return (
        <>
            {/* Info SlidePanel for payment details */}
            <SlidePanel
                isOpen={infoPanelOpen}
                onClose={() => { setInfoPanelOpen(false); setInfoPanelVisualId(null); }}
                title={t('reimbursement.reportProcessing') + (infoPanelVisualId ? ' ' + infoPanelVisualId : '')}
            >
                {infoPanelItemId && !infoPanelLoading && <ReportProceedingInfo id={infoPanelItemId} userId={infoPanelUserId} onClose={() => setInfoPanelOpen(false)} />}
                {infoPanelLoading && (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
                        <Spinner color="success" />
                    </div>
                )}
            </SlidePanel>

            {/* Export Notifications */}
            <div className="sortable-export-notifications-wrap">
                {exportNotifications.map((n) => {
                    const notificationTypeClass = n.status === 'error' ? 'sortable-export-notification-error' : 'sortable-export-notification-success';

                    const icons = {
                        generating: <Spinner size="sm" color="light" />,
                        completed: <i className="fas fa-check-circle" />,
                        error: <i className="fas fa-exclamation-circle" />
                    };

                    // Función para cerrar notificación
                    const handleClose = (id) => {
                        setExportNotifications((prev) => prev.filter((notif) => notif.id !== id));
                    };

                    return (
                        <div
                            key={n.id}
                            className={`sortable-export-notification ${notificationTypeClass}`}
                        >
                            <div className="sortable-export-notification-content">
                                {icons[n.status]}
                                {n.status === 'generating' && (
                                    <span>
                                        {t('sortableList.generatingFile')}
                                    </span>
                                )}
                                {n.status === 'completed' && (
                                    <span>
                                        {t('sortableList.fileDownloaded', {
                                            format: n.format,
                                            fileName: n.fileName
                                        })}
                                    </span>
                                )}
                                {n.status === 'error' && (
                                    <span>
                                        {t('sortableList.errorGeneratingFile')}
                                    </span>
                                )}
                            </div>

                            {/* Botón de cierre */}
                            <button
                                onClick={() => handleClose(n.id)}
                                className="sortable-export-notification-close"
                                title={t('close') || 'Close'}
                            >
                                &times;
                            </button>
                        </div>
                    );
                })}
            </div>
            {/* {filterTestIsAvailable && (
                <Row>
                    <Col md={9}>
                        {generateFormFilterUI()} 
                    </Col>
                </Row>
            )} */}
            {wrapTopInCard ? (
                <div className="gc-card2 p-4 ms-0">
                    {typeof renderTopHeader === 'function' && renderTopHeader()}
                    {(filterPosition === 'top' || filterPosition === 'both') && (
                        <Row className={topControlsRowClass}>
                            {!filterTestIsAvailable && !hideFilter && (
                                <Col xs={12} md={isReimbursementFiltersDesignMode ? undefined : true} className={topFilterColClass}>
                                    {renderFilterUI()}
                                </Col>
                            )}
                            {filterTestIsAvailable && (
                                <Col xs={12} md={isReimbursementFiltersDesignMode ? undefined : true} className={topFormFilterColClass}>
                                    {generateFormFilterUI()}
                                </Col>
                            )}

                            <Col xs="auto" className="d-flex align-items-center gap-2 flex-shrink-0 mt-3">
                                {!isReimbursementFiltersDesignMode && exportIsAvailable && (
                                    <div>{renderExportFileDropdown()}</div>
                                )}
                                {!isReimbursementFiltersDesignMode && viewDropdownPerPage && !hideFilter && (
                                    <div>{renderItemsPerPageDropdown()}</div>
                                )}
                            </Col>
                        </Row>
                    )}
                    {renderAddButton()}
                </div>
            ) : (
                <>
                    {(filterPosition === 'top' || filterPosition === 'both') && (
                        <Row className={topControlsRowClass}>
                            {!filterTestIsAvailable && !hideFilter && (
                                <Col xs={12} md={isReimbursementFiltersDesignMode ? undefined : true} className={topFilterColClass}>
                                    {renderFilterUI()}
                                </Col>
                            )}
                            {filterTestIsAvailable && (
                                <Col xs={12} md={isReimbursementFiltersDesignMode ? undefined : true} className={topFormFilterColClass}>
                                    {generateFormFilterUI()}
                                </Col>
                            )}

                            <Col xs="auto" className="d-flex align-items-center gap-2 flex-shrink-0 mt-3">
                                {!isReimbursementFiltersDesignMode && exportIsAvailable && (
                                    <div>{renderExportFileDropdown()}</div>
                                )}
                                {!isReimbursementFiltersDesignMode && viewDropdownPerPage && !hideFilter && (
                                    <div>{renderItemsPerPageDropdown()}</div>
                                )}
                            </Col>
                        </Row>
                    )}
                    {renderAddButton()}
                </>
            )}
            <Row>
                <Col>
                    {listOptions?.layoutOptions && listOptions?.layoutOptions?.grid?.renderGrid && canChangeLayout && (
                        <>
                            <Button
                                onClick={() => setViewMode('table')}
                                className={`border-0 position-relative ${viewMode === 'table' ? 'bg-transparent i-color-grow-brown' : 'bg-transparent text-secondary'}`}
                            >
                                {/!*Table View *!/} <i className="fas fa-list"></i>
                                <div className="tooltip-custom-button"> {t('sortableList.buttonTable')}</div>
                            </Button>
                            {' '}
                            <Button
                                onClick={() => setViewMode('grid')}
                                className={` border-0 position-relative ${viewMode === 'grid' ? 'bg-transparent i-color-grow-brown' : 'bg-transparent text-secondary'}`}
                            >
                                {/!*Grid View*!/}<i className="fas fa-th"></i>
                                <div className="tooltip-custom-button"> {t('sortableList.buttonGrid')}</div>
                            </Button>
                        </>
                    )}
                    {/*{viewDropdownPerPage === true ? (*/}
                    {/*    <div className="float-end">*/}
                    {/*        {renderItemsPerPageDropdown()}*/}
                    {/*    </div>*/}
                    {/*) : (*/}
                    {/*    ''*/}
                    {/*)}*/}

                </Col>
            </Row>
            {shouldShowPagination && (paginationControlsPosition === 'top' || paginationControlsPosition === 'both') && (
                    <Row className="justify-content-center custom-pagination sortable-pagination-row d-flex flex-md-nowrap align-items-center">
                        {renderPaginationControls()}
                    </Row>)}

                    <Row className={`${typeof addButton === "function" || wrapTopInCard || listOptions?.viewName === "ReportProceedingList" || listOptions?.viewName === "AdminApiKeyList" || useModernListDesign ? '' :'pt-4'} ${isMobile ? 'px-3' : ''}`}>
                        {viewMode === 'grid' &&
                            (listOptions.layoutOptions && listOptions.layoutOptions.filter && listOptions.layoutOptions.filter.renderSortDropdown
                                ? listOptions.layoutOptions.filter.renderSortDropdown(handleSortChange)
                                : renderGenericSortDropdown())
                        }
                        {renderContentBasedOnViewMode()}
                        {/*{ loading ? <Spinner color='success' /> : renderContentBasedOnViewMode()} /!*<Spinner color='success' /> <div>{t('sortableList.loading')}</div>*/}
                    </Row>
                    {economicReportFilter ?
                    <Row className="justify-content-end custom-pagination mb-3 ">
                        <Col xs="auto" className="d-flex justify-content-start">
                        <Card className="p-3 bg-light border">
                            <span > 
                                 <b>Summa belopp:</b> {totalAmount ? ToCustomDecimal(totalAmount) +' kr' : "0 kr"}
                            </span>
                        </Card>
                        </Col>
                    </Row>
                      :""}
            {shouldShowPagination && (paginationControlsPosition === 'bottom' || paginationControlsPosition === 'both') && !hideFilter&& (
                    <Row className="justify-content-center custom-pagination sortable-pagination-row d-flex flex-md-nowrap align-items-center">
                        {/*<Col lg="4" className="custom-pagination d-flex align-items-center justify-content-between p-0">*/}
                        {renderPaginationControls()}
                        {/*</Col>*/}
                    </Row>
                )}

            {(filterPosition === 'bottom' || filterPosition === 'both') && (
                <Row>
                    {renderFilterUI()}
                </Row>
            )}
        </>
    );
};

SortableList.propTypes = {
    //fetchDataFunction: PropTypes.func.isRequired,
    listOptions: PropTypes.object,
};

export default SortableList;
